/**
 * stencil.cloud — Main Editor Application
 *
 * Initializes the Fabric.js canvas, sidebar controls, template hydration,
 * background rendering, and export pipeline.
 */

import * as fabric from 'fabric';
import { SCHEMES, listSchemes, getScheme } from '../canvas/schemes.js';
import { createDesignState, TEMPLATE_SIZES, DEFAULT_SIZE } from '../state/design-state.js';
import { LINE_ART_STYLES } from '../overlays/line-art.js';
import { renderBackground } from './background.js';
import { normalizeOverlay } from '../utils/normalize.js';
import { pushUndoState, undo, redo, canUndo, canRedo } from '../state/undo.js';
import { applyMangle, mangleLayout, resetMangle, getMangleSeed, setMangleSeed } from './mangle.js';
import { captureSnapshot } from '../state/snapshot.js';
import { savePreset, loadPreset, deletePreset, listPresets } from '../state/presets.js';
import { addQrCode, removeQrCode } from './qr.js';
import { addWordmark, addTextWordmark, removeWordmark, hasWordmark, WORDMARK_FONTS, WORDMARK_ACCENTS } from './wordmark.js';
import { embedPngMetadata } from '../utils/png-metadata.js';
import { generateVariations, renderVariationThumbnail, variationToStatePatch, FONT_PAIRINGS } from './remix.js';
import {
  buildSharePayload, encodeShareLink, decodeShareLink, hasShareLink, clearShareHash,
  MAX_URL_WARN, MAX_URL_HARD, getAnonRefId,
} from '../state/share.js';
import { parseEventText, mapToTemplate, getFieldLabels } from '../utils/text-parser.js';
import JSZip from 'jszip';
import {
  initAuth, getUser, isAuthenticated, isPro, isUltra, isQA, getQATier, setQATier, logout,
  loginWithGoogle, loginWithEmail, registerWithEmail, startCheckout,
} from '../auth/auth-client.js';

// Load template JSON
import genericEventTemplate from '../../templates/events/generic-event.json';
import headlineCenterTemplate from '../../templates/events/headline-center.json';
import bottomHeavyTemplate from '../../templates/events/bottom-heavy.json';
import splitStackTemplate from '../../templates/events/split-stack.json';
import ravePosterTemplate from '../../templates/events/rave-poster.json';
import minimalTypeTemplate from '../../templates/events/minimal-type.json';
import blockPartyTemplate from '../../templates/events/block-party.json';
import undergroundTemplate from '../../templates/events/underground.json';
import festivalLineupTemplate from '../../templates/events/festival-lineup.json';
import albumReleaseTemplate from '../../templates/events/album-release.json';
import neonNightsTemplate from '../../templates/events/neon-nights.json';
import brutalistTemplate from '../../templates/events/brutalist.json';
import galleryOpeningTemplate from '../../templates/events/gallery-opening.json';
import garageSaleTemplate from '../../templates/community/garage-sale.json';
import nowHiringTemplate from '../../templates/business/now-hiring.json';

const TEMPLATES = {
  'generic-event': genericEventTemplate,
  'headline-center': headlineCenterTemplate,
  'bottom-heavy': bottomHeavyTemplate,
  'split-stack': splitStackTemplate,
  'rave-poster': ravePosterTemplate,
  'minimal-type': minimalTypeTemplate,
  'block-party': blockPartyTemplate,
  'underground': undergroundTemplate,
  'festival-lineup': festivalLineupTemplate,
  'album-release': albumReleaseTemplate,
  'neon-nights': neonNightsTemplate,
  'brutalist': brutalistTemplate,
  'gallery-opening': galleryOpeningTemplate,
  'garage-sale': garageSaleTemplate,
  'now-hiring': nowHiringTemplate,
};

let fabricCanvas = null;
let currentState = null;
let currentSize = null;
let displayScale = 1; // ratio: displayPixels / templatePixels

// Track hidden object IDs and collapsed sidebar sections
const hiddenObjects = new Set();
const sectionCollapsed = {};

// ── Snap-to-grid state ──
let snapEnabled = false;
let snapGridSize = 20; // pixels at display scale

// ── Editor mode: 'template' (layout engine) vs 'advanced' (free placement) ──
let editorMode = 'template';
// Per-mode, per-size position maps.
// Structure: { [sizeKey]: { [objectId]: { leftPct, topPct, scaleX, scaleY, angle, width, fontSize } } }
const advancedPositions = {};
const templatePositions = {};
// Track which sizes the user has manually modified (for batch export pre-selection)
const userModifiedSizes = new Set();
// User-created text objects — survive hydrateTemplate rebuilds
// Each entry: { id, text, fontFamily, fontSize, fontWeight, fontStyle, underline, fill,
//   textAlign, leftPct, topPct, width, visible, fxLocked, textBg, sliceEffect, vSliceEffect }
let userTextObjects = [];

// ── Per-field text style overrides (Template Mode) ──
// User customizations that persist across hydrateTemplate rebuilds.
// Structure: { [objectId]: { fontFamily?, fontWeight?, fontStyle?, fill?, textBg? } }
const textStyleOverrides = {};

// ── Group alignment overrides (Template Mode "smart justification") ──
// Allows Lineup/Venue-Info groups to be left/center/right justified.
// Structure: { [groupName]: 'left' | 'center' | 'right' }
const groupAlignOverrides = {};

// ── Remix gallery state ──
// Stores the last set of generated variations so the user can return to the gallery.
// Structure: { variations: [...], thumbnails: [...], originalSnapshot: {...}, masterSeed: number }
let _lastRemixGallery = null;

// ── Action logger ──
const actionLog = [];    // { text, time } — most recent 10
const MAX_LOG = 10;
let _logPanelEl = null;  // reference to the live DOM element

/**
 * Log a user action. Updates the action log pane in real time.
 */
function logAction(text) {
  actionLog.unshift({ text, time: new Date() });
  if (actionLog.length > MAX_LOG) actionLog.pop();
  renderActionLog();
}

function renderActionLog() {
  if (!_logPanelEl) return;
  _logPanelEl.innerHTML = '';
  if (actionLog.length === 0) {
    _logPanelEl.innerHTML = '<div style="color:var(--text-dim);font-size:10px;padding:4px 0">No actions yet</div>';
    return;
  }
  for (const entry of actionLog) {
    const row = document.createElement('div');
    row.style.cssText = 'font-size:10px;color:var(--text-dim);padding:3px 0;border-bottom:1px solid var(--border);display:flex;gap:6px;';
    const ts = document.createElement('span');
    ts.style.cssText = 'color:#555;min-width:42px;flex-shrink:0;';
    const h = entry.time.getHours().toString().padStart(2, '0');
    const m = entry.time.getMinutes().toString().padStart(2, '0');
    const s = entry.time.getSeconds().toString().padStart(2, '0');
    ts.textContent = `${h}:${m}:${s}`;
    const msg = document.createElement('span');
    msg.style.cssText = 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
    msg.textContent = entry.text;
    row.appendChild(ts);
    row.appendChild(msg);
    _logPanelEl.appendChild(row);
  }
}

// Calculate display dimensions that fit the canvas area
function getDisplayScale(w, h) {
  const area = document.querySelector('.canvas-area');
  if (!area) return Math.min(700 / w, 700 / h, 1);
  // Use the actual available space with comfortable padding
  const pad = window.innerWidth <= 800 ? 16 : 40;
  const maxW = area.clientWidth - pad * 2;
  const maxH = area.clientHeight - pad * 2;
  const scaleW = maxW / w;
  const scaleH = maxH / h;
  return Math.min(scaleW, scaleH, 1);
}

// ── Advanced Mode: per-size position management ──────────────────────────

/**
 * Save all content-object positions for the current size into a given store.
 * Positions are stored as proportional (0-1) of canvas dimensions for cross-size remapping.
 *
 * @param {string} sizeKey - The size key (e.g. 'poster', 'story')
 * @param {object} [store] - Position store to save into. Defaults to current mode's store.
 */
function savePositions(sizeKey, store) {
  if (!fabricCanvas) return;
  const target = store || (editorMode === 'advanced' ? advancedPositions : templatePositions);
  const positions = {};
  const canvasW = fabricCanvas.width;
  const canvasH = fabricCanvas.height;
  fabricCanvas.getObjects().forEach(obj => {
    if (!obj.data?.id) return;
    if (obj.data.stencilType === 'blendLayer') return; // blend layers saved separately
    if (obj.data.id.startsWith('__')) return; // skip internal objects
    positions[obj.data.id] = {
      leftPct: obj.left / canvasW,
      topPct: obj.top / canvasH,
      scaleX: obj.scaleX,
      scaleY: obj.scaleY,
      angle: obj.angle || 0,
      width: obj.width,
      fontSize: obj.fontSize,
      type: obj.type, // track object type for differentiated restore
    };
  });
  target[sizeKey] = positions;
}

// Legacy alias used in existing code
function saveAdvancedPositions(sizeKey) { savePositions(sizeKey, advancedPositions); }

/**
 * Restore saved positions from a given store for the target size.
 * Falls back to proportional remapping from prevSize if no saved data for newSize.
 *
 * @param {string} newSize - Size key to restore positions for
 * @param {string} prevSize - Previous size key (fallback for proportional remap)
 * @param {object} [store] - Position store to read from. Defaults to current mode's store.
 */
function restorePositions(newSize, prevSize, store) {
  if (!fabricCanvas) return;
  const target = store || (editorMode === 'advanced' ? advancedPositions : templatePositions);
  const canvasW = fabricCanvas.width;
  const canvasH = fabricCanvas.height;

  // Use saved positions for newSize if available, otherwise remap from prevSize
  const saved = target[newSize] || target[prevSize];
  if (!saved) return;
  const isExactMatch = !!target[newSize];

  fabricCanvas.getObjects().forEach(obj => {
    if (!obj.data?.id) return;
    if (obj.data.stencilType === 'blendLayer') return;
    if (obj.data.id.startsWith('__')) return;
    const pos = saved[obj.data.id];
    if (!pos) return;

    const newLeft = pos.leftPct * canvasW;
    const newTop = pos.topPct * canvasH;
    obj.set({
      left: Math.max(0, Math.min(canvasW, newLeft)),
      top: Math.max(0, Math.min(canvasH, newTop)),
      angle: pos.angle,
    });
    // Only restore exact font size / width if this is a saved position (not remapped)
    if (isExactMatch && obj.type === 'textbox') {
      if (pos.fontSize) obj.set('fontSize', pos.fontSize);
      if (pos.width) obj.set('width', pos.width);
    }
  });
  fabricCanvas.renderAll();
}

// Legacy alias
function restoreAdvancedPositions(newSize, prevSize) { restorePositions(newSize, prevSize, advancedPositions); }

/**
 * Resolve color tokens like "{primary}" in template objects against the scheme.
 */
function resolveColor(color, scheme) {
  if (!color || !color.startsWith('{')) return color;
  const key = color.slice(1, -1);
  return scheme[key] || color;
}

/**
 * Resolve a color token like "{primary}" to a hex value without a request context.
 * Works with scheme objects, scheme name strings, or falls back to the raw value.
 */
function resolveColorStatic(color, scheme) {
  if (!color) return null;
  if (!color.startsWith('{')) return color;
  const key = color.slice(1, -1);
  // scheme can be an object or a name string
  if (typeof scheme === 'object' && scheme !== null) {
    return scheme[key] || color;
  }
  // If scheme is a name string, look it up
  const resolved = getScheme(scheme || 'dark');
  if (resolved) return resolved[key] || color;
  return color;
}

/**
 * Apply style properties to a canvas object by its data.id.
 * Used by sidebar text-style controls for live preview.
 */
function applyStyleToCanvas(objId, styleProps) {
  if (!fabricCanvas) return;
  const obj = fabricCanvas.getObjects().find(o => o.data?.id === objId);
  if (!obj) return;
  obj.set(styleProps);
  obj.initDimensions();
  obj.setCoords();
  fabricCanvas.renderAll();
}

/**
 * Capture current user text edits from the canvas.
 * Returns a map of object id → text content.
 */
function captureTextEdits() {
  if (!fabricCanvas) return {};
  const edits = {};
  fabricCanvas.getObjects().forEach((obj) => {
    if (obj.data?.id && obj.data.stencilType === 'text') {
      edits[obj.data.id] = obj.text;
    }
  });
  return edits;
}

/**
 * Capture current text styling (font, size, color, weight, style, align) per object.
 * Used by mangle to preserve user choices across hydrateTemplate rebuilds.
 */
function captureTextStyles() {
  if (!fabricCanvas) return {};
  const styles = {};
  fabricCanvas.getObjects().forEach((obj) => {
    if (obj.data?.id && obj.type === 'textbox') {
      styles[obj.data.id] = {
        fontFamily: obj.fontFamily,
        fontSize: obj.fontSize,
        fill: obj.fill,
        fontWeight: obj.fontWeight,
        fontStyle: obj.fontStyle,
        underline: obj.underline,
        textAlign: obj.textAlign,
      };
    }
  });
  return styles;
}

/**
 * Restore text styling captured by captureTextStyles().
 * Only restores font and fontSize unconditionally (user choices).
 * Color, weight, style are NOT restored here — mangle will apply its own if probability hits.
 */
function restoreTextStyles(styles) {
  if (!fabricCanvas || !styles) return;
  fabricCanvas.getObjects().forEach((obj) => {
    if (obj.data?.id && obj.type === 'textbox' && styles[obj.data.id]) {
      const s = styles[obj.data.id];
      obj.set({
        fontFamily: s.fontFamily,
        fontSize: s.fontSize,
        fill: s.fill,
        fontWeight: s.fontWeight,
        fontStyle: s.fontStyle,
        underline: s.underline,
        textAlign: s.textAlign,
      });
    }
  });
}

/**
 * Snapshot all user-created text objects from the canvas into userTextObjects[].
 * Called before hydrateTemplate clears the canvas so user objects survive.
 */
function captureUserTextObjects() {
  if (!fabricCanvas) return;
  const canvasW = fabricCanvas.width;
  const canvasH = fabricCanvas.height;
  userTextObjects = userTextObjects.map(stored => {
    const live = fabricCanvas.getObjects().find(o => o.data?.id === stored.id);
    if (!live) return stored; // not on canvas yet (e.g., different size) — keep stored version
    return {
      id: stored.id,
      text: live.text,
      fontFamily: live.fontFamily,
      fontSize: live.fontSize,
      fontWeight: live.fontWeight,
      fontStyle: live.fontStyle,
      underline: live.underline,
      fill: live.fill,
      textAlign: live.textAlign,
      leftPct: live.left / canvasW,
      topPct: live.top / canvasH,
      width: live.width,
      visible: live.visible !== false,
      fxLocked: !!live.data?.fxLocked,
      textBg: live.data?.textBg || null,
      sliceEffect: live.data?.sliceEffect || null,
      vSliceEffect: live.data?.vSliceEffect || null,
    };
  });
}

/**
 * Re-add user-created text objects to the canvas after hydrateTemplate rebuild.
 * Scales positions proportionally to the current canvas dimensions.
 */
function rehydrateUserTextObjects() {
  if (!fabricCanvas || userTextObjects.length === 0) return;
  const canvasW = fabricCanvas.width;
  const canvasH = fabricCanvas.height;

  for (const uto of userTextObjects) {
    const textbox = new fabric.Textbox(uto.text || 'New Text', {
      left: uto.leftPct * canvasW,
      top: uto.topPct * canvasH,
      width: Math.min(uto.width || canvasW * 0.5, canvasW * 0.9),
      originX: 'left',
      originY: 'top',
      fontFamily: uto.fontFamily || 'Work Sans',
      fontSize: Math.max(8, uto.fontSize || 24),
      fontWeight: uto.fontWeight || 'normal',
      fontStyle: uto.fontStyle || 'normal',
      underline: uto.underline || false,
      fill: uto.fill || '#ffffff',
      textAlign: uto.textAlign || 'left',
      splitByGrapheme: false,
      editable: true,
      visible: uto.visible !== false,
      data: {
        id: uto.id,
        stencilType: 'text',
        role: 'user',
        userCreated: true,
        fxLocked: uto.fxLocked || false,
        textBg: uto.textBg || null,
        sliceEffect: uto.sliceEffect || null,
        vSliceEffect: uto.vSliceEffect || null,
      },
    });

    // Prevent vertical stretch
    textbox.on('scaling', () => {
      const newWidth = textbox.width * textbox.scaleX;
      textbox.set({ width: newWidth, scaleX: 1, scaleY: 1 });
    });

    fabricCanvas.add(textbox);

    // Re-apply text background if it was enabled
    if (uto.textBg?.enabled) {
      applyTextBackground(textbox);
    }
    // Re-apply slice effect if it was enabled
    if (uto.sliceEffect?.enabled || uto.vSliceEffect?.enabled) {
      applySliceEffect(textbox);
    }
  }
}

/**
 * Create a new user text object at the given canvas coordinates.
 * @param {number} canvasX - X position in canvas pixels
 * @param {number} canvasY - Y position in canvas pixels
 */
function addUserTextObject(canvasX, canvasY) {
  if (!fabricCanvas) return;
  const canvasW = fabricCanvas.width;
  const canvasH = fabricCanvas.height;

  const id = `user-text-${Date.now()}`;
  const scheme = currentState.scheme;
  const fill = scheme?.text || '#ffffff';
  const width = canvasW * 0.5;

  // Clamp position so the object is fully within the canvas
  const left = Math.max(0, Math.min(canvasX, canvasW - width));
  const top = Math.max(0, Math.min(canvasY, canvasH - 30));

  const entry = {
    id,
    text: 'New Text',
    fontFamily: 'Work Sans',
    fontSize: Math.round(canvasH * 0.04),
    fontWeight: 'normal',
    fontStyle: 'normal',
    underline: false,
    fill,
    textAlign: 'left',
    leftPct: left / canvasW,
    topPct: top / canvasH,
    width,
    visible: true,
    fxLocked: false,
    textBg: null,
    sliceEffect: null,
    vSliceEffect: null,
  };

  userTextObjects.push(entry);

  const textbox = new fabric.Textbox(entry.text, {
    left,
    top,
    width,
    originX: 'left',
    originY: 'top',
    fontFamily: entry.fontFamily,
    fontSize: entry.fontSize,
    fontWeight: entry.fontWeight,
    fill: entry.fill,
    textAlign: entry.textAlign,
    splitByGrapheme: false,
    editable: true,
    data: {
      id,
      stencilType: 'text',
      role: 'user',
      userCreated: true,
      fxLocked: false,
      textBg: null,
      sliceEffect: null,
      vSliceEffect: null,
    },
  });

  textbox.on('scaling', () => {
    const newWidth = textbox.width * textbox.scaleX;
    textbox.set({ width: newWidth, scaleX: 1, scaleY: 1 });
  });

  fabricCanvas.add(textbox);
  fabricCanvas.setActiveObject(textbox);
  fabricCanvas.renderAll();
  buildSidebar();
  updateObjectPanel(); // re-populate after buildSidebar wiped the panel
  logAction('Added text object');
}

/**
 * Remove a user-created text object by id.
 */
function removeUserTextObject(id) {
  userTextObjects = userTextObjects.filter(o => o.id !== id);
  const obj = fabricCanvas?.getObjects().find(o => o.data?.id === id);
  if (obj) {
    // Also remove its text background if present
    const bg = fabricCanvas.getObjects().find(o => o.data?.id === `__textbg_${id}`);
    if (bg) fabricCanvas.remove(bg);
    fabricCanvas.remove(obj);
    fabricCanvas.renderAll();
  }
  logAction('Removed text object');
}

/**
 * Duplicate one or more canvas objects.
 * All duplicates are created as user-created objects so they survive re-hydration.
 * Offset by 20px so the copies are visually distinguishable from originals.
 */
function duplicateObjects(objects) {
  if (!fabricCanvas || objects.length === 0) return;
  const canvasW = fabricCanvas.width;
  const canvasH = fabricCanvas.height;
  const offset = 20;
  const newObjs = [];

  for (const src of objects) {
    if (src.type !== 'textbox') continue; // only textbox duplication for now

    const id = `user-text-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const left = Math.min(src.left + offset, canvasW - 40);
    const top = Math.min(src.top + offset, canvasH - 40);

    const entry = {
      id,
      text: src.text || 'New Text',
      fontFamily: src.fontFamily || 'Work Sans',
      fontSize: src.fontSize || 24,
      fontWeight: src.fontWeight || 'normal',
      fontStyle: src.fontStyle || 'normal',
      underline: !!src.underline,
      fill: src.fill || '#ffffff',
      textAlign: src.textAlign || 'left',
      leftPct: left / canvasW,
      topPct: top / canvasH,
      width: src.width || canvasW * 0.5,
      visible: true,
      fxLocked: false,
      textBg: src.data?.textBg || null,
      sliceEffect: src.data?.sliceEffect || null,
      vSliceEffect: src.data?.vSliceEffect || null,
    };
    userTextObjects.push(entry);

    const textbox = new fabric.Textbox(entry.text, {
      left,
      top,
      width: entry.width,
      originX: 'left',
      originY: 'top',
      fontFamily: entry.fontFamily,
      fontSize: entry.fontSize,
      fontWeight: entry.fontWeight,
      fontStyle: entry.fontStyle,
      underline: entry.underline,
      fill: entry.fill,
      textAlign: entry.textAlign,
      splitByGrapheme: false,
      editable: true,
      data: {
        id,
        stencilType: 'text',
        role: 'user',
        userCreated: true,
        fxLocked: false,
        textBg: entry.textBg,
        sliceEffect: entry.sliceEffect,
        vSliceEffect: entry.vSliceEffect,
      },
    });

    textbox.on('scaling', () => {
      const newWidth = textbox.width * textbox.scaleX;
      textbox.set({ width: newWidth, scaleX: 1, scaleY: 1 });
    });

    fabricCanvas.add(textbox);
    newObjs.push(textbox);
  }

  if (newObjs.length === 1) {
    fabricCanvas.setActiveObject(newObjs[0]);
  } else if (newObjs.length > 1) {
    const sel = new fabric.ActiveSelection(newObjs, { canvas: fabricCanvas });
    fabricCanvas.setActiveObject(sel);
  }
  fabricCanvas.renderAll();
  buildSidebar();
  updateObjectPanel();
  logAction(`Duplicated ${newObjs.length} object(s)`);
}

/**
 * Hydrate the Fabric.js canvas with objects from a template definition.
 * Only call this when template or size changes — NOT for scheme/overlay changes.
 *
 * Objects are scaled from template coordinates (print resolution) to display
 * coordinates so text stays readable. Export uses multiplier to restore full res.
 * User text edits are preserved across size switches within the same template.
 */
function hydrateTemplate(template, scheme, size, textEditsOverride) {
  // Capture user text edits before clearing (unless overridden, e.g. during undo)
  const textEdits = textEditsOverride || captureTextEdits();
  const isFirstLoad = Object.keys(textEdits).length === 0;

  // Snapshot user-created text objects before clear — they'll be re-added after
  captureUserTextObjects();

  // On first load, apply template-default hidden states
  if (isFirstLoad) {
    for (const obj of template.objects) {
      if (obj.hidden) hiddenObjects.add(obj.id);
    }
  }

  fabricCanvas.clear();
  const sizeInfo = TEMPLATE_SIZES[size];
  displayScale = getDisplayScale(sizeInfo.width, sizeInfo.height);
  const dw = Math.round(sizeInfo.width * displayScale);
  const dh = Math.round(sizeInfo.height * displayScale);

  // Canvas at display size, zoom 1 — so text renders at readable pixel sizes
  fabricCanvas.setDimensions({ width: dw, height: dh });
  fabricCanvas.setZoom(1);

  // Render and set background
  updateBackground(scheme, size);

  // --- Three-zone layout engine ---
  //
  // Canvas is divided into three zones:
  //   1. HEADER — pinned to top edge (header-left, header-center, header-right)
  //   2. CONTENT — flow-layout in the middle (title, date, venue, lineup, info, etc.)
  //   3. FOOTER — pinned to bottom edge (footer-left, footer-center, footer-right)
  //
  // Content zone uses flow layout: objects are sorted by template Y, sized by role,
  // and positioned sequentially with distributed whitespace between groups.
  // Wide/ultra-wide formats get compact centered layout instead of squished vertical.

  const aspect = sizeInfo.width / sizeInfo.height;
  const isWide = aspect > 1.5;       // landscape: banners, covers, thumbnails
  const isUltraWide = aspect > 2.5;  // extreme: event banner, fb cover
  const isSquare = aspect > 0.9 && aspect < 1.15; // square-ish
  const isTall = aspect < 0.65;      // story, threads

  // --- Step 1: Compute role-based font sizes ---
  const roleSizes = getRoleFontSizes(dh, dw, aspect, isWide, isUltraWide);

  // --- Step 2: Separate objects into zones ---
  const HEADER_ROLES = new Set(['header-left', 'header-center', 'header-right']);
  const FOOTER_ROLES = new Set(['footer-left', 'footer-center', 'footer-right']);
  // Venue-info zone: pinned above footer, separate from main content flow
  const VENUE_INFO_ROLES = new Set(['venue', 'detail', 'time', 'admission', 'info']);

  const allTextObjs = template.objects.filter(o => o.type === 'text');
  const headerObjs = allTextObjs.filter(o => HEADER_ROLES.has(o.role));
  const footerObjs = allTextObjs.filter(o => FOOTER_ROLES.has(o.role));
  const venueInfoObjs = allTextObjs.filter(o => VENUE_INFO_ROLES.has(o.role));
  const contentObjs = allTextObjs.filter(o =>
    !HEADER_ROLES.has(o.role) && !FOOTER_ROLES.has(o.role) && !VENUE_INFO_ROLES.has(o.role)
  );
  const sorted = [...contentObjs].sort((a, b) => a.y - b.y);

  // Layout constants
  const REF_W = 2550;
  const marginX = dw * (isUltraWide ? 0.04 : 0.05);
  const maxTextWidth = isUltraWide ? dw * 0.85 : isWide ? dw * 0.7 : dw * 0.85;
  const headerMarginY = dh * (isUltraWide ? 0.04 : isWide ? 0.03 : 0.02);
  const footerMarginY = dh * (isUltraWide ? 0.04 : isWide ? 0.03 : 0.02);
  const headerFontSize = Math.max(8, Math.round(dh * (isUltraWide ? 0.045 : isWide ? 0.04 : 0.015)));
  const footerFontSize = Math.max(8, Math.round(dh * (isUltraWide ? 0.045 : isWide ? 0.04 : 0.015)));

  // Determine header/footer heights for content zone calculation
  const hasHeader = headerObjs.some(o => !hiddenObjects.has(o.id) && (textEdits[o.id] || o.content));
  const hasFooter = footerObjs.some(o => !hiddenObjects.has(o.id) && (textEdits[o.id] || o.content));
  const headerZoneH = hasHeader ? headerMarginY + headerFontSize * 1.5 + headerMarginY * 0.5 : 0;
  const footerZoneH = hasFooter ? footerMarginY + footerFontSize * 1.5 + footerMarginY * 0.5 : 0;

  // Estimate venue-info zone height (pinned above footer)
  const visibleVenueInfoObjs = venueInfoObjs.filter(o =>
    !hiddenObjects.has(o.id) && (textEdits[o.id] || o.content)
  );
  const venueInfoFontSize = roleSizes.detail || roleSizes._default;
  const venueInfoLineH = venueInfoFontSize * 1.4;
  const venueInfoZoneH = visibleVenueInfoObjs.length > 0
    ? visibleVenueInfoObjs.length * venueInfoLineH + dh * 0.02
    : 0;

  // --- Step 3: Create header objects (pinned to top) ---
  for (const obj of headerObjs) {
    const content = textEdits[obj.id] || obj.content;
    if (!content && hiddenObjects.has(obj.id)) continue;

    let leftPos, textWidth;
    if (obj.role === 'header-left') {
      leftPos = marginX;
      textWidth = (dw - marginX * 2) * 0.33;
    } else if (obj.role === 'header-center') {
      textWidth = (dw - marginX * 2) * 0.34;
      leftPos = (dw - textWidth) / 2;
    } else {
      textWidth = (dw - marginX * 2) * 0.33;
      leftPos = dw - textWidth - marginX;
    }

    const align = obj.role === 'header-left' ? 'left' : obj.role === 'header-center' ? 'center' : 'right';
    createTextbox(obj, content, headerFontSize, leftPos, headerMarginY, textWidth, align, scheme);
  }

  // --- Step 4: Create footer objects (pinned to bottom) ---
  for (const obj of footerObjs) {
    const content = textEdits[obj.id] || obj.content;
    if (!content && hiddenObjects.has(obj.id)) continue;

    let leftPos, textWidth;
    if (obj.role === 'footer-left') {
      leftPos = marginX;
      textWidth = (dw - marginX * 2) * 0.33;
    } else if (obj.role === 'footer-center') {
      textWidth = (dw - marginX * 2) * 0.34;
      leftPos = (dw - textWidth) / 2;
    } else {
      textWidth = (dw - marginX * 2) * 0.33;
      leftPos = dw - textWidth - marginX;
    }

    const yPos = dh - footerMarginY - footerFontSize;
    const align = obj.role === 'footer-left' ? 'left' : obj.role === 'footer-center' ? 'center' : 'right';
    createTextbox(obj, content, footerFontSize, leftPos, yPos, textWidth, align, scheme);
  }

  // --- Smart justification helpers (used by venue-info & content zones) ---
  const _roleToGroup = {};
  const _SIDEBAR_GROUPS = {
    'Event': ['title', 'date'],
    'Lineup': ['lineup-heading', 'lineup'],
    'Venue & Info': ['venue', 'detail', 'time', 'admission', 'info'],
  };
  for (const [gn, roles] of Object.entries(_SIDEBAR_GROUPS)) {
    for (const r of roles) _roleToGroup[r] = gn;
  }

  function getSmartAlign(obj, defaultAlign) {
    const group = _roleToGroup[obj.role];
    if (group && groupAlignOverrides[group]) return groupAlignOverrides[group];
    return defaultAlign;
  }

  // --- Step 4b: Create venue-info zone (pinned above footer) ---
  const venueInfoBottom = dh - footerZoneH - dh * (isUltraWide ? 0.04 : isWide ? 0.04 : 0.03);
  if (visibleVenueInfoObjs.length > 0) {
    const sortedVenueInfo = [...venueInfoObjs].sort((a, b) => a.y - b.y);
    let viCursorY = venueInfoBottom - venueInfoZoneH;

    for (const obj of sortedVenueInfo) {
      const content = textEdits[obj.id] || obj.content;
      if (!content && hiddenObjects.has(obj.id)) continue;

      const fontSize = roleSizes[obj.role] || venueInfoFontSize;
      const textWidth = isUltraWide ? dw * 0.4 : isWide ? dw * 0.5 : maxTextWidth;
      const baseAlign = isUltraWide ? 'center' : (obj.textAlign || 'left');
      const align = getSmartAlign(obj, baseAlign);

      let leftPos;
      if (align === 'center' || isUltraWide) leftPos = (dw - textWidth) / 2;
      else if (align === 'right') leftPos = dw - textWidth - marginX;
      else leftPos = marginX;

      createTextbox(obj, content, fontSize, leftPos, viCursorY, textWidth, align, scheme);
      viCursorY += fontSize * 1.4;
    }
  }

  // --- Step 5: Flow-layout the content zone ---
  const contentTop = headerZoneH + dh * (isUltraWide ? 0.04 : isWide ? 0.04 : 0.04);
  const contentBottom = venueInfoBottom - venueInfoZoneH - dh * 0.02;
  const usableH = contentBottom - contentTop;
  const intraGap = Math.max(2, dh * 0.01);

  function buildLayoutItems(objs, roleSizes, fontScale) {
    return objs.map(obj => {
      const content = textEdits[obj.id] || obj.content;
      const fontSize = (roleSizes[obj.role] || roleSizes._default) * fontScale;
      const isHidden = hiddenObjects.has(obj.id);

      const explicitLines = content.split('\n');
      const isBold = obj.fontWeight === 'bold';
      const isUpper = content === content.toUpperCase();
      const avgCharWidth = fontSize * (isBold ? 0.72 : isUpper ? 0.65 : 0.58);

      const longestLine = Math.max(...explicitLines.map(l => l.length));
      const estimatedContentWidth = longestLine * avgCharWidth;
      const templateWidth = Math.min(((obj.width || 600) / REF_W) * dw, maxTextWidth);
      const needsWrapping = estimatedContentWidth > templateWidth * 0.95;
      const textWidth = needsWrapping
        ? templateWidth
        : Math.min(Math.max(estimatedContentWidth * 1.15, dw * 0.2), templateWidth);

      const charsPerLine = Math.max(1, Math.floor(textWidth / avgCharWidth));
      let totalLines = 0;
      for (const line of explicitLines) {
        totalLines += Math.max(1, Math.ceil(line.length / charsPerLine));
      }

      const lineHeight = 1.25;
      const estimatedHeight = isHidden ? 0 : fontSize * lineHeight * totalLines;
      return { obj, content, fontSize, estimatedHeight, lineCount: totalLines, isHidden, textWidth };
    });
  }

  const REF_H = 3300;
  function buildGroups(items) {
    const grps = [[]];
    for (let i = 0; i < items.length; i++) {
      if (i > 0) {
        const gap = (sorted[i].y - sorted[i - 1].y) / REF_H;
        if (gap > 0.15) grps.push([]);
      }
      grps[grps.length - 1].push(items[i]);
    }
    return grps;
  }

  function measureContentH(grps) {
    let h = 0;
    grps.forEach(group => {
      group.forEach(item => { h += item.estimatedHeight; });
      h += (group.length - 1) * intraGap;
    });
    return h;
  }

  let fontScale = 1.0;
  let layoutItems = buildLayoutItems(sorted, roleSizes, fontScale);
  let groups = buildGroups(layoutItems);
  let totalContentH = measureContentH(groups);

  // Auto-shrink if content overflows (down to 50%)
  if (totalContentH > usableH && usableH > 0) {
    fontScale = Math.max(0.5, usableH / totalContentH * 0.92);
    layoutItems = buildLayoutItems(sorted, roleSizes, fontScale);
    groups = buildGroups(layoutItems);
    totalContentH = measureContentH(groups);
  }

  // Distribute whitespace between groups
  const interGroupGaps = Math.max(1, groups.length - 1);
  const remainingH = Math.max(0, usableH - totalContentH);
  const interGap = remainingH / interGroupGaps;

  // Assign Y positions — content only (headers/footers already placed)
  let cursorY = contentTop;
  const positionMap = {};

  // For wide formats, vertically center content in the content zone
  if (isUltraWide || isWide) {
    const totalWithGaps = totalContentH + (interGroupGaps * Math.min(interGap, dh * 0.15));
    cursorY = Math.max(contentTop, contentTop + (usableH - totalWithGaps) / 2);
  }

  groups.forEach((group, gi) => {
    group.forEach((item) => {
      positionMap[item.obj.id] = cursorY;
      cursorY += item.estimatedHeight + intraGap;
    });
    cursorY -= intraGap;
    if (gi < groups.length - 1) {
      cursorY += Math.min(interGap, dh * 0.2);
    }
  });

  // --- Step 6: Create content Fabric objects ---
  const centerText = isUltraWide;

  for (const item of layoutItems) {
    const { obj, content, fontSize } = item;
    const yPos = positionMap[obj.id] ?? contentTop;
    const textWidth = item.textWidth;

    const baseAlign = centerText ? 'center' : (obj.textAlign || 'left');
    const align = getSmartAlign(obj, baseAlign);

    let leftPos;
    if (centerText || align === 'center') {
      leftPos = (dw - textWidth) / 2;
    } else if (align === 'right') {
      leftPos = dw - textWidth - marginX;
    } else {
      leftPos = Math.min((obj.x / REF_W) * dw, dw - textWidth - marginX);
      leftPos = Math.max(marginX, leftPos);
    }

    createTextbox(obj, content, fontSize, leftPos, yPos, textWidth, align, scheme);
  }

  // --- Shared textbox creation helper ---
  function createTextbox(obj, content, fontSize, leftPos, yPos, textWidth, align, scheme) {
    // Apply user style overrides if they exist
    const overrides = textStyleOverrides[obj.id] || {};

    const textbox = new fabric.Textbox(content, {
      left: leftPos,
      top: yPos,
      width: textWidth,
      originX: 'left',
      originY: 'top',
      fontFamily: overrides.fontFamily || obj.fontFamily || 'DM Mono',
      fontSize: Math.max(8, Math.round(fontSize)),
      fontWeight: overrides.fontWeight || obj.fontWeight || 'normal',
      fontStyle: overrides.fontStyle || 'normal',
      fill: overrides.fill || resolveColor(obj.fill, scheme),
      textAlign: align,
      splitByGrapheme: false,
      editable: true,
      visible: !hiddenObjects.has(obj.id),
      backgroundColor: overrides.textBg || '',
      data: { id: obj.id, stencilType: 'text', role: obj.role },
    });

    textbox.on('scaling', () => {
      const newWidth = textbox.width * textbox.scaleX;
      textbox.set({ width: newWidth, scaleX: 1, scaleY: 1 });
    });

    // In Template Mode, lock all template text objects
    if (obj.locked || editorMode === 'template') {
      textbox.set({
        lockMovementX: true, lockMovementY: true, lockRotation: true,
        lockScalingX: true, lockScalingY: true,
        hasControls: false, selectable: editorMode !== 'template',
        editable: editorMode !== 'template',
        hoverCursor: editorMode === 'template' ? 'default' : 'move',
      });
    }

    fabricCanvas.add(textbox);
    return textbox;
  }

  // ── Restore blend layers from state (proportional → display pixels) ──
  if (currentState.blendLayers) {
    const canvasW = fabricCanvas.width;
    const canvasH = fabricCanvas.height;
    for (let i = 0; i < currentState.blendLayers.length; i++) {
      const layer = currentState.blendLayers[i];
      if (!layer) continue;
      // Support both legacy (x/y/width/height) and new proportional (xPct/yPct/wPct/hPct)
      const lx = layer.xPct != null ? Math.round(layer.xPct * canvasW) : (layer.x || 0);
      const ly = layer.yPct != null ? Math.round(layer.yPct * canvasH) : (layer.y || 0);
      const lw = layer.wPct != null ? Math.round(layer.wPct * canvasW) : (layer.width || 100);
      const lh = layer.hPct != null ? Math.round(layer.hPct * canvasH) : (layer.height || 100);
      const rect = new fabric.Rect({
        left: lx,
        top: ly,
        width: lw,
        height: lh,
        fill: layer.color,
        opacity: layer.opacity,
        globalCompositeOperation: layer.blendMode,
        originX: 'left',
        originY: 'top',
        strokeWidth: 1,
        stroke: 'rgba(255,255,255,0.15)',
        data: { id: `__blend_${i}`, stencilType: 'blendLayer', index: i },
      });
      rect.on('modified', () => syncBlendLayerToState(rect));
      fabricCanvas.add(rect);
    }
  }

  // Re-add user-created text objects (survive template rebuild)
  rehydrateUserTextObjects();

  fabricCanvas.renderAll();

  // Fonts loaded via Google Fonts CSS may arrive after Fabric.js first renders
  // textboxes, causing serif fallback rendering. We explicitly load every font
  // family used on this canvas, then force all textboxes to re-measure.
  // document.fonts.load() actually triggers the download — unlike .ready which
  // just waits for fonts already in the queue.
  const usedFonts = new Set();
  fabricCanvas.getObjects().forEach(obj => {
    if (obj.type === 'textbox' && obj.fontFamily) {
      const w = obj.fontWeight || '400';
      usedFonts.add(`${w} 16px "${obj.fontFamily}"`);
    }
  });
  if (usedFonts.size > 0) {
    Promise.all([...usedFonts].map(f => document.fonts.load(f).catch(() => null)))
      .then(() => {
        fabricCanvas.getObjects().forEach(obj => {
          if (obj.type === 'textbox') {
            obj.initDimensions();
            obj.setCoords();
          }
        });
        fabricCanvas.requestRenderAll();
      });
  }
}

/**
 * Role-based font sizing — returns pixel font sizes for each role.
 *
 * Sizes are calculated as proportions of canvas height, ensuring text
 * is always readable and never overflows. Adjusts for aspect ratio:
 * wide formats get smaller proportions since height is limited.
 */
function getRoleFontSizes(dh, dw, aspect, isWide, isUltraWide) {
  // Base proportions of canvas height for each role
  let title, date, venue, detail, body, info, footer, lineupHeading, lineup, time, admission;

  if (isUltraWide) {
    // Event banner / FB cover — bold title, compact everything else
    title  = 0.18;
    date   = 0.08;
    venue  = 0.07;
    detail = 0.055;
    body   = 0.07;
    lineupHeading = 0.055;
    lineup = 0.07;
    info   = 0.055;
    time   = 0.055;
    admission = 0.055;
    footer = 0.045;
  } else if (isWide) {
    // YouTube thumbnail, LinkedIn — medium sizes
    title  = 0.12;
    date   = 0.06;
    venue  = 0.05;
    detail = 0.04;
    body   = 0.055;
    lineupHeading = 0.035;
    lineup = 0.055;
    info   = 0.04;
    time   = 0.04;
    admission = 0.04;
    footer = 0.035;
  } else if (aspect > 0.9) {
    // Square, social vertical (4:5) — balanced
    title  = 0.07;
    date   = 0.035;
    venue  = 0.03;
    detail = 0.022;
    body   = 0.035;
    lineupHeading = 0.022;
    lineup = 0.035;
    info   = 0.022;
    time   = 0.022;
    admission = 0.022;
    footer = 0.018;
  } else {
    // Portrait: letter, tabloid, story, pinterest, threads
    title  = 0.05;
    date   = 0.025;
    venue  = 0.02;
    detail = 0.015;
    body   = 0.025;
    lineupHeading = 0.015;
    lineup = 0.025;
    info   = 0.015;
    time   = 0.015;
    admission = 0.015;
    footer = 0.012;
  }

  // Convert proportions to pixel sizes, with min/max bounds
  const clamp = (ratio, min, max) => Math.round(Math.max(min, Math.min(max, dh * ratio)));

  return {
    title:            clamp(title,  16, 120),
    date:             clamp(date,   10, 60),
    venue:            clamp(venue,  10, 50),
    detail:           clamp(detail, 9, 40),
    body:             clamp(body,   10, 55),
    'lineup-heading': clamp(lineupHeading, 9, 40),
    lineup:           clamp(lineup, 10, 55),
    info:             clamp(info,   9, 40),
    time:             clamp(time,   9, 40),
    admission:        clamp(admission, 9, 40),
    footer:           clamp(footer, 8, 36),
    _default:         clamp(0.025, 9, 40),
  };
}

/**
 * Update colors of existing objects when scheme changes.
 * Does NOT rebuild objects — preserves positions and user edits.
 */
function applySchemeToObjects(scheme) {
  const template = TEMPLATES[currentState.template];
  if (!template) return;

  fabricCanvas.getObjects().forEach((obj) => {
    if (!obj.data?.id) return;
    // Find the matching template object to get the color token
    const tmplObj = template.objects.find((t) => t.id === obj.data.id);
    if (tmplObj && tmplObj.fill) {
      obj.set('fill', resolveColor(tmplObj.fill, scheme));
    }
  });
  fabricCanvas.renderAll();
}

/**
 * Render background at FULL output resolution, then display it scaled down.
 *
 * Why full resolution? The line art algorithms use absolute pixel coordinates
 * (e.g., sin(x * freq)). Different canvas sizes produce different patterns
 * even with the same seed. By always rendering at the output resolution,
 * preview and export see the same artwork.
 *
 * The full-res data URL is cached so export can reuse it without regenerating.
 */
let _cachedBgDataUrl = null;

/**
 * Determine if premium features are in use — triggers the watermark.
 * Free tier: plain text on a flat colored background (no watermark).
 * Premium features that trigger watermark:
 *   - Generative line art (any type other than 'none')
 *   - Film grain overlay
 *   - Mangle (layout randomizer, seed > 0)
 *   - Wordmark (branding image or text on canvas)
 */
function shouldShowWatermark() {
  // Paid users (designer / ultra) never see watermarks
  if (isPro()) return false;

  const la = currentState.overlays?.lineArt;
  const la2 = currentState.overlays?.lineArt2;
  const grain = currentState.overlays?.grain;
  const hasLineArt = la && la.type !== 'none';
  const hasLineArt2 = la2 && la2.type !== 'none';
  const hasGrain = grain && grain.enabled;
  const hasMangle = getMangleSeed() > 0;
  const hasWordmarkObj = fabricCanvas
    ? fabricCanvas.getObjects().some(o => o.data?.id === '__wordmark')
    : false;

  return hasLineArt || hasLineArt2 || hasGrain || hasMangle || hasWordmarkObj;
}

function updateBackground(scheme, size) {
  const sizeInfo = TEMPLATE_SIZES[size];

  // Watermark only appears when premium features are active
  const showWatermark = shouldShowWatermark();

  // Always render at full output resolution for consistency
  const bgDataUrl = renderBackground({
    width: sizeInfo.width,
    height: sizeInfo.height,
    scheme,
    overlays: currentState.overlays,
    watermark: showWatermark,
  });
  _cachedBgDataUrl = bgDataUrl;

  fabric.FabricImage.fromURL(bgDataUrl).then((img) => {
    // Scale the full-res image down to display dimensions
    img.scaleX = displayScale;
    img.scaleY = displayScale;
    // Fabric.js v7 defaults originX/Y to 'center' — pin to top-left
    // so the background fills the canvas from (0,0)
    img.originX = 'left';
    img.originY = 'top';
    fabricCanvas.backgroundImage = img;
    fabricCanvas.renderAll();
  });
}

/**
 * Export the canvas as a full-resolution PNG.
 *
 * Strategy: manual compositing on an offscreen canvas.
 *   1. Draw the cached full-res background (same pixels as preview → identical art)
 *   2. Export Fabric objects only (no background) with multiplier to scale up
 *   3. Composite objects on top of background
 *   4. Download the result
 *
 * This avoids Fabric.js's broken backgroundImage handling with multiplier,
 * and guarantees the exported art matches the preview exactly.
 */
function exportPNG() {
  const sizeInfo = TEMPLATE_SIZES[currentSize];
  const fullW = sizeInfo.width;
  const fullH = sizeInfo.height;
  const multiplier = 1 / displayScale;

  // Use the cached full-res background (same render as the preview)
  const bgUrl = _cachedBgDataUrl || renderBackground({
    width: fullW,
    height: fullH,
    scheme: getScheme(currentState.scheme.name || 'dark'),
    overlays: currentState.overlays,
    watermark: shouldShowWatermark(),
  });

  // Step 1: Get Fabric objects without background
  const savedBg = fabricCanvas.backgroundImage;
  fabricCanvas.backgroundImage = null;
  const objectsDataUrl = fabricCanvas.toDataURL({
    format: 'png',
    quality: 1,
    multiplier: multiplier,
  });
  fabricCanvas.backgroundImage = savedBg;
  fabricCanvas.renderAll();

  // Step 2: Composite background + objects on offscreen canvas
  const bgImg = new Image();
  bgImg.onload = () => {
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = fullW;
    exportCanvas.height = fullH;
    const ctx = exportCanvas.getContext('2d');

    // Draw full-res background
    ctx.drawImage(bgImg, 0, 0, fullW, fullH);

    // Draw scaled-up objects on top
    const objImg = new Image();
    objImg.onload = () => {
      ctx.drawImage(objImg, 0, 0, fullW, fullH);

      // Embed metadata and download
      const rawDataUrl = exportCanvas.toDataURL('image/png');
      const template = TEMPLATES[currentState.template];
      const templateName = template ? template.name.replace(/\s+/g, '-').toLowerCase() : 'design';
      const metaDataUrl = embedPngMetadata(rawDataUrl, {
        'Software': 'stencil.cloud',
        'Author': 'stencil.cloud',
        'Source': 'https://stencil.cloud',
        'Copyright': 'Created with stencil.cloud',
        'Comment': `Design: ${templateName} | Size: ${currentSize} (${fullW}x${fullH}) | Scheme: ${currentState.scheme.name}`,
        'Title': `stencil.cloud — ${templateName}`,
      });
      const link = document.createElement('a');
      link.download = `stencil-${templateName}-${currentSize}.png`;
      link.href = metaDataUrl;
      link.click();
      logAction(`Exported PNG: ${currentSize}`);
    };
    objImg.src = objectsDataUrl;
  };
  bgImg.src = bgUrl;
}

/**
 * Build the sidebar controls.
 */
function buildSidebar() {
  // On mobile, the sidebar is hidden — content is rendered into the bottom sheet tabs.
  // Refresh the active mobile tab content instead of the sidebar.
  if (window.innerWidth <= 800 && _mobileSheet) {
    if (_activeTab) renderMobileTabContent(_activeTab);
    return;
  }

  const sidebar = document.getElementById('sidebar');
  sidebar.innerHTML = '';

  if (editorMode === 'template') {
    // ── TEMPLATE MODE ──
    // Form-driven: text fields in sidebar, canvas objects locked.
    // Like the original kit.fafo.date: you type here, layout engine positions everything.

    // Paste & Parse — quick import from clipboard
    sidebar.appendChild(buildSection('Quick Import', buildPasteParseButton()));

    // Reset text + template text fields
    const textFieldsContent = document.createElement('div');
    textFieldsContent.appendChild(buildResetTextButton());
    textFieldsContent.appendChild(buildTemplateTextFields());
    sidebar.appendChild(buildSection('Text Fields', textFieldsContent));

    // Remix button — prominent, full-width
    sidebar.appendChild(buildSection('Remix', buildRemixButton()));

    // Share button
    sidebar.appendChild(buildSection('Share', buildShareButton()));

    // ── Canvas — grouped section for document-level settings ──
    sidebar.appendChild(buildGroupSection('Layout', [
      { title: 'Editor Mode', content: buildEditorModeToggle() },
      { title: 'Template', content: buildTemplateSelector() },
      { title: 'Output Size', content: buildSizeSelector() },
      { title: 'Color Scheme', content: buildSchemeSelector() },
      { title: 'Presets', content: buildPresetsControls() },
      { title: 'New Design', content: buildNewDesignButton() },
    ]));

    // Wordmark
    sidebar.appendChild(buildSection('Wordmark', buildWordmarkControls()));

    // ── Algorithmic FX — grouped parent section ──
    sidebar.appendChild(buildGroupSection('Algorithmic FX', [
      { title: 'Line Art', content: buildLineArtControls() },
      { title: 'Line Art 2', content: buildLineArt2Controls() },
      { title: 'Film Grain', content: buildGrainControls() },
      { title: 'Mangle', content: buildMangleControls() },
    ]));

    // Blend layers
    sidebar.appendChild(buildSection('Blend Layers', buildBlendLayerControls()));

    // Event date picker
    sidebar.appendChild(buildSection('Event Date', buildDatePickerControls()));

    // QR Code
    sidebar.appendChild(buildSection('QR Code', buildQrControls()));

    // Hidden objects
    if (hiddenObjects.size > 0) {
      sidebar.appendChild(buildSection('Hidden Objects', buildHiddenObjectsList()));
    }
  } else {
    // ── ADVANCED MODE ──
    // Direct canvas manipulation: select, move, resize objects on canvas.

    // Selected object properties — top of sidebar for fast access
    const objProps = document.createElement('div');
    objProps.id = 'object-properties';
    sidebar.appendChild(buildSection('Selected Object', objProps));

    // Remix button — prominent, full-width
    sidebar.appendChild(buildSection('Remix', buildRemixButton()));

    // Share button
    sidebar.appendChild(buildSection('Share', buildShareButton()));

    // ── Canvas — grouped section for document-level settings ──
    sidebar.appendChild(buildGroupSection('Layout', [
      { title: 'Editor Mode', content: buildEditorModeToggle() },
      { title: 'Template', content: buildTemplateSelector() },
      { title: 'Output Size', content: buildSizeSelector() },
      { title: 'Color Scheme', content: buildSchemeSelector() },
      { title: 'Presets', content: buildPresetsControls() },
      { title: 'New Design', content: buildNewDesignButton() },
    ]));

    // Wordmark
    sidebar.appendChild(buildSection('Wordmark', buildWordmarkControls()));

    // ── Algorithmic FX — grouped parent section ──
    sidebar.appendChild(buildGroupSection('Algorithmic FX', [
      { title: 'Line Art', content: buildLineArtControls() },
      { title: 'Line Art 2', content: buildLineArt2Controls() },
      { title: 'Film Grain', content: buildGrainControls() },
      { title: 'Mangle', content: buildMangleControls() },
    ]));

    // Blend layers
    sidebar.appendChild(buildSection('Blend Layers', buildBlendLayerControls()));

    // Event date picker
    sidebar.appendChild(buildSection('Event Date', buildDatePickerControls()));

    // QR Code
    sidebar.appendChild(buildSection('QR Code', buildQrControls()));

    // Hidden objects
    if (hiddenObjects.size > 0) {
      sidebar.appendChild(buildSection('Hidden Objects', buildHiddenObjectsList()));
    }

    // Snap to grid — only in Advanced mode (template mode locks positions)
    const snapRow = document.createElement('div');
    snapRow.className = 'check-row';
    snapRow.style.marginBottom = '12px';
    const snapCheck = document.createElement('input');
    snapCheck.type = 'checkbox';
    snapCheck.id = 'snap-check';
    snapCheck.checked = snapEnabled;
    snapCheck.onchange = () => {
      snapEnabled = snapCheck.checked;
      logAction(snapEnabled ? 'Snap to grid ON' : 'Snap to grid OFF');
    };
    const snapLabel = document.createElement('label');
    snapLabel.htmlFor = 'snap-check';
    snapLabel.className = 'checkbox-label';
    snapLabel.style.cssText = 'font-size:11px;color:var(--text-dim);';
    snapLabel.textContent = `Snap to grid (${snapGridSize}px)`;
    snapLabel.title = 'When enabled, objects snap to a grid when dragged for precise alignment';
    snapRow.appendChild(snapCheck);
    snapRow.appendChild(snapLabel);
    sidebar.appendChild(snapRow);
  }
}

/**
 * Build template selector cards.
 */
// ─────────────────────────────────────────────────────────────────────────────
// NEW DESIGN + RESET TEXT
// ─────────────────────────────────────────────────────────────────────────────

function buildNewDesignButton() {
  const container = document.createElement('div');

  const btn = document.createElement('button');
  btn.className = 'btn-small';
  btn.style.cssText = `
    width:100%;padding:8px 14px;font-size:11px;letter-spacing:2px;
    font-weight:600;text-transform:uppercase;
    border:1px solid var(--border);color:var(--text-dim);
    background:transparent;cursor:pointer;
    transition:background 0.2s, color 0.2s;
  `;
  btn.textContent = 'NEW DESIGN';
  btn.title = 'Reset everything to a fresh blank design';
  btn.onmouseenter = () => { btn.style.borderColor = '#ff4444'; btn.style.color = '#ff4444'; };
  btn.onmouseleave = () => { btn.style.borderColor = 'var(--border)'; btn.style.color = 'var(--text-dim)'; };
  btn.onclick = () => {
    if (!confirm('Start a new design? This will clear all your current work.')) return;
    resetToNewDesign();
  };

  container.appendChild(btn);
  return container;
}

function resetToNewDesign() {
  // Clear all state
  resetMangle();
  Object.keys(textStyleOverrides).forEach(k => delete textStyleOverrides[k]);
  Object.keys(groupAlignOverrides).forEach(k => delete groupAlignOverrides[k]);
  userTextObjects.length = 0;
  hiddenObjects.clear();
  Object.keys(advancedPositions).forEach(k => delete advancedPositions[k]);
  Object.keys(templatePositions).forEach(k => delete templatePositions[k]);
  userModifiedSizes.clear();
  _lastRemixGallery = null;

  // Reset to default template + scheme
  const defaultTemplate = TEMPLATES['generic-event'];
  const defaultScheme = getScheme(defaultTemplate.defaultScheme || 'dark');

  currentState.template = 'generic-event';
  currentState.scheme = { ...defaultScheme, name: defaultTemplate.defaultScheme || 'dark' };
  currentState.overlays = {
    lineArt: { ...defaultTemplate.overlays.lineArt },
    lineArt2: { type: 'none', density: 30, opacity: 0.2, seed: 137 },
    grain: { ...defaultTemplate.overlays.grain },
  };
  currentState.eventDateISO = '';
  currentState.dateFormat = 'dayMonth';
  currentState.eventTime = '';
  currentState._mangleParams = { spread: 50, angle: 0, colorShift: 0, styleShift: 0 };
  currentState._wordmarkText = '';
  currentState._wordmarkFont = '';
  currentState._wordmarkColor = '';
  currentState._wordmarkBold = false;
  currentState._wordmarkItalic = false;
  currentState._wordmarkUppercase = false;
  currentState._wordmarkAccent = '';
  currentState.blendLayers = [];

  currentSize = DEFAULT_SIZE;
  editorMode = 'template';

  buildSidebar();
  hydrateTemplate(defaultTemplate, defaultScheme, currentSize);
  updateBackground(defaultScheme, currentSize);
  logAction('New design');

  // Clear localStorage
  try { localStorage.removeItem('stencil-editor-state'); } catch(_) {}
}

function buildResetTextButton() {
  const container = document.createElement('div');
  container.style.cssText = 'display:flex;gap:8px;margin-bottom:8px;';

  const btn = document.createElement('button');
  btn.className = 'btn-small';
  btn.style.cssText = `
    flex:1;padding:6px 10px;font-size:10px;letter-spacing:1px;
    font-weight:600;text-transform:uppercase;
    border:1px solid var(--border);color:var(--text-dim);
    background:transparent;cursor:pointer;
  `;
  btn.textContent = 'RESET TEXT';
  btn.title = 'Reset all text fields to template defaults';
  btn.onclick = () => {
    const template = TEMPLATES[currentState.template];
    if (!template) return;
    pushUndoState(fabricCanvas, currentState, currentSize);

    // Reset every text object to the template's default content
    fabricCanvas.getObjects().forEach(obj => {
      if (obj.data?.id && obj.type === 'textbox') {
        const tmplObj = template.objects.find(t => t.id === obj.data.id);
        if (tmplObj) {
          obj.set('text', tmplObj.content || '');
        }
      }
    });
    fabricCanvas.renderAll();
    buildSidebar();
    logAction('Reset text to defaults');
  };
  container.appendChild(btn);

  return container;
}

function buildTemplateSelector() {
  const container = document.createElement('div');
  const select = document.createElement('select');
  select.className = 'field-select template-select';
  select.title = 'Choose a template layout';

  const templateList = Object.entries(TEMPLATES).map(([key, tmpl]) => ({
    key,
    label: tmpl.name || key,
  }));

  for (const t of templateList) {
    const opt = document.createElement('option');
    opt.value = t.key;
    opt.textContent = t.label;
    if (currentState.template === t.key) opt.selected = true;
    select.appendChild(opt);
  }

  select.onchange = () => {
    const key = select.value;
    currentState.template = key;
    hiddenObjects.clear();
    const template = TEMPLATES[key];
    const scheme = getScheme(currentState.scheme.name || 'dark');
    hydrateTemplate(template, scheme, currentSize);
    logAction(`Template: ${template.name || key}`);
    pushUndoState(fabricCanvas, currentState, currentSize);
    buildSidebar();
  };

  container.appendChild(select);
  return container;
}

function buildSection(title, content) {
  const section = document.createElement('div');
  section.className = 'section';

  const titleEl = document.createElement('div');
  titleEl.className = 'section-title';
  titleEl.style.cursor = 'pointer';
  titleEl.style.display = 'flex';
  titleEl.style.justifyContent = 'space-between';
  titleEl.style.alignItems = 'center';
  titleEl.style.userSelect = 'none';

  const titleText = document.createElement('span');
  titleText.textContent = title;

  const arrow = document.createElement('span');
  arrow.className = 'section-arrow';
  arrow.textContent = sectionCollapsed[title] ? '\u25B6' : '\u25BC';

  titleEl.appendChild(titleText);
  titleEl.appendChild(arrow);

  const contentWrapper = document.createElement('div');
  contentWrapper.appendChild(content);
  if (sectionCollapsed[title]) {
    contentWrapper.style.display = 'none';
  }

  titleEl.onclick = () => {
    sectionCollapsed[title] = !sectionCollapsed[title];
    contentWrapper.style.display = sectionCollapsed[title] ? 'none' : '';
    arrow.textContent = sectionCollapsed[title] ? '\u25B6' : '\u25BC';
  };

  section.appendChild(titleEl);
  section.appendChild(contentWrapper);
  return section;
}

/**
 * Build a grouped parent section containing multiple child sections.
 * Collapsing the parent hides all children; each child also has independent collapse.
 */
function buildGroupSection(title, children) {
  const section = document.createElement('div');
  section.className = 'section section-group';

  const titleEl = document.createElement('div');
  titleEl.className = 'section-title section-group-title';
  titleEl.style.cursor = 'pointer';
  titleEl.style.display = 'flex';
  titleEl.style.justifyContent = 'space-between';
  titleEl.style.alignItems = 'center';
  titleEl.style.userSelect = 'none';

  const titleText = document.createElement('span');
  titleText.textContent = title;

  const arrow = document.createElement('span');
  arrow.className = 'section-arrow';
  arrow.textContent = sectionCollapsed[title] ? '\u25B6' : '\u25BC';

  titleEl.appendChild(titleText);
  titleEl.appendChild(arrow);

  const contentWrapper = document.createElement('div');
  contentWrapper.className = 'section-group-children';
  if (sectionCollapsed[title]) {
    contentWrapper.style.display = 'none';
  }

  for (const child of children) {
    contentWrapper.appendChild(buildSection(child.title, child.content));
  }

  titleEl.onclick = () => {
    sectionCollapsed[title] = !sectionCollapsed[title];
    contentWrapper.style.display = sectionCollapsed[title] ? 'none' : '';
    arrow.textContent = sectionCollapsed[title] ? '\u25B6' : '\u25BC';
  };

  section.appendChild(titleEl);
  section.appendChild(contentWrapper);
  return section;
}

// ── BLEND MODE FILTER LAYERS ──────────────────────────────────────────────
// Up to 3 draggable Rect objects with Canvas2D blend modes (multiply, etc.)

const BLEND_MODES = [
  'normal', 'multiply', 'screen', 'overlay', 'darken', 'lighten',
  'color-dodge', 'color-burn', 'hard-light', 'soft-light',
  'difference', 'exclusion', 'hue', 'saturation', 'color', 'luminosity',
];

/**
 * Add a blend layer at the given slot (0-2).
 * Blend layer positions are stored as PROPORTIONAL (0-1) of template dimensions
 * and converted to display pixels on render. This prevents position drift on
 * export/resize when displayScale changes.
 */
function addBlendLayer(index) {
  if (!fabricCanvas) return;

  const sizeInfo = TEMPLATE_SIZES[currentSize];
  const dw = Math.round(sizeInfo.width * displayScale);
  const dh = Math.round(sizeInfo.height * displayScale);

  // Initialize state if needed
  if (!currentState.blendLayers) currentState.blendLayers = [];

  // Store proportional values (0-1) — scale-independent
  const layerState = {
    color: '#e040a0',
    opacity: 0.3,
    blendMode: 'multiply',
    xPct: 0.3 + index * 0.03,   // ~30% from left + stagger
    yPct: 0.3 + index * 0.03,   // ~30% from top + stagger
    wPct: 0.4,                    // 40% of canvas width
    hPct: 0.4,                    // 40% of canvas height
  };

  const rect = new fabric.Rect({
    left: Math.round(layerState.xPct * dw),
    top: Math.round(layerState.yPct * dh),
    width: Math.round(layerState.wPct * dw),
    height: Math.round(layerState.hPct * dh),
    fill: layerState.color,
    opacity: layerState.opacity,
    globalCompositeOperation: layerState.blendMode,
    originX: 'left',
    originY: 'top',
    strokeWidth: 1,
    stroke: 'rgba(255,255,255,0.15)',
    data: { id: `__blend_${index}`, stencilType: 'blendLayer', index },
  });

  fabricCanvas.add(rect);
  rect.on('modified', () => syncBlendLayerToState(rect));

  currentState.blendLayers[index] = layerState;
  logAction(`Added blend layer ${index + 1}`);
  pushUndoState(fabricCanvas, currentState, currentSize);
  buildSidebar();
}

function removeBlendLayer(index) {
  if (!fabricCanvas) return;
  const obj = fabricCanvas.getObjects().find(o => o.data?.id === `__blend_${index}`);
  if (obj) fabricCanvas.remove(obj);
  if (currentState.blendLayers) currentState.blendLayers[index] = null;
  logAction(`Removed blend layer ${index + 1}`);
  pushUndoState(fabricCanvas, currentState, currentSize);
  buildSidebar();
}

/**
 * Sync a blend layer Rect's display-pixel position back to proportional state.
 * Called after the user drags or resizes a blend layer on canvas.
 */
function syncBlendLayerToState(rect) {
  if (!currentState.blendLayers) return;
  const idx = rect.data?.index;
  if (idx == null || !currentState.blendLayers[idx]) return;

  const canvasW = fabricCanvas.width;
  const canvasH = fabricCanvas.height;
  const actualW = Math.round(rect.width * rect.scaleX);
  const actualH = Math.round(rect.height * rect.scaleY);

  // Store as proportional
  currentState.blendLayers[idx].xPct = rect.left / canvasW;
  currentState.blendLayers[idx].yPct = rect.top / canvasH;
  currentState.blendLayers[idx].wPct = actualW / canvasW;
  currentState.blendLayers[idx].hPct = actualH / canvasH;

  // Reset scale after capturing dimensions
  rect.set({ scaleX: 1, scaleY: 1, width: actualW, height: actualH });
}

function updateBlendLayerProp(index, prop, value) {
  if (!currentState.blendLayers?.[index]) return;
  currentState.blendLayers[index][prop] = value;

  const obj = fabricCanvas?.getObjects().find(o => o.data?.id === `__blend_${index}`);
  if (!obj) return;

  if (prop === 'color') obj.set('fill', value);
  else if (prop === 'opacity') obj.set('opacity', value);
  else if (prop === 'blendMode') obj.set('globalCompositeOperation', value);

  fabricCanvas.renderAll();
}

function buildBlendLayerControls() {
  const container = document.createElement('div');
  if (!currentState.blendLayers) currentState.blendLayers = [];

  const layers = currentState.blendLayers;
  const activeCount = layers.filter(l => l != null).length;

  for (let i = 0; i < 3; i++) {
    const layer = layers[i];
    if (!layer) continue;

    const card = document.createElement('div');
    card.style.cssText = 'background:var(--bg);border:1px solid var(--border);padding:8px;margin-bottom:6px;border-radius:3px;';

    const header = document.createElement('div');
    header.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;';
    const title = document.createElement('span');
    title.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;letter-spacing:1px;';
    title.textContent = `LAYER ${i + 1}`;
    const removeBtn = document.createElement('button');
    removeBtn.className = 'btn-small';
    removeBtn.textContent = '\u2715';
    removeBtn.style.cssText = 'padding:2px 6px;font-size:9px;';
    removeBtn.title = 'Remove this blend layer from the canvas';
    removeBtn.onclick = () => removeBlendLayer(i);
    header.appendChild(title);
    header.appendChild(removeBtn);
    card.appendChild(header);

    // Blend mode select
    const modeSelect = document.createElement('select');
    modeSelect.className = 'field-select';
    modeSelect.style.marginBottom = '4px';
    modeSelect.title = 'Blend mode — how this color layer mixes with layers below (e.g. multiply, screen, overlay)';
    for (const mode of BLEND_MODES) {
      const opt = document.createElement('option');
      opt.value = mode;
      opt.textContent = mode;
      opt.selected = layer.blendMode === mode;
      modeSelect.appendChild(opt);
    }
    modeSelect.onchange = () => { updateBlendLayerProp(i, 'blendMode', modeSelect.value); logAction(`Blend ${i+1}: ${modeSelect.value}`); };
    card.appendChild(modeSelect);

    // Color + opacity row
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:6px;align-items:center;';
    const colorInput = document.createElement('input');
    colorInput.type = 'color';
    colorInput.className = 'field-color';
    colorInput.value = layer.color;
    colorInput.title = 'Color of this blend layer overlay';
    colorInput.oninput = () => updateBlendLayerProp(i, 'color', colorInput.value);

    const opSlider = document.createElement('input');
    opSlider.type = 'range';
    opSlider.className = 'field-range';
    opSlider.style.flex = '1';
    opSlider.min = 0;
    opSlider.max = 100;
    opSlider.value = Math.round(layer.opacity * 100);
    opSlider.title = 'Transparency of this blend layer (0 = invisible, 100 = fully opaque)';
    opSlider.oninput = () => updateBlendLayerProp(i, 'opacity', parseInt(opSlider.value) / 100);

    row.appendChild(colorInput);
    row.appendChild(opSlider);
    card.appendChild(row);

    container.appendChild(card);
  }

  // Add layer button (max 3)
  if (activeCount < 3) {
    const addBtn = document.createElement('button');
    addBtn.className = 'btn-small';
    addBtn.style.width = '100%';
    addBtn.textContent = `+ Add Blend Layer (${activeCount}/3)`;
    addBtn.title = 'Add a new color overlay with a Photoshop-style blend mode';
    addBtn.onclick = () => {
      // Find first empty slot
      let slot = 0;
      while (slot < 3 && layers[slot] != null) slot++;
      addBlendLayer(slot);
    };
    container.appendChild(addBtn);
  }

  if (activeCount === 0) {
    const hint = document.createElement('div');
    hint.className = 'hint';
    hint.textContent = 'Add color overlays with blend modes (multiply, screen, overlay, etc.)';
    hint.style.marginBottom = '6px';
    container.insertBefore(hint, container.firstChild);
  }

  return container;
}

// ── TEXT SLICE / CHOP EFFECT ──────────────────────────────────────────────
// Horizontal & vertical cuts through text via native Canvas2D ctx.clip().
// Avoids Fabric.js clipPath which is unreliable on Textbox in v7.
//
// sliceEffect: { enabled, count, gap }                     — horizontal slicer
// vSliceEffect: { enabled, count, gap, spread, seed }      — vertical slicer
//   spread: 0 = clean columns, >0 = shatter/explode offset per slice

function applySliceEffect(textbox) {
  const hCfg = textbox.data?.sliceEffect;
  const vCfg = textbox.data?.vSliceEffect;

  const hActive = hCfg?.enabled;
  const vActive = vCfg?.enabled;

  if (!hActive && !vActive) {
    removeSliceEffect(textbox);
    return;
  }

  // Disable object caching so our custom render runs every frame
  textbox.objectCaching = false;

  // Store the original _render if we haven't already
  if (!textbox._originalRender) {
    textbox._originalRender = textbox._render.bind(textbox);
  }

  // Override _render with a clipped version
  textbox._render = function(ctx) {
    const hsc = this.data?.sliceEffect;
    const vsc = this.data?.vSliceEffect;
    const hOn = hsc?.enabled;
    const vOn = vsc?.enabled;

    if (!hOn && !vOn) {
      this._originalRender(ctx);
      return;
    }

    const h = this.height;
    const w = this.width;
    const halfH = h / 2;
    const halfW = w / 2 + 10;

    // ── Vertical slicer with spread/shatter ──
    if (vOn && !hOn) {
      const count = vsc.count || 5;
      const gapPx = Math.max(1, (vsc.gap || 3) * (w / 120));
      const bandWidth = (w + 20 - gapPx * count) / (count + 1);
      const spread = vsc.spread || 0;
      const seed = vsc.seed || 42;

      if (bandWidth < 2) { this._originalRender(ctx); return; }

      let x = -halfW;
      for (let i = 0; i <= count; i++) {
        const bw = Math.max(1, bandWidth);
        // Spread offset: seeded pseudo-random vertical shift per column
        const offsetY = spread > 0
          ? ((((seed * (i + 1) * 9301 + 49297) % 233280) / 233280 - 0.5) * 2 * spread * (h / 20))
          : 0;
        ctx.save();
        ctx.beginPath();
        ctx.rect(x, -halfH - Math.abs(offsetY) - 10, bw, h + Math.abs(offsetY) * 2 + 20);
        ctx.clip();
        ctx.translate(0, offsetY);
        this._originalRender(ctx);
        ctx.restore();
        x += bw + gapPx;
      }
      return;
    }

    // ── Horizontal slicer only ──
    if (hOn && !vOn) {
      const count = hsc.count || 5;
      const gapPx = Math.max(1, (hsc.gap || 3) * (h / 80));
      const bandHeight = (h - gapPx * count) / (count + 1);
      if (bandHeight < 2) { this._originalRender(ctx); return; }

      ctx.save();
      ctx.beginPath();
      let y = -halfH;
      for (let i = 0; i <= count; i++) {
        const bh = Math.max(1, bandHeight);
        ctx.rect(-halfW, y, halfW * 2, bh);
        y += bh + gapPx;
      }
      ctx.clip();
      this._originalRender(ctx);
      ctx.restore();
      return;
    }

    // ── Both horizontal + vertical ──
    if (hOn && vOn) {
      const hCount = hsc.count || 5;
      const hGap = Math.max(1, (hsc.gap || 3) * (h / 80));
      const bandH = (h - hGap * hCount) / (hCount + 1);
      const vCount = vsc.count || 5;
      const vGap = Math.max(1, (vsc.gap || 3) * (w / 120));
      const bandW = (w + 20 - vGap * vCount) / (vCount + 1);
      const spread = vsc.spread || 0;
      const seed = vsc.seed || 42;

      if (bandH < 2 || bandW < 2) { this._originalRender(ctx); return; }

      let x = -halfW;
      for (let vi = 0; vi <= vCount; vi++) {
        const bw = Math.max(1, bandW);
        const offsetY = spread > 0
          ? ((((seed * (vi + 1) * 9301 + 49297) % 233280) / 233280 - 0.5) * 2 * spread * (h / 20))
          : 0;

        let y = -halfH;
        for (let hi = 0; hi <= hCount; hi++) {
          const bh = Math.max(1, bandH);
          ctx.save();
          ctx.beginPath();
          ctx.rect(x, y, bw, bh);
          ctx.clip();
          ctx.translate(0, offsetY);
          this._originalRender(ctx);
          ctx.restore();
          y += bh + hGap;
        }
        x += bw + vGap;
      }
      return;
    }
  };

  textbox.dirty = true;
  fabricCanvas.renderAll();
}

function removeSliceEffect(textbox) {
  // Mark configs as disabled but DON'T null them out — the button
  // style updates still need to read .enabled after this returns
  if (textbox.data?.sliceEffect) textbox.data.sliceEffect.enabled = false;
  if (textbox.data?.vSliceEffect) textbox.data.vSliceEffect.enabled = false;

  // Restore original render function
  if (textbox._originalRender) {
    textbox._render = textbox._originalRender;
    delete textbox._originalRender;
  }

  // Remove any stale clipPath from previous approaches
  textbox.clipPath = null;

  // Re-enable caching
  textbox.objectCaching = true;
  textbox.dirty = true;
  fabricCanvas.renderAll();
}

// ── TEXT FIELD BACKGROUND ─────────────────────────────────────────────────
// Colored rect behind a text object with adjustable color and opacity

function applyTextBackground(textbox) {
  const cfg = textbox.data?.textBg;
  const existingBg = fabricCanvas?.getObjects().find(
    o => o.data?.id === `__textbg_${textbox.data.id}`
  );

  if (!cfg?.enabled) {
    if (existingBg) fabricCanvas.remove(existingBg);
    return;
  }

  const padX = cfg.padX ?? 10;
  const padY = cfg.padY ?? 4;

  if (existingBg) {
    // Update existing background rect
    existingBg.set({
      left: textbox.left - padX,
      top: textbox.top - padY,
      width: textbox.width + padX * 2,
      height: textbox.height + padY * 2,
      fill: cfg.color || 'rgba(0,0,0,0.5)',
      opacity: cfg.opacity ?? 0.5,
    });
  } else {
    // Create new background rect
    const bgRect = new fabric.Rect({
      left: textbox.left - padX,
      top: textbox.top - padY,
      width: textbox.width + padX * 2,
      height: textbox.height + padY * 2,
      fill: cfg.color || '#000000',
      opacity: cfg.opacity ?? 0.5,
      originX: 'left',
      originY: 'top',
      selectable: false,
      evented: false,
      data: { id: `__textbg_${textbox.data.id}`, stencilType: 'textBg' },
    });

    // Insert below the textbox in z-order (v7 API: insertAt(index, ...objects))
    const textIdx = fabricCanvas.getObjects().indexOf(textbox);
    fabricCanvas.insertAt(textIdx, bgRect);
  }
}

/** Sync all text backgrounds to their text objects' current positions. */
function syncTextBackgrounds() {
  if (!fabricCanvas) return;
  // Copy the array — getObjects() returns the live internal array in v7,
  // and applyTextBackground may insert new objects via insertAt.
  [...fabricCanvas.getObjects()].forEach(obj => {
    if (obj.type === 'textbox' && obj.data?.textBg?.enabled) {
      applyTextBackground(obj);
    }
  });
  fabricCanvas.renderAll();
}

/**
 * Build the Template Mode / Advanced Mode toggle.
 *
 * Template Mode: positions come from the template layout engine. Size switches re-layout.
 * Advanced Mode: positions are free-placed by the user and stored per-size. Size switches
 *   proportionally remap or restore saved positions.
 */
function buildEditorModeToggle() {
  const container = document.createElement('div');
  container.style.cssText = 'display:flex;gap:4px;margin-bottom:4px;';

  const makeBtn = (label, mode) => {
    const btn = document.createElement('button');
    btn.className = 'btn-small' + (editorMode === mode ? ' active' : '');
    btn.textContent = label;
    btn.style.flex = '1';
    btn.onclick = () => {
      if (editorMode === mode) return;
      const prevMode = editorMode;

      // Save current positions into the OUTGOING mode's store before switching
      savePositions(currentSize, prevMode === 'advanced' ? advancedPositions : templatePositions);

      editorMode = mode;

      if (mode === 'advanced') {
        // Entering Advanced Mode: restore saved advanced positions if they exist
        if (advancedPositions[currentSize]) {
          restorePositions(currentSize, currentSize, advancedPositions);
        }
        // Otherwise keep current positions (template layout becomes the starting point)
        logAction('Advanced Mode — free placement');
      } else {
        // Entering Template Mode: restore saved template positions if they exist
        if (templatePositions[currentSize]) {
          restorePositions(currentSize, currentSize, templatePositions);
        } else {
          // No saved template positions — re-run layout engine for a fresh layout
          const template = TEMPLATES[currentState.template];
          const scheme = getScheme(currentState.scheme.name || 'dark');
          hydrateTemplate(template, scheme, currentSize);
        }
        logAction('Template Mode — auto-layout');
      }
      pushUndoState(fabricCanvas, currentState, currentSize);
      buildSidebar();
    };
    return btn;
  };

  const templateBtn = makeBtn('TEMPLATE', 'template');
  templateBtn.title = 'Template Mode — objects positioned by the template layout engine';
  const advancedBtn = makeBtn('ADVANCED', 'advanced');
  advancedBtn.title = 'Advanced Mode — free placement, positions saved per output size';
  container.appendChild(templateBtn);
  container.appendChild(advancedBtn);

  // Hint text
  const hint = document.createElement('div');
  hint.className = 'hint';
  hint.style.marginTop = '4px';
  hint.textContent = editorMode === 'advanced'
    ? 'Free placement — positions saved per size'
    : 'Auto-layout from template definitions';

  // Reset Layout button
  const resetBtn = document.createElement('button');
  resetBtn.className = 'btn-small';
  resetBtn.style.cssText = 'width:100%;margin-top:6px;color:var(--danger);';
  resetBtn.textContent = 'RESET LAYOUT';
  resetBtn.title = 'Clear all saved positions for the current mode and re-run the template layout engine';
  resetBtn.onclick = () => {
    if (!confirm(`Reset ${editorMode === 'advanced' ? 'Advanced' : 'Template'} layout for "${currentSize}"?\n\nThis will discard your custom positions and re-apply the template layout.`)) return;
    // Clear saved positions for current size in the active mode's store
    const store = editorMode === 'advanced' ? advancedPositions : templatePositions;
    delete store[currentSize];
    // Re-hydrate from template
    const template = TEMPLATES[currentState.template];
    const scheme = getScheme(currentState.scheme.name || 'dark');
    hydrateTemplate(template, scheme, currentSize);
    logAction(`Reset ${editorMode} layout: ${currentSize}`);
    pushUndoState(fabricCanvas, currentState, currentSize);
    buildSidebar();
  };

  const wrapper = document.createElement('div');
  wrapper.appendChild(container);
  wrapper.appendChild(hint);
  wrapper.appendChild(resetBtn);
  return wrapper;
}

/**
 * Build the Template Mode text fields panel.
 *
 * In Template Mode, this is the primary way to edit text. Each template
 * object gets a labeled sidebar input. Typing updates the canvas in
 * real-time; on blur, the layout engine re-runs to position everything
 * correctly for the new content lengths.
 *
 * Mirrors the original kit.fafo.date sidebar-driven editing workflow:
 * - Text fields are grouped by role (Event, Lineup, Footer)
 * - Each field has a show/hide toggle
 * - Canvas text objects are locked (no drag/resize/direct edit)
 * - All output sizes stay consistent because the layout engine handles positioning
 */
function buildTemplateTextFields() {
  const container = document.createElement('div');
  const template = TEMPLATES[currentState.template];
  if (!template) return container;

  const textObjs = template.objects.filter(o => o.type === 'text');
  if (textObjs.length === 0) return container;

  // Capture current text state from canvas
  const textEdits = captureTextEdits();

  // Role display names and grouping
  const ROLE_LABELS = {
    'header-left': 'Header Left',
    'header-center': 'Header Center',
    'header-right': 'Header Right',
    'title': 'Event Name',
    'date': 'Event Date',
    'venue': 'Venue',
    'detail': 'Address / Details',
    'lineup-heading': 'Section Heading',
    'lineup': 'Artist / Performer',
    'time': 'Time',
    'admission': 'Admission',
    'info': 'Info',
    'footer-left': 'Footer Left',
    'footer-center': 'Footer Center',
    'footer-right': 'Footer Right',
    'footer': 'Footer',
    'body': 'Body Text',
    'accent': 'Accent Text',
  };

  const ROLE_GROUPS = {
    'Header': ['header-left', 'header-center', 'header-right'],
    'Event': ['title', 'date'],
    'Lineup': ['lineup-heading', 'lineup'],
    'Venue & Info': ['venue', 'detail', 'time', 'admission', 'info'],
    'Other': ['body', 'accent'],
    'Footer': ['footer-left', 'footer-center', 'footer-right', 'footer'],
  };

  // Build a map of role → objects for grouping
  const groupedObjs = {};
  for (const [groupName, roles] of Object.entries(ROLE_GROUPS)) {
    const items = textObjs.filter(o => roles.includes(o.role));
    if (items.length > 0) {
      groupedObjs[groupName] = items;
    }
  }

  // Any objects with roles not in ROLE_GROUPS go in "Other"
  const coveredIds = new Set(Object.values(groupedObjs).flat().map(o => o.id));
  const uncovered = textObjs.filter(o => !coveredIds.has(o.id));
  if (uncovered.length > 0) {
    groupedObjs['Other'] = uncovered;
  }

  // Debounce timer for re-layout
  let relayoutTimer = null;

  // Groups that support smart justification
  const JUSTIFIABLE_GROUPS = new Set(['Lineup', 'Venue & Info']);

  for (const [groupName, objs] of Object.entries(groupedObjs)) {
    const groupHeader = document.createElement('div');
    groupHeader.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin:10px 0 6px;';

    const groupLabel = document.createElement('div');
    groupLabel.style.cssText = 'font-size:10px;color:var(--accent);font-family:"DM Mono",monospace;letter-spacing:1px;';
    groupLabel.textContent = groupName.toUpperCase();
    groupHeader.appendChild(groupLabel);

    // Smart justification toggle for applicable groups
    if (JUSTIFIABLE_GROUPS.has(groupName)) {
      const alignRow = document.createElement('div');
      alignRow.style.cssText = 'display:flex;gap:1px;';

      const currentAlign = groupAlignOverrides[groupName] || 'left';
      for (const alignOpt of ['left', 'center', 'right']) {
        const alignBtn = document.createElement('button');
        const icon = alignOpt === 'left' ? '\u2261' : alignOpt === 'center' ? '\u2550' : '\u2261';
        alignBtn.textContent = alignOpt === 'left' ? 'L' : alignOpt === 'center' ? 'C' : 'R';
        alignBtn.title = `Align ${groupName.toLowerCase()} ${alignOpt}`;
        const isActive = currentAlign === alignOpt;
        alignBtn.style.cssText = `
          background:none;border:1px solid ${isActive ? 'var(--accent)' : 'var(--border)'};
          color:${isActive ? 'var(--accent)' : 'var(--text-dim)'};
          font-family:"DM Mono",monospace;font-size:8px;
          cursor:pointer;padding:1px 4px;min-width:16px;
        `;
        alignBtn.onclick = () => {
          groupAlignOverrides[groupName] = alignOpt;
          triggerRelayout();
          buildSidebar();
        };
        alignRow.appendChild(alignBtn);
      }
      groupHeader.appendChild(alignRow);
    }

    container.appendChild(groupHeader);

    for (const obj of objs) {
      const isHidden = hiddenObjects.has(obj.id);
      const currentText = textEdits[obj.id] || obj.content;
      const overrides = textStyleOverrides[obj.id] || {};

      const fieldWrap = document.createElement('div');
      fieldWrap.style.cssText = `margin-bottom:8px;padding:6px;border:1px solid var(--border);opacity:${isHidden ? '0.4' : '1'};`;

      // Label row with role name and hide toggle
      const labelRow = document.createElement('div');
      labelRow.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin-bottom:3px;';

      const label = document.createElement('label');
      label.className = 'field-label';
      label.style.cssText = 'margin:0;font-size:9px;';

      const roleCount = objs.filter(o => o.role === obj.role);
      if (roleCount.length > 1) {
        const idx = roleCount.indexOf(obj) + 1;
        label.textContent = (ROLE_LABELS[obj.role] || obj.role) + ' ' + idx;
      } else {
        label.textContent = ROLE_LABELS[obj.role] || obj.role;
      }
      labelRow.appendChild(label);

      // Hide/show toggle
      const hideBtn = document.createElement('button');
      hideBtn.style.cssText = `
        background:none;border:none;color:${isHidden ? 'var(--danger)' : 'var(--text-dim)'};
        font-family:"DM Mono",monospace;font-size:8px;letter-spacing:0.5px;
        cursor:pointer;padding:1px 4px;
      `;
      hideBtn.textContent = isHidden ? 'HIDDEN' : 'VISIBLE';
      hideBtn.title = isHidden ? 'Click to show this field' : 'Click to hide this field';
      hideBtn.onclick = () => {
        if (hiddenObjects.has(obj.id)) {
          hiddenObjects.delete(obj.id);
        } else {
          hiddenObjects.add(obj.id);
        }
        triggerRelayout();
        buildSidebar();
      };
      labelRow.appendChild(hideBtn);
      fieldWrap.appendChild(labelRow);

      // Text input row (with optional date picker for date role)
      const inputRow = document.createElement('div');
      inputRow.style.cssText = 'display:flex;align-items:center;gap:3px;';

      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'field-input';
      input.style.flex = '1';
      input.value = currentText;
      input.placeholder = obj.content;
      input.dataset.objId = obj.id;

      // Date role: add a calendar picker alongside the text input
      if (obj.role === 'date') {
        const datePicker = document.createElement('input');
        datePicker.type = 'date';
        datePicker.style.cssText = `
          width:28px;height:24px;padding:0;border:1px solid var(--border);
          background:var(--surface);color:transparent;cursor:pointer;
          position:relative;
        `;
        datePicker.title = 'Pick a date from calendar';
        // Try to parse current text value into a date for the picker
        try {
          const parsed = new Date(currentText);
          if (!isNaN(parsed.getTime())) {
            datePicker.value = parsed.toISOString().split('T')[0];
          }
        } catch (_) {}

        datePicker.onchange = () => {
          if (!datePicker.value) return;
          const d = new Date(datePicker.value + 'T12:00:00');
          // Use the active date format from DATE_FORMATS
          const fmtId = currentState.dateFormat || 'dayMonth';
          const fmt = DATE_FORMATS.find(f => f.id === fmtId) || DATE_FORMATS[5];
          input.value = fmt.fn(d);
          input.dispatchEvent(new Event('input'));
          input.dispatchEvent(new Event('change'));
          // Store the ISO date for format changes and scheduler
          currentState.eventDateISO = datePicker.value;
        };
        inputRow.appendChild(input);
        inputRow.appendChild(datePicker);
      } else {
        inputRow.appendChild(input);
      }

      input.oninput = () => {
        const fabricObj = fabricCanvas.getObjects().find(o => o.data?.id === obj.id);
        if (fabricObj) {
          fabricObj.set('text', input.value || obj.content);
          fabricObj.initDimensions();
          fabricObj.setCoords();
          fabricCanvas.renderAll();
        }
        clearTimeout(relayoutTimer);
        relayoutTimer = setTimeout(() => triggerRelayout(), 800);
      };
      input.onchange = () => { clearTimeout(relayoutTimer); triggerRelayout(); };
      fieldWrap.appendChild(inputRow);

      // ── Style controls row ──
      const styleRow = document.createElement('div');
      styleRow.style.cssText = 'display:flex;align-items:center;gap:3px;margin-top:3px;flex-wrap:wrap;';

      // Font selector (compact)
      const fontSelect = document.createElement('select');
      fontSelect.className = 'field-select';
      fontSelect.style.cssText = 'flex:1;min-width:80px;font-size:10px;padding:3px 4px;';
      fontSelect.title = 'Font family';
      const fonts = ['DM Mono', 'Work Sans', 'Bebas Neue', 'Inter', 'Oswald', 'Poppins',
        'Montserrat', 'Space Grotesk', 'Archivo Black', 'Barlow Condensed', 'Anton',
        'Playfair Display', 'Outfit', 'Raleway', 'Rubik', 'Righteous', 'Orbitron'];
      const currentFont = overrides.fontFamily || obj.fontFamily || 'DM Mono';
      for (const f of fonts) {
        const opt = document.createElement('option');
        opt.value = f;
        opt.textContent = f;
        opt.style.fontFamily = f;
        if (f === currentFont) opt.selected = true;
        fontSelect.appendChild(opt);
      }
      fontSelect.onchange = () => {
        if (!textStyleOverrides[obj.id]) textStyleOverrides[obj.id] = {};
        textStyleOverrides[obj.id].fontFamily = fontSelect.value;
        applyStyleToCanvas(obj.id, { fontFamily: fontSelect.value });
      };
      styleRow.appendChild(fontSelect);

      // Bold toggle
      const boldBtn = document.createElement('button');
      const isBold = (overrides.fontWeight || obj.fontWeight) === 'bold';
      boldBtn.style.cssText = `
        background:none;border:1px solid ${isBold ? 'var(--accent)' : 'var(--border)'};
        color:${isBold ? 'var(--accent)' : 'var(--text-dim)'};
        font-family:"DM Mono",monospace;font-size:11px;font-weight:bold;
        cursor:pointer;padding:2px 6px;min-width:22px;
      `;
      boldBtn.textContent = 'B';
      boldBtn.title = 'Bold';
      boldBtn.onclick = () => {
        if (!textStyleOverrides[obj.id]) textStyleOverrides[obj.id] = {};
        const newWeight = textStyleOverrides[obj.id].fontWeight === 'bold' ? 'normal' : 'bold';
        textStyleOverrides[obj.id].fontWeight = newWeight;
        boldBtn.style.borderColor = newWeight === 'bold' ? 'var(--accent)' : 'var(--border)';
        boldBtn.style.color = newWeight === 'bold' ? 'var(--accent)' : 'var(--text-dim)';
        applyStyleToCanvas(obj.id, { fontWeight: newWeight });
      };
      styleRow.appendChild(boldBtn);

      // Italic toggle
      const italicBtn = document.createElement('button');
      const isItalic = overrides.fontStyle === 'italic';
      italicBtn.style.cssText = `
        background:none;border:1px solid ${isItalic ? 'var(--accent)' : 'var(--border)'};
        color:${isItalic ? 'var(--accent)' : 'var(--text-dim)'};
        font-family:"DM Mono",monospace;font-size:11px;font-style:italic;
        cursor:pointer;padding:2px 6px;min-width:22px;
      `;
      italicBtn.textContent = 'I';
      italicBtn.title = 'Italic';
      italicBtn.onclick = () => {
        if (!textStyleOverrides[obj.id]) textStyleOverrides[obj.id] = {};
        const newStyle = textStyleOverrides[obj.id].fontStyle === 'italic' ? 'normal' : 'italic';
        textStyleOverrides[obj.id].fontStyle = newStyle;
        italicBtn.style.borderColor = newStyle === 'italic' ? 'var(--accent)' : 'var(--border)';
        italicBtn.style.color = newStyle === 'italic' ? 'var(--accent)' : 'var(--text-dim)';
        applyStyleToCanvas(obj.id, { fontStyle: newStyle });
      };
      styleRow.appendChild(italicBtn);

      // Text color picker
      const colorInput = document.createElement('input');
      colorInput.type = 'color';
      colorInput.style.cssText = 'width:22px;height:22px;border:1px solid var(--border);background:none;padding:0;cursor:pointer;';
      colorInput.title = 'Text color';
      colorInput.value = overrides.fill || resolveColorStatic(obj.fill, currentState.scheme) || '#ffffff';
      colorInput.oninput = () => {
        if (!textStyleOverrides[obj.id]) textStyleOverrides[obj.id] = {};
        textStyleOverrides[obj.id].fill = colorInput.value;
        applyStyleToCanvas(obj.id, { fill: colorInput.value });
      };
      styleRow.appendChild(colorInput);

      // Text background color (the "text BG" FX)
      const bgBtn = document.createElement('button');
      const hasBg = !!overrides.textBg;
      bgBtn.style.cssText = `
        background:${hasBg ? overrides.textBg : 'none'};
        border:1px solid ${hasBg ? 'var(--accent)' : 'var(--border)'};
        color:${hasBg ? '#000' : 'var(--text-dim)'};
        font-family:"DM Mono",monospace;font-size:8px;letter-spacing:0.5px;
        cursor:pointer;padding:2px 5px;
      `;
      bgBtn.textContent = 'BG';
      bgBtn.title = 'Text background highlight — click to toggle, right-click for color';
      bgBtn.onclick = () => {
        if (!textStyleOverrides[obj.id]) textStyleOverrides[obj.id] = {};
        if (textStyleOverrides[obj.id].textBg) {
          textStyleOverrides[obj.id].textBg = '';
          bgBtn.style.background = 'none';
          bgBtn.style.borderColor = 'var(--border)';
          bgBtn.style.color = 'var(--text-dim)';
        } else {
          // Default to scheme accent color
          const bgColor = currentState.scheme?.accent || '#e040a0';
          textStyleOverrides[obj.id].textBg = bgColor;
          bgBtn.style.background = bgColor;
          bgBtn.style.borderColor = 'var(--accent)';
          bgBtn.style.color = '#000';
        }
        applyStyleToCanvas(obj.id, { backgroundColor: textStyleOverrides[obj.id].textBg || '' });
      };
      bgBtn.oncontextmenu = (e) => {
        e.preventDefault();
        // Use a hidden color input for BG color selection
        const picker = document.createElement('input');
        picker.type = 'color';
        picker.value = overrides.textBg || currentState.scheme?.accent || '#e040a0';
        picker.style.cssText = 'position:absolute;opacity:0;pointer-events:none;';
        document.body.appendChild(picker);
        picker.oninput = () => {
          if (!textStyleOverrides[obj.id]) textStyleOverrides[obj.id] = {};
          textStyleOverrides[obj.id].textBg = picker.value;
          bgBtn.style.background = picker.value;
          bgBtn.style.borderColor = 'var(--accent)';
          bgBtn.style.color = '#000';
          applyStyleToCanvas(obj.id, { backgroundColor: picker.value });
        };
        picker.onchange = () => { picker.remove(); };
        picker.click();
      };
      styleRow.appendChild(bgBtn);

      fieldWrap.appendChild(styleRow);

      // ── Compact FX row (Template Mode) ──
      const fxRow = document.createElement('div');
      fxRow.style.cssText = 'display:flex;align-items:center;gap:3px;margin-top:3px;';

      // Helper to create compact FX toggle buttons
      function makeFxBtn(label, title, isActive, onClick) {
        const btn = document.createElement('button');
        btn.style.cssText = `
          background:none;border:1px solid ${isActive ? 'var(--accent)' : 'var(--border)'};
          color:${isActive ? 'var(--accent)' : 'var(--text-dim)'};
          font-family:"DM Mono",monospace;font-size:7px;letter-spacing:0.5px;
          cursor:pointer;padding:2px 5px;
        `;
        btn.textContent = label;
        btn.title = title;
        btn.onclick = onClick;
        return btn;
      }

      // H-Slice toggle
      const fabricObj = fabricCanvas.getObjects().find(o => o.data?.id === obj.id);
      const hSliceActive = fabricObj?.data?.sliceEffect?.enabled || false;
      const hSliceBtn = makeFxBtn('H-SLICE', 'Horizontal text slicer (glitch effect)', hSliceActive, () => {
        const fObj = fabricCanvas.getObjects().find(o => o.data?.id === obj.id);
        if (!fObj) return;
        if (!fObj.data.sliceEffect) fObj.data.sliceEffect = { enabled: false, count: 5, gap: 3 };
        fObj.data.sliceEffect.enabled = !fObj.data.sliceEffect.enabled;
        applySliceEffect(fObj);
        hSliceBtn.style.borderColor = fObj.data.sliceEffect.enabled ? 'var(--accent)' : 'var(--border)';
        hSliceBtn.style.color = fObj.data.sliceEffect.enabled ? 'var(--accent)' : 'var(--text-dim)';
      });
      fxRow.appendChild(hSliceBtn);

      // V-Slice toggle
      const vSliceActive = fabricObj?.data?.vSliceEffect?.enabled || false;
      const vSliceBtn = makeFxBtn('V-SLICE', 'Vertical text slicer', vSliceActive, () => {
        const fObj = fabricCanvas.getObjects().find(o => o.data?.id === obj.id);
        if (!fObj) return;
        if (!fObj.data.vSliceEffect) fObj.data.vSliceEffect = { enabled: false, count: 5, gap: 3, spread: 0, seed: 42 };
        fObj.data.vSliceEffect.enabled = !fObj.data.vSliceEffect.enabled;
        applySliceEffect(fObj);
        vSliceBtn.style.borderColor = fObj.data.vSliceEffect.enabled ? 'var(--accent)' : 'var(--border)';
        vSliceBtn.style.color = fObj.data.vSliceEffect.enabled ? 'var(--accent)' : 'var(--text-dim)';
      });
      fxRow.appendChild(vSliceBtn);

      // Shatter toggle (only meaningful when V-Slice is on)
      const spreadActive = (fabricObj?.data?.vSliceEffect?.spread || 0) > 0;
      const shatterBtn = makeFxBtn('SHATTER', 'Spread vertical slices for a shattered look', spreadActive, () => {
        const fObj = fabricCanvas.getObjects().find(o => o.data?.id === obj.id);
        if (!fObj) return;
        if (!fObj.data.vSliceEffect) fObj.data.vSliceEffect = { enabled: true, count: 5, gap: 3, spread: 0, seed: 42 };
        if (fObj.data.vSliceEffect.spread > 0) {
          fObj.data.vSliceEffect.spread = 0;
        } else {
          fObj.data.vSliceEffect.enabled = true;
          fObj.data.vSliceEffect.spread = 8;
          fObj.data.vSliceEffect.seed = Math.floor(Math.random() * 100000);
        }
        applySliceEffect(fObj);
        const isOn = fObj.data.vSliceEffect.spread > 0;
        shatterBtn.style.borderColor = isOn ? 'var(--accent)' : 'var(--border)';
        shatterBtn.style.color = isOn ? 'var(--accent)' : 'var(--text-dim)';
        // Also update V-Slice button state
        vSliceBtn.style.borderColor = fObj.data.vSliceEffect.enabled ? 'var(--accent)' : 'var(--border)';
        vSliceBtn.style.color = fObj.data.vSliceEffect.enabled ? 'var(--accent)' : 'var(--text-dim)';
      });
      fxRow.appendChild(shatterBtn);

      fieldWrap.appendChild(fxRow);
      container.appendChild(fieldWrap);
    }
  }

  function triggerRelayout() {
    const template = TEMPLATES[currentState.template];
    const scheme = getScheme(currentState.scheme.name || 'dark');
    hydrateTemplate(template, scheme, currentSize);
    pushUndoState(fabricCanvas, currentState, currentSize);
  }

  // Hint text at the bottom
  const hint = document.createElement('div');
  hint.className = 'hint';
  hint.style.marginTop = '8px';
  hint.textContent = 'Edit text here — the layout engine positions everything automatically across all output sizes.';
  container.appendChild(hint);

  return container;
}

function buildSizeSelector() {
  const container = document.createElement('div');

  const groups = {
    'Print': ['letter', 'tabloid'],
    'Social': ['social-vertical', 'story', 'event-banner', 'pinterest', 'yt-thumbnail', 'threads', 'bandcamp'],
  };

  for (const [groupName, keys] of Object.entries(groups)) {
    const groupLabel = document.createElement('div');
    groupLabel.className = 'field-label';
    groupLabel.textContent = groupName;
    container.appendChild(groupLabel);

    const grid = document.createElement('div');
    grid.className = 'size-grid';
    for (const key of keys) {
      const size = TEMPLATE_SIZES[key];
      if (!size) continue;

      const card = document.createElement('button');
      card.className = 'size-card has-tooltip' + (key === currentSize ? ' active' : '');

      // Custom styled tooltip
      const dpi = size.category === 'print' ? 300 : 72;
      const tooltip = document.createElement('div');
      tooltip.className = 'custom-tooltip';
      tooltip.textContent = `${size.label}\n${size.width} \u00d7 ${size.height}px\n${dpi} DPI`;
      card.appendChild(tooltip);

      // Visual aspect-ratio preview only — no text label
      const preview = document.createElement('div');
      preview.className = 'size-preview';
      const maxDim = 36;
      const aspect = size.width / size.height;
      const pw = aspect >= 1 ? maxDim : Math.round(maxDim * aspect);
      const ph = aspect >= 1 ? Math.round(maxDim / aspect) : maxDim;
      preview.style.width = pw + 'px';
      preview.style.height = ph + 'px';
      card.appendChild(preview);

      card.onclick = () => {
        const prevSize = currentSize;
        // Save current positions before switching — both modes preserve layout
        const store = editorMode === 'advanced' ? advancedPositions : templatePositions;
        savePositions(prevSize, store);

        currentSize = key;
        currentState.size = currentSize;
        userModifiedSizes.add(key);
        const template = TEMPLATES[currentState.template];
        const scheme = getScheme(currentState.scheme.name || 'dark');
        hydrateTemplate(template, scheme, currentSize);
        // Restore saved positions (or proportionally remap from previous size)
        restorePositions(currentSize, prevSize, store);
        logAction(`Size: ${size.label}`);
        pushUndoState(fabricCanvas, currentState, currentSize);
        buildSidebar();
      };
      grid.appendChild(card);
    }
    container.appendChild(grid);
  }
  return container;
}

function buildSchemeSelector() {
  const container = document.createElement('div');
  const select = document.createElement('select');
  select.className = 'field-select scheme-select';
  select.title = 'Choose a color scheme — defines primary, accent, and background colors';

  for (const scheme of listSchemes()) {
    const opt = document.createElement('option');
    opt.value = scheme.key;
    opt.textContent = scheme.name;
    opt.selected = currentState.scheme.name === scheme.key;
    select.appendChild(opt);
  }

  // Gradient preview bar showing current scheme colors
  const preview = document.createElement('div');
  preview.className = 'scheme-preview';
  preview.title = 'Preview of the selected color scheme gradient';
  function updatePreview(schemeKey) {
    const s = getScheme(schemeKey);
    preview.style.background = `linear-gradient(90deg, ${s.bg} 0%, ${s.primary} 30%, ${s.accent} 60%, ${s.secondary} 100%)`;
  }
  updatePreview(currentState.scheme.name || 'dark');

  select.onchange = () => {
    const scheme = getScheme(select.value);
    // Spread scheme first, then override name with the key (not display name)
    currentState.scheme = { ...scheme, name: select.value };
    applySchemeToObjects(scheme);
    updateBackground(scheme, currentSize);
    updatePreview(select.value);
    logAction(`Scheme: ${select.value}`);
    pushUndoState(fabricCanvas, currentState, currentSize);
  };

  container.appendChild(select);
  container.appendChild(preview);
  return container;
}

function buildLineArtControls() {
  const container = document.createElement('div');

  // Style selector
  const styleSelect = document.createElement('select');
  styleSelect.className = 'field-select';
  styleSelect.title = 'Select the generative line art pattern style';
  for (const style of LINE_ART_STYLES) {
    const opt = document.createElement('option');
    opt.value = style.id;
    opt.textContent = style.label;
    opt.selected = currentState.overlays.lineArt.type === style.id;
    styleSelect.appendChild(opt);
  }

  // Density slider
  const densityLabel = document.createElement('label');
  densityLabel.textContent = 'Density';
  densityLabel.className = 'field-label';
  const densitySlider = document.createElement('input');
  densitySlider.type = 'range';
  densitySlider.min = 1;
  densitySlider.max = 100;
  densitySlider.value = normalizeOverlay(currentState.overlays.lineArt.density, 50);
  densitySlider.className = 'field-range';
  densitySlider.title = 'Controls how many lines are drawn (higher = denser pattern)';

  // Opacity slider
  const opacityLabel = document.createElement('label');
  opacityLabel.textContent = 'Opacity';
  opacityLabel.className = 'field-label';
  const opacitySlider = document.createElement('input');
  opacitySlider.type = 'range';
  opacitySlider.min = 1;
  opacitySlider.max = 100;
  opacitySlider.value = normalizeOverlay(currentState.overlays.lineArt.opacity, 30);
  opacitySlider.className = 'field-range';
  opacitySlider.title = 'Controls how visible the line art overlay is (0 = hidden, 100 = fully visible)';

  // Seed input
  const seedLabel = document.createElement('label');
  seedLabel.textContent = 'Seed';
  seedLabel.className = 'field-label';
  const seedInput = document.createElement('input');
  seedInput.type = 'number';
  seedInput.value = currentState.overlays.lineArt.seed || 42;
  seedInput.className = 'field-input';
  seedInput.title = 'Random seed — same seed always produces the same pattern';

  const randomBtn = document.createElement('button');
  randomBtn.textContent = 'Randomize';
  randomBtn.className = 'btn-small';
  randomBtn.title = 'Generate a new random seed for a different pattern variation';
  randomBtn.onclick = () => {
    seedInput.value = Math.floor(Math.random() * 100000);
    applyLineArt();
  };

  function applyLineArt() {
    currentState.overlays.lineArt = {
      type: styleSelect.value,
      density: parseInt(densitySlider.value),
      opacity: parseInt(opacitySlider.value),
      seed: parseInt(seedInput.value),
    };
    const scheme = getScheme(currentState.scheme.name || 'dark');
    updateBackground(scheme, currentSize);
  }

  function applyAndSnapshot() {
    applyLineArt();
    logAction(`Line Art: ${styleSelect.value}`);
    pushUndoState(fabricCanvas, currentState, currentSize);
  }

  styleSelect.onchange = applyAndSnapshot;
  densitySlider.oninput = applyLineArt;
  densitySlider.onchange = () => { logAction('Line Art density'); pushUndoState(fabricCanvas, currentState, currentSize); };
  opacitySlider.oninput = applyLineArt;
  opacitySlider.onchange = () => { logAction('Line Art opacity'); pushUndoState(fabricCanvas, currentState, currentSize); };
  seedInput.onchange = applyAndSnapshot;

  container.appendChild(styleSelect);
  container.appendChild(densityLabel);
  container.appendChild(densitySlider);
  container.appendChild(opacityLabel);
  container.appendChild(opacitySlider);
  container.appendChild(seedLabel);
  const seedRow = document.createElement('div');
  seedRow.className = 'seed-row';
  seedRow.appendChild(seedInput);
  seedRow.appendChild(randomBtn);
  container.appendChild(seedRow);

  return container;
}

function buildLineArt2Controls() {
  const container = document.createElement('div');

  // Ensure lineArt2 state exists
  if (!currentState.overlays.lineArt2) {
    currentState.overlays.lineArt2 = { type: 'none', density: 30, opacity: 0.2, seed: 137 };
  }
  const la2 = currentState.overlays.lineArt2;

  // Hint about secondary color
  const hint = document.createElement('div');
  hint.className = 'hint';
  hint.textContent = 'Mixes a second pattern using the scheme\u2019s secondary line art color.';
  hint.style.marginBottom = '6px';
  container.appendChild(hint);

  // Style selector
  const styleSelect = document.createElement('select');
  styleSelect.className = 'field-select';
  styleSelect.title = 'Select the secondary generative line art pattern style';
  for (const style of LINE_ART_STYLES) {
    const opt = document.createElement('option');
    opt.value = style.id;
    opt.textContent = style.label;
    opt.selected = la2.type === style.id;
    styleSelect.appendChild(opt);
  }

  // Density slider
  const densityLabel = document.createElement('label');
  densityLabel.textContent = 'Density';
  densityLabel.className = 'field-label';
  const densitySlider = document.createElement('input');
  densitySlider.type = 'range';
  densitySlider.min = 1;
  densitySlider.max = 100;
  densitySlider.value = normalizeOverlay(la2.density, 30);
  densitySlider.className = 'field-range';
  densitySlider.title = 'Controls how many secondary lines are drawn (higher = denser pattern)';

  // Opacity slider
  const opacityLabel = document.createElement('label');
  opacityLabel.textContent = 'Opacity';
  opacityLabel.className = 'field-label';
  const opacitySlider = document.createElement('input');
  opacitySlider.type = 'range';
  opacitySlider.min = 1;
  opacitySlider.max = 100;
  opacitySlider.value = normalizeOverlay(la2.opacity, 20);
  opacitySlider.className = 'field-range';
  opacitySlider.title = 'Controls how visible the secondary line art overlay is (0 = hidden, 100 = fully visible)';

  // Seed input
  const seedLabel = document.createElement('label');
  seedLabel.textContent = 'Seed';
  seedLabel.className = 'field-label';
  const seedInput = document.createElement('input');
  seedInput.type = 'number';
  seedInput.value = la2.seed || 137;
  seedInput.className = 'field-input';
  seedInput.title = 'Secondary random seed — same seed always produces the same pattern';

  const randomBtn = document.createElement('button');
  randomBtn.textContent = 'Randomize';
  randomBtn.className = 'btn-small';
  randomBtn.title = 'Generate a new random seed for a different secondary pattern variation';
  randomBtn.onclick = () => {
    seedInput.value = Math.floor(Math.random() * 100000);
    applyLineArt2();
  };

  function applyLineArt2() {
    currentState.overlays.lineArt2 = {
      type: styleSelect.value,
      density: parseInt(densitySlider.value),
      opacity: parseInt(opacitySlider.value),
      seed: parseInt(seedInput.value),
    };
    const scheme = getScheme(currentState.scheme.name || 'dark');
    updateBackground(scheme, currentSize);
  }

  function applyAndSnapshot() {
    applyLineArt2();
    pushUndoState(fabricCanvas, currentState, currentSize);
  }

  styleSelect.onchange = applyAndSnapshot;
  densitySlider.oninput = applyLineArt2;
  densitySlider.onchange = () => pushUndoState(fabricCanvas, currentState, currentSize);
  opacitySlider.oninput = applyLineArt2;
  opacitySlider.onchange = () => pushUndoState(fabricCanvas, currentState, currentSize);
  seedInput.onchange = applyAndSnapshot;

  container.appendChild(styleSelect);
  container.appendChild(densityLabel);
  container.appendChild(densitySlider);
  container.appendChild(opacityLabel);
  container.appendChild(opacitySlider);
  container.appendChild(seedLabel);
  const seedRow = document.createElement('div');
  seedRow.className = 'seed-row';
  seedRow.appendChild(seedInput);
  seedRow.appendChild(randomBtn);
  container.appendChild(seedRow);

  return container;
}

function buildGrainControls() {
  const container = document.createElement('div');

  const enableCheck = document.createElement('input');
  enableCheck.type = 'checkbox';
  enableCheck.checked = currentState.overlays.grain.enabled;
  enableCheck.id = 'grain-enable';
  const enableLabel = document.createElement('label');
  enableLabel.htmlFor = 'grain-enable';
  enableLabel.textContent = ' Enable grain';
  enableLabel.className = 'checkbox-label';
  enableLabel.title = 'Add a film grain texture overlay for vintage/analog feel';

  const densityLabel = document.createElement('label');
  densityLabel.textContent = 'Density';
  densityLabel.className = 'field-label';
  const densitySlider = document.createElement('input');
  densitySlider.type = 'range';
  densitySlider.min = 1;
  densitySlider.max = 100;
  densitySlider.value = normalizeOverlay(currentState.overlays.grain.density, 40);
  densitySlider.className = 'field-range';
  densitySlider.title = 'Controls grain particle density (higher = grittier)';

  const opacityLabel = document.createElement('label');
  opacityLabel.textContent = 'Opacity';
  opacityLabel.className = 'field-label';
  const opacitySlider = document.createElement('input');
  opacitySlider.type = 'range';
  opacitySlider.min = 1;
  opacitySlider.max = 100;
  opacitySlider.value = normalizeOverlay(currentState.overlays.grain.opacity, 15);
  opacitySlider.className = 'field-range';
  opacitySlider.title = 'Controls grain visibility (0 = no grain, 100 = heavy grain)';

  function applyGrain() {
    currentState.overlays.grain = {
      enabled: enableCheck.checked,
      density: parseInt(densitySlider.value),
      opacity: parseInt(opacitySlider.value),
    };
    const scheme = getScheme(currentState.scheme.name || 'dark');
    updateBackground(scheme, currentSize);
  }

  enableCheck.onchange = () => { applyGrain(); logAction(enableCheck.checked ? 'Grain enabled' : 'Grain disabled'); pushUndoState(fabricCanvas, currentState, currentSize); };
  densitySlider.oninput = applyGrain;
  densitySlider.onchange = () => pushUndoState(fabricCanvas, currentState, currentSize);
  opacitySlider.oninput = applyGrain;
  opacitySlider.onchange = () => pushUndoState(fabricCanvas, currentState, currentSize);

  const checkRow = document.createElement('div');
  checkRow.className = 'check-row';
  checkRow.appendChild(enableCheck);
  checkRow.appendChild(enableLabel);
  container.appendChild(checkRow);
  container.appendChild(densityLabel);
  container.appendChild(densitySlider);
  container.appendChild(opacityLabel);
  container.appendChild(opacitySlider);

  return container;
}

function buildMangleControls() {
  const container = document.createElement('div');

  // Initialize mangle params if not set
  if (!currentState._mangleParams) {
    currentState._mangleParams = { spread: 50, angle: 0, colorShift: 0, styleShift: 0 };
  }

  const params = [
    { key: 'spread', label: 'Spread', tip: 'How far objects can move from their original positions' },
    { key: 'angle', label: 'Angle', tip: 'Maximum rotation angle applied to objects (in degrees)' },
    { key: 'colorShift', label: 'Color', tip: 'How much colors can deviate from the scheme palette' },
    { key: 'styleShift', label: 'Style', tip: 'How much font weights and styles can vary' },
  ];

  for (const p of params) {
    const label = document.createElement('label');
    label.className = 'field-label';
    label.textContent = p.label;
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = 0;
    slider.max = 100;
    slider.value = currentState._mangleParams[p.key] ?? 50;
    slider.className = 'field-range';
    slider.title = p.tip;
    slider.oninput = () => { currentState._mangleParams[p.key] = parseInt(slider.value); };
    container.appendChild(label);
    container.appendChild(slider);
  }

  const btnRow = document.createElement('div');
  btnRow.className = 'seed-row';
  btnRow.style.marginTop = '6px';

  const mangleBtn = document.createElement('button');
  mangleBtn.className = 'btn-small';
  mangleBtn.style.flex = '1';
  mangleBtn.textContent = 'Mangle';
  mangleBtn.title = 'Randomize object positions, angles, and colors for creative variation';
  mangleBtn.onclick = () => {
    mangleLayout();
    // Capture user's font/color/style choices and positions before hydration resets them
    const savedStyles = captureTextStyles();
    const store = editorMode === 'advanced' ? advancedPositions : templatePositions;
    savePositions(currentSize, store);
    const template = TEMPLATES[currentState.template];
    const scheme = getScheme(currentState.scheme.name || 'dark');
    hydrateTemplate(template, scheme, currentSize);
    // Restore user's choices — mangle will only override color/style if probability hits
    restoreTextStyles(savedStyles);
    restorePositions(currentSize, currentSize, store);
    const dw = Math.round(TEMPLATE_SIZES[currentSize].width * displayScale);
    const dh = Math.round(TEMPLATE_SIZES[currentSize].height * displayScale);
    applyMangle(fabricCanvas, dw, dh, currentState._mangleParams);
    logAction('Mangled layout');
    pushUndoState(fabricCanvas, currentState, currentSize);
  };

  const resetBtn = document.createElement('button');
  resetBtn.className = 'btn-small';
  resetBtn.style.flex = '1';
  resetBtn.textContent = 'Reset';
  resetBtn.title = 'Restore objects to their original template positions';
  resetBtn.onclick = () => {
    resetMangle();
    const template = TEMPLATES[currentState.template];
    const scheme = getScheme(currentState.scheme.name || 'dark');
    hydrateTemplate(template, scheme, currentSize);
    logAction('Reset mangle');
    pushUndoState(fabricCanvas, currentState, currentSize);
  };

  btnRow.appendChild(mangleBtn);
  btnRow.appendChild(resetBtn);
  container.appendChild(btnRow);

  if (getMangleSeed() > 0) {
    const seedInfo = document.createElement('div');
    seedInfo.className = 'hint';
    seedInfo.textContent = `seed: ${getMangleSeed()}`;
    seedInfo.style.marginTop = '4px';
    seedInfo.title = 'Mangle seed — same seed produces the same randomization';
    container.appendChild(seedInfo);
  }

  return container;
}

function buildQrControls() {
  const container = document.createElement('div');

  // URL input
  const urlLabel = document.createElement('label');
  urlLabel.className = 'field-label';
  urlLabel.textContent = 'URL / Text';
  const urlInput = document.createElement('input');
  urlInput.type = 'text';
  urlInput.className = 'field-input';
  urlInput.value = 'https://stencil.cloud';
  urlInput.placeholder = 'https://...';
  urlInput.title = 'The URL the QR code will link to when scanned';

  // Size slider
  const sizeLabel = document.createElement('label');
  sizeLabel.className = 'field-label';
  sizeLabel.textContent = 'Size';
  const sizeSlider = document.createElement('input');
  sizeSlider.type = 'range';
  sizeSlider.min = 40;
  sizeSlider.max = 300;
  sizeSlider.value = 120;
  sizeSlider.className = 'field-range';
  sizeSlider.title = 'Size of the QR code relative to the canvas';

  // Style selector
  const styleLabel = document.createElement('label');
  styleLabel.className = 'field-label';
  styleLabel.textContent = 'Style';
  const styleSelect = document.createElement('select');
  styleSelect.className = 'field-select';
  styleSelect.title = 'QR code visual style (square modules, rounded corners, or dot pattern)';
  for (const s of ['square', 'rounded', 'dots']) {
    const opt = document.createElement('option');
    opt.value = s;
    opt.textContent = s.charAt(0).toUpperCase() + s.slice(1);
    styleSelect.appendChild(opt);
  }

  // Color inputs
  const colorRow = document.createElement('div');
  colorRow.className = 'seed-row';
  const fgLabel = document.createElement('label');
  fgLabel.className = 'field-label';
  fgLabel.textContent = 'Colors';
  const fgInput = document.createElement('input');
  fgInput.type = 'color';
  fgInput.className = 'field-color';
  fgInput.value = '#000000';
  fgInput.title = 'Foreground';
  const bgInput = document.createElement('input');
  bgInput.type = 'color';
  bgInput.className = 'field-color';
  bgInput.value = '#ffffff';
  bgInput.title = 'Background';
  colorRow.appendChild(fgInput);
  colorRow.appendChild(bgInput);

  // Buttons
  const btnRow = document.createElement('div');
  btnRow.className = 'seed-row';
  btnRow.style.marginTop = '6px';

  const addBtn = document.createElement('button');
  addBtn.className = 'btn-small';
  addBtn.style.flex = '1';
  addBtn.textContent = 'Generate';
  addBtn.title = 'Generate a QR code on the canvas linking to this URL';
  addBtn.onclick = async () => {
    const dw = Math.round(TEMPLATE_SIZES[currentSize].width * displayScale);
    const dh = Math.round(TEMPLATE_SIZES[currentSize].height * displayScale);
    await addQrCode(fabricCanvas, {
      url: urlInput.value || 'https://stencil.cloud',
      size: parseInt(sizeSlider.value),
      fgColor: fgInput.value,
      bgColor: bgInput.value,
      style: styleSelect.value,
      x: dw - parseInt(sizeSlider.value) - 20,
      y: dh - parseInt(sizeSlider.value) - 30,
    });
    logAction(`QR code: ${styleSelect.value}`);
    pushUndoState(fabricCanvas, currentState, currentSize);
  };

  const removeBtn = document.createElement('button');
  removeBtn.className = 'btn-small';
  removeBtn.style.flex = '1';
  removeBtn.textContent = 'Remove';
  removeBtn.title = 'Remove the QR code from the canvas';
  removeBtn.onclick = () => {
    removeQrCode(fabricCanvas);
    logAction('Removed QR code');
    pushUndoState(fabricCanvas, currentState, currentSize);
  };

  btnRow.appendChild(addBtn);
  btnRow.appendChild(removeBtn);

  container.appendChild(urlLabel);
  container.appendChild(urlInput);
  container.appendChild(sizeLabel);
  container.appendChild(sizeSlider);
  container.appendChild(styleLabel);
  container.appendChild(styleSelect);
  container.appendChild(fgLabel);
  container.appendChild(colorRow);
  container.appendChild(btnRow);
  return container;
}

function buildWordmarkControls() {
  const container = document.createElement('div');

  // ── Image upload ──
  const uploadLabel = document.createElement('label');
  uploadLabel.className = 'field-label';
  uploadLabel.textContent = 'Logo Image';
  const uploadHint = document.createElement('div');
  uploadHint.className = 'hint';
  uploadHint.textContent = 'Min 200\u00d7100px. PNG, JPG, or SVG.';
  uploadHint.style.marginBottom = '4px';

  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/png,image/jpeg,image/svg+xml';
  fileInput.style.display = 'none';

  const uploadBtn = document.createElement('button');
  uploadBtn.className = 'btn-small';
  uploadBtn.style.width = '100%';
  uploadBtn.textContent = 'Upload Logo';
  uploadBtn.onclick = () => fileInput.click();

  const statusEl = document.createElement('div');
  statusEl.className = 'hint';
  statusEl.style.marginTop = '4px';

  fileInput.onchange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const sizeInfo = TEMPLATE_SIZES[currentSize];
      const dw = Math.round(sizeInfo.width * displayScale);
      const dh = Math.round(sizeInfo.height * displayScale);
      const result = await addWordmark(fabricCanvas, ev.target.result, {
        displayScale, canvasWidth: dw, canvasHeight: dh, outputWidth: sizeInfo.width,
      });
      if (result.success) {
        statusEl.textContent = 'Logo added. Drag to reposition.';
        statusEl.style.color = 'var(--accent)';
        logAction('Added logo wordmark');
        updateBackground(getScheme(currentState.scheme.name || 'dark'), currentSize);
        pushUndoState(fabricCanvas, currentState, currentSize);
      } else {
        statusEl.textContent = result.error;
        statusEl.style.color = 'var(--danger)';
      }
    };
    reader.readAsDataURL(file);
  };

  container.appendChild(uploadLabel);
  container.appendChild(uploadHint);
  container.appendChild(fileInput);
  container.appendChild(uploadBtn);
  container.appendChild(statusEl);

  // ── Text Wordmark ──
  const textLabel = document.createElement('label');
  textLabel.className = 'field-label';
  textLabel.style.marginTop = '10px';
  textLabel.textContent = 'Text Wordmark';

  const textInput = document.createElement('input');
  textInput.type = 'text';
  textInput.className = 'field-input';
  textInput.placeholder = 'Brand name';
  textInput.value = currentState._wordmarkText || '';
  textInput.title = 'Custom brand text displayed on the design (your logo/brand name)';

  // Font selector
  const fontLabel = document.createElement('label');
  fontLabel.className = 'field-label';
  fontLabel.textContent = 'Font';
  const fontSelect = document.createElement('select');
  fontSelect.className = 'field-select';
  fontSelect.title = 'Font family for the wordmark text';
  for (const f of WORDMARK_FONTS) {
    const opt = document.createElement('option');
    opt.value = f;
    opt.textContent = f;
    opt.style.fontFamily = f;
    if (f === (currentState._wordmarkFont || 'Bebas Neue')) opt.selected = true;
    fontSelect.appendChild(opt);
  }

  // Color picker
  const colorRow = document.createElement('div');
  colorRow.className = 'seed-row';
  const colorLabel = document.createElement('label');
  colorLabel.className = 'field-label';
  colorLabel.textContent = 'Color';
  colorLabel.style.flex = 'none';
  colorLabel.style.width = '40px';
  const colorInput = document.createElement('input');
  colorInput.type = 'color';
  colorInput.className = 'field-color';
  colorInput.value = currentState._wordmarkColor || '#ffffff';
  colorInput.title = 'Wordmark text color';
  colorRow.appendChild(colorLabel);
  colorRow.appendChild(colorInput);

  // Style toggles (bold / italic / uppercase)
  const styleRow = document.createElement('div');
  styleRow.className = 'seed-row';
  styleRow.style.marginTop = '4px';

  const boldBtn = document.createElement('button');
  boldBtn.className = 'btn-small' + (currentState._wordmarkBold ? ' active' : '');
  boldBtn.textContent = 'B';
  boldBtn.style.fontWeight = 'bold';
  boldBtn.style.flex = '1';
  boldBtn.title = 'Toggle bold weight for the wordmark';
  boldBtn.onclick = () => {
    currentState._wordmarkBold = !currentState._wordmarkBold;
    boldBtn.classList.toggle('active');
  };

  const italicBtn = document.createElement('button');
  italicBtn.className = 'btn-small' + (currentState._wordmarkItalic ? ' active' : '');
  italicBtn.textContent = 'I';
  italicBtn.style.fontStyle = 'italic';
  italicBtn.style.flex = '1';
  italicBtn.title = 'Toggle italic style for the wordmark';
  italicBtn.onclick = () => {
    currentState._wordmarkItalic = !currentState._wordmarkItalic;
    italicBtn.classList.toggle('active');
  };

  const upperBtn = document.createElement('button');
  upperBtn.className = 'btn-small' + ((currentState._wordmarkUppercase !== false) ? ' active' : '');
  upperBtn.textContent = 'AA';
  upperBtn.title = 'Transform wordmark text to all uppercase';
  upperBtn.style.flex = '1';
  upperBtn.onclick = () => {
    currentState._wordmarkUppercase = currentState._wordmarkUppercase === false;
    upperBtn.classList.toggle('active');
  };

  styleRow.appendChild(boldBtn);
  styleRow.appendChild(italicBtn);
  styleRow.appendChild(upperBtn);

  // Accent selector
  const accentLabel = document.createElement('label');
  accentLabel.className = 'field-label';
  accentLabel.textContent = 'Accent';
  const accentSelect = document.createElement('select');
  accentSelect.className = 'field-select';
  accentSelect.title = 'Decorative accent element added around the wordmark';
  for (const a of WORDMARK_ACCENTS) {
    const opt = document.createElement('option');
    opt.value = a.id;
    opt.textContent = a.label;
    if (a.id === (currentState._wordmarkAccent || 'none')) opt.selected = true;
    accentSelect.appendChild(opt);
  }

  // ── Live apply — rebuild wordmark on any property change ──
  function applyWordmark(isInitial = false) {
    const text = textInput.value.trim();
    if (!text) return;

    const sizeInfo = TEMPLATE_SIZES[currentSize];
    const dw = Math.round(sizeInfo.width * displayScale);
    const dh = Math.round(sizeInfo.height * displayScale);

    // Save config to state
    currentState._wordmarkText = text;
    currentState._wordmarkFont = fontSelect.value;
    currentState._wordmarkColor = colorInput.value;
    currentState._wordmarkAccent = accentSelect.value;

    addTextWordmark(fabricCanvas, text, {
      canvasWidth: dw,
      canvasHeight: dh,
      fontFamily: fontSelect.value,
      fill: colorInput.value,
      bold: !!currentState._wordmarkBold,
      italic: !!currentState._wordmarkItalic,
      uppercase: currentState._wordmarkUppercase !== false,
      accent: accentSelect.value,
      preservePosition: !isInitial, // keep position on updates, default position on first add
    });

    statusEl.textContent = 'Wordmark updated.';
    statusEl.style.color = 'var(--accent)';
    addBtn.textContent = 'Update Wordmark';
    updateBackground(getScheme(currentState.scheme.name || 'dark'), currentSize);
  }

  // Debounced snapshot push (don't flood undo stack on rapid slider/typing changes)
  let _wmSnapTimer = null;
  function pushWordmarkSnapshot() {
    clearTimeout(_wmSnapTimer);
    _wmSnapTimer = setTimeout(() => pushUndoState(fabricCanvas, currentState, currentSize), 400);
  }

  // Wire live updates — every control change immediately rebuilds the wordmark
  fontSelect.onchange = () => { applyWordmark(); logAction(`Wordmark font: ${fontSelect.value}`); pushWordmarkSnapshot(); };
  colorInput.oninput = () => { applyWordmark(); };
  colorInput.onchange = () => { logAction('Wordmark color'); pushWordmarkSnapshot(); };
  accentSelect.onchange = () => { applyWordmark(); logAction(`Wordmark accent: ${accentSelect.value}`); pushWordmarkSnapshot(); };
  textInput.oninput = () => { applyWordmark(); };
  textInput.onchange = () => { pushWordmarkSnapshot(); };

  // Style buttons: also live-apply
  boldBtn.onclick = () => {
    currentState._wordmarkBold = !currentState._wordmarkBold;
    boldBtn.classList.toggle('active');
    applyWordmark();
    pushWordmarkSnapshot();
  };
  italicBtn.onclick = () => {
    currentState._wordmarkItalic = !currentState._wordmarkItalic;
    italicBtn.classList.toggle('active');
    applyWordmark();
    pushWordmarkSnapshot();
  };
  upperBtn.onclick = () => {
    currentState._wordmarkUppercase = currentState._wordmarkUppercase === false;
    upperBtn.classList.toggle('active');
    applyWordmark();
    pushWordmarkSnapshot();
  };

  // Add / Update button (still useful for initial creation)
  const addBtn = document.createElement('button');
  addBtn.className = 'btn-small';
  addBtn.style.width = '100%';
  addBtn.style.marginTop = '6px';
  addBtn.textContent = hasWordmark(fabricCanvas) ? 'Update Wordmark' : 'Add Wordmark';

  addBtn.onclick = () => {
    const text = textInput.value.trim();
    if (!text) { statusEl.textContent = 'Enter a brand name'; statusEl.style.color = 'var(--danger)'; return; }
    const isFirst = !hasWordmark(fabricCanvas);
    applyWordmark(isFirst);
    logAction(`Wordmark: ${text} [${accentSelect.value}]`);
    pushUndoState(fabricCanvas, currentState, currentSize);
  };

  // Remove button
  const removeBtn = document.createElement('button');
  removeBtn.className = 'btn-small';
  removeBtn.style.width = '100%';
  removeBtn.style.marginTop = '4px';
  removeBtn.textContent = 'Remove Wordmark';
  removeBtn.onclick = () => {
    removeWordmark(fabricCanvas);
    statusEl.textContent = '';
    addBtn.textContent = 'Add Wordmark';
    logAction('Removed wordmark');
    updateBackground(getScheme(currentState.scheme.name || 'dark'), currentSize);
    pushUndoState(fabricCanvas, currentState, currentSize);
  };

  container.appendChild(textLabel);
  container.appendChild(textInput);
  container.appendChild(fontLabel);
  container.appendChild(fontSelect);
  container.appendChild(colorRow);
  container.appendChild(styleRow);
  container.appendChild(accentLabel);
  container.appendChild(accentSelect);
  container.appendChild(addBtn);
  container.appendChild(removeBtn);
  return container;
}

// ─────────────────────────────────────────────────
// STATE PERSISTENCE — localStorage save/restore
// ─────────────────────────────────────────────────

const PERSIST_KEY = 'stencil-editor-state';
const PERSIST_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

function saveEditorState() {
  try {
    const data = {
      timestamp: Date.now(),
      template: currentState.template,
      scheme: currentState.scheme?.name,
      size: currentSize,
      overlays: currentState.overlays,
      eventDateISO: currentState.eventDateISO,
      sectionCollapsed: { ...sectionCollapsed },
      snapEnabled,
      snapGridSize,
      textEdits: captureTextEdits(),
      wordmark: {
        text: currentState._wordmarkText,
        font: currentState._wordmarkFont,
        color: currentState._wordmarkColor,
        bold: currentState._wordmarkBold,
        italic: currentState._wordmarkItalic,
        uppercase: currentState._wordmarkUppercase,
        accent: currentState._wordmarkAccent,
      },
      mangleSeed: getMangleSeed(),
      mangleParams: currentState._mangleParams,
      blendLayers: currentState.blendLayers || [],
      dateFormat: currentState.dateFormat,
      eventTime: currentState.eventTime,
      editorMode,
      advancedPositions: { ...advancedPositions },
      templatePositions: { ...templatePositions },
      userModifiedSizes: [...userModifiedSizes],
      userTextObjects: (() => { captureUserTextObjects(); return [...userTextObjects]; })(),
      textStyleOverrides: { ...textStyleOverrides },
      groupAlignOverrides: { ...groupAlignOverrides },
    };
    localStorage.setItem(PERSIST_KEY, JSON.stringify(data));
  } catch (_) { /* quota exceeded or private browsing */ }
}

function loadEditorState() {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.timestamp > PERSIST_TTL) {
      localStorage.removeItem(PERSIST_KEY);
      return null;
    }
    return data;
  } catch (_) { return null; }
}

// Auto-save periodically and on key events
function setupPersistence() {
  // Save every 10 seconds
  setInterval(saveEditorState, 10000);
  // Save on visibility change (tab switch / close)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') saveEditorState();
  });
}

// ─────────────────────────────────────────────────
// BATCH EXPORT — export all sizes at once
// ─────────────────────────────────────────────────

/**
 * Open a modal with size checkboxes for batch export.
 * Pre-selects sizes the user has actively worked on (userModifiedSizes).
 * Always pre-selects the current active size.
 */
function openBatchExportModal() {
  const overlay = document.createElement('div');
  overlay.className = 'scheduler-overlay';

  const panel = document.createElement('div');
  panel.className = 'scheduler-panel';
  panel.style.maxWidth = '500px';

  const title = document.createElement('h3');
  title.textContent = 'BATCH EXPORT';
  panel.appendChild(title);

  const hint = document.createElement('div');
  hint.className = 'hint';
  hint.style.marginBottom = '12px';
  hint.textContent = 'Select which sizes to export. Sizes you\u2019ve worked on are pre-selected.';
  panel.appendChild(hint);

  const allSizes = Object.keys(TEMPLATE_SIZES);
  const checkboxes = {};

  // Select All / None buttons
  const selectRow = document.createElement('div');
  selectRow.style.cssText = 'display:flex;gap:6px;margin-bottom:10px;';
  const selectAll = document.createElement('button');
  selectAll.className = 'btn-small';
  selectAll.textContent = 'SELECT ALL';
  selectAll.title = 'Select all available sizes for export';
  selectAll.onclick = () => Object.values(checkboxes).forEach(cb => cb.checked = true);
  const selectNone = document.createElement('button');
  selectNone.className = 'btn-small';
  selectNone.textContent = 'SELECT NONE';
  selectNone.title = 'Deselect all sizes';
  selectNone.onclick = () => Object.values(checkboxes).forEach(cb => cb.checked = false);
  selectRow.appendChild(selectAll);
  selectRow.appendChild(selectNone);
  panel.appendChild(selectRow);

  // Size checkboxes grouped by category
  const groups = {
    'Print': ['letter', 'tabloid'],
    'Social': ['social-vertical', 'story', 'event-banner', 'pinterest', 'yt-thumbnail', 'threads', 'bandcamp'],
  };

  for (const [groupName, keys] of Object.entries(groups)) {
    const groupLabel = document.createElement('div');
    groupLabel.className = 'field-label';
    groupLabel.textContent = groupName;
    panel.appendChild(groupLabel);

    for (const key of keys) {
      const size = TEMPLATE_SIZES[key];
      if (!size) continue;

      const row = document.createElement('div');
      row.className = 'check-row';

      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.id = `batch-${key}`;
      // Pre-select: current size + any user-modified sizes
      cb.checked = key === currentSize || userModifiedSizes.has(key);

      const label = document.createElement('label');
      label.htmlFor = `batch-${key}`;
      label.className = 'checkbox-label';
      label.textContent = `${size.label} (${size.width}\u00d7${size.height})`;
      label.title = `Export at ${size.width}\u00d7${size.height} pixels`;
      if (key === currentSize) {
        label.style.color = 'var(--accent)';
        label.textContent += ' \u2022 active';
      }
      if (userModifiedSizes.has(key) && key !== currentSize) {
        label.textContent += ' \u2022 modified';
      }

      row.appendChild(cb);
      row.appendChild(label);
      panel.appendChild(row);
      checkboxes[key] = cb;
    }
  }

  // Action buttons
  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;gap:8px;margin-top:16px;justify-content:flex-end;';

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'btn-small';
  cancelBtn.textContent = 'CANCEL';
  cancelBtn.onclick = () => overlay.remove();

  const exportBtn = document.createElement('button');
  exportBtn.className = 'btn-small';
  exportBtn.style.cssText = 'border-color:var(--accent);color:var(--accent);';
  exportBtn.textContent = 'EXPORT SELECTED';
  exportBtn.title = 'Export all selected sizes as individual PNG files';
  exportBtn.onclick = () => {
    const selected = Object.entries(checkboxes)
      .filter(([_, cb]) => cb.checked)
      .map(([key]) => key);
    overlay.remove();
    if (selected.length === 0) return;
    batchExportSizes(selected);
  };

  actions.appendChild(cancelBtn);
  actions.appendChild(exportBtn);
  panel.appendChild(actions);

  overlay.appendChild(panel);
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  document.body.appendChild(overlay);
}

/**
 * Batch export specific sizes.
 * Uses Advanced Mode positions when in Advanced Mode.
 *
 * @param {string[]} sizeKeys - Array of size keys to export
 */
async function batchExportSizes(sizeKeys) {
  const originalSize = currentSize;
  const template = TEMPLATES[currentState.template];
  const scheme = getScheme(currentState.scheme.name || 'dark');
  const templateName = template ? template.name.replace(/\s+/g, '-').toLowerCase() : 'design';

  logAction(`Batch export: ${sizeKeys.length} sizes`);

  // Show progress overlay
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position:fixed; inset:0; background:rgba(0,0,0,0.85); z-index:10000;
    display:flex; align-items:center; justify-content:center;
  `;
  const progressBox = document.createElement('div');
  progressBox.style.cssText = `
    background:#1a1a1a; border:1px solid rgba(255,255,255,0.15); padding:32px 48px;
    font-family:'DM Mono',monospace; color:#e0e0e0; text-align:center; min-width:280px;
  `;
  const progressLabel = document.createElement('div');
  progressLabel.style.cssText = 'font-size:11px; letter-spacing:1px; margin-bottom:12px;';
  progressLabel.textContent = 'EXPORTING...';
  const progressBar = document.createElement('div');
  progressBar.style.cssText = 'width:100%; height:4px; background:#333; border-radius:2px; overflow:hidden;';
  const progressFill = document.createElement('div');
  progressFill.style.cssText = 'width:0%; height:100%; background:var(--accent, #e040a0); transition:width 0.2s;';
  progressBar.appendChild(progressFill);
  const progressDetail = document.createElement('div');
  progressDetail.style.cssText = 'font-size:10px; color:#666; margin-top:8px;';
  progressBox.appendChild(progressLabel);
  progressBox.appendChild(progressBar);
  progressBox.appendChild(progressDetail);
  overlay.appendChild(progressBox);
  document.body.appendChild(overlay);

  const zip = new JSZip();
  let exported = 0;

  for (let i = 0; i < sizeKeys.length; i++) {
    const key = sizeKeys[i];
    const sizeInfo = TEMPLATE_SIZES[key];
    currentSize = key;

    // Update progress
    progressLabel.textContent = `EXPORTING ${i + 1} / ${sizeKeys.length}`;
    progressFill.style.width = `${((i) / sizeKeys.length) * 100}%`;
    progressDetail.textContent = `${sizeInfo.label} (${sizeInfo.width}\u00d7${sizeInfo.height})`;

    displayScale = getDisplayScale(sizeInfo.width, sizeInfo.height);
    const dw = Math.round(sizeInfo.width * displayScale);
    const dh = Math.round(sizeInfo.height * displayScale);
    fabricCanvas.setDimensions({ width: dw, height: dh });

    hydrateTemplate(template, scheme, key);

    // Restore saved positions for this size (both modes)
    const exportStore = editorMode === 'advanced' ? advancedPositions : templatePositions;
    if (exportStore[key]) {
      restorePositions(key, key, exportStore);
    }

    // Allow render to complete
    await new Promise(r => setTimeout(r, 150));

    try {
      const blob = await exportPNGToBlob(key, sizeInfo);
      const filename = `stencil-${templateName}-${key}.png`;
      zip.file(filename, blob);
      exported++;
    } catch (e) {
      console.error(`Batch export failed for ${key}:`, e);
    }
  }

  // Restore original size and positions
  currentSize = originalSize;
  hydrateTemplate(template, scheme, currentSize);
  const restoreStore = editorMode === 'advanced' ? advancedPositions : templatePositions;
  if (restoreStore[currentSize]) {
    restorePositions(currentSize, currentSize, restoreStore);
  }

  // Generate and download zip
  progressLabel.textContent = 'ZIPPING FILES...';
  progressFill.style.width = '100%';
  progressDetail.textContent = `${exported} files`;

  try {
    const zipBlob = await zip.generateAsync({ type: 'blob' }, (meta) => {
      progressDetail.textContent = `Compressing: ${Math.round(meta.percent)}%`;
    });
    const link = document.createElement('a');
    link.download = `stencil-${templateName}-batch.zip`;
    link.href = URL.createObjectURL(zipBlob);
    link.click();
    // Clean up object URL after a delay
    setTimeout(() => URL.revokeObjectURL(link.href), 10000);
    logAction(`Batch export complete: ${exported} files zipped`);
  } catch (e) {
    console.error('Zip generation failed:', e);
    logAction('Batch export failed');
  }

  overlay.remove();
}

/**
 * Render a single size to a PNG Blob (no download).
 * Used by batch export to collect blobs for zipping.
 *
 * @param {string} key - Size key
 * @param {object} sizeInfo - { width, height, label }
 * @returns {Promise<Blob>}
 */
function exportPNGToBlob(key, sizeInfo) {
  return new Promise((resolve, reject) => {
    const fullW = sizeInfo.width;
    const fullH = sizeInfo.height;
    const multiplier = 1 / displayScale;
    const scheme = getScheme(currentState.scheme.name || 'dark');

    const bgUrl = renderBackground({
      width: fullW,
      height: fullH,
      scheme,
      overlays: currentState.overlays,
      watermark: shouldShowWatermark(),
    });

    const savedBg = fabricCanvas.backgroundImage;
    fabricCanvas.backgroundImage = null;
    const objectsDataUrl = fabricCanvas.toDataURL({ format: 'png', quality: 1, multiplier });
    fabricCanvas.backgroundImage = savedBg;

    const bgImg = new Image();
    bgImg.src = bgUrl;

    bgImg.onload = () => {
      const objImg = new Image();
      objImg.src = objectsDataUrl;

      objImg.onload = () => {
        const exportCanvas = document.createElement('canvas');
        exportCanvas.width = fullW;
        exportCanvas.height = fullH;
        const ctx = exportCanvas.getContext('2d');

        ctx.drawImage(bgImg, 0, 0, fullW, fullH);
        ctx.drawImage(objImg, 0, 0, fullW, fullH);

        exportCanvas.toBlob(blob => {
          if (blob) resolve(blob);
          else reject(new Error(`toBlob returned null for ${key}`));
        }, 'image/png');
      };
      objImg.onerror = () => reject(new Error(`Failed to load objects image for ${key}`));
    };
    bgImg.onerror = () => reject(new Error(`Failed to load background for ${key}`));
  });
}

// ─────────────────────────────────────────────────
// ACTION LOG — floating overlay on canvas
// ─────────────────────────────────────────────────

let _actionLogVisible = false;

function setupActionLogOverlay() {
  // Toggle button
  const toggle = document.createElement('button');
  toggle.className = 'action-log-toggle';
  toggle.textContent = 'LOG';
  toggle.onclick = () => {
    _actionLogVisible = !_actionLogVisible;
    panel.style.display = _actionLogVisible ? 'block' : 'none';
    toggle.textContent = _actionLogVisible ? 'LOG ▼' : 'LOG';
  };
  document.body.appendChild(toggle);

  // Panel
  const panel = document.createElement('div');
  panel.className = 'action-log-panel';
  panel.style.display = 'none';
  _logPanelEl = panel;
  document.body.appendChild(panel);
  renderActionLog();
}

// ─────────────────────────────────────────────────
// AI DESIGNER — modal interface
// ─────────────────────────────────────────────────

function openAiDesigner() {
  document.querySelector('.ai-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'ai-overlay';

  const panel = document.createElement('div');
  panel.className = 'ai-panel';

  const title = document.createElement('h3');
  title.textContent = 'ai designer';
  panel.appendChild(title);

  const hint = document.createElement('div');
  hint.className = 'hint';
  hint.style.marginBottom = '10px';
  hint.textContent = 'Describe your event and let AI suggest the design — colors, fonts, text, and patterns.';
  panel.appendChild(hint);

  // Quick presets
  const presets = document.createElement('div');
  presets.className = 'ai-presets';
  const presetPrompts = [
    'Make it look like a punk show',
    'Corporate networking event',
    'Beach party vibes',
    'Elegant gala dinner',
    'Retro 80s theme',
    'Minimalist and clean',
  ];
  for (const p of presetPrompts) {
    const btn = document.createElement('button');
    btn.className = 'ai-preset-btn';
    btn.textContent = p;
    btn.onclick = () => { promptInput.value = p; };
    presets.appendChild(btn);
  }
  panel.appendChild(presets);

  // Prompt input
  const promptInput = document.createElement('textarea');
  promptInput.className = 'ai-prompt-input';
  promptInput.placeholder = 'Describe your event, mood, or style...\n\nExamples:\n"Electronic music festival in a warehouse, dark and neon"\n"Community charity 5K run, family-friendly, bright colors"';
  panel.appendChild(promptInput);

  // File upload
  const fileRow = document.createElement('div');
  fileRow.style.cssText = 'display:flex;gap:8px;margin-bottom:10px;align-items:center;';
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/*,text/plain';
  fileInput.style.cssText = 'font-size:10px;color:var(--text-dim);';
  const fileHint = document.createElement('span');
  fileHint.className = 'hint';
  fileHint.textContent = 'Or upload a flyer/screenshot to copy its style';
  fileRow.appendChild(fileInput);
  fileRow.appendChild(fileHint);
  panel.appendChild(fileRow);

  // Buttons
  const btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;gap:8px;margin-bottom:14px;';

  const generateBtn = document.createElement('button');
  generateBtn.className = 'btn-small';
  generateBtn.style.cssText = 'flex:1;padding:10px;border-color:var(--accent);color:var(--accent);';
  generateBtn.textContent = 'GENERATE DESIGN';
  generateBtn.onclick = () => runAiGeneration(promptInput, fileInput, resultsEl, generateBtn);
  btnRow.appendChild(generateBtn);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'btn-small';
  closeBtn.textContent = 'CLOSE';
  closeBtn.onclick = () => overlay.remove();
  btnRow.appendChild(closeBtn);

  panel.appendChild(btnRow);

  // Results area
  const resultsEl = document.createElement('div');
  resultsEl.className = 'ai-suggestions';
  panel.appendChild(resultsEl);

  overlay.appendChild(panel);
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
  promptInput.focus();
}

async function runAiGeneration(promptInput, fileInput, resultsEl, generateBtn) {
  const prompt = promptInput.value.trim();
  if (!prompt) { resultsEl.innerHTML = '<div class="hint" style="color:var(--danger)">Enter a description</div>'; return; }

  generateBtn.textContent = 'GENERATING…';
  generateBtn.disabled = true;
  resultsEl.innerHTML = '<div class="hint">Thinking about your design…</div>';

  try {
    const formData = new FormData();
    formData.append('prompt', prompt);

    // Include current state context
    formData.append('context', JSON.stringify({
      template: currentState.template,
      scheme: currentState.scheme?.name,
      lineArt: currentState.overlays?.lineArt?.type,
    }));

    const file = fileInput.files?.[0];
    if (file) formData.append('file', file);

    const res = await fetch('/api/ai-assist', {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    const data = await res.json();
    if (!res.ok || !data.ok) {
      throw new Error(data.error || 'Generation failed');
    }

    renderAiResults(data.design, resultsEl);
    logAction('AI design generated');
  } catch (e) {
    resultsEl.innerHTML = `<div class="hint" style="color:var(--danger)">${e.message}</div>`;
  } finally {
    generateBtn.textContent = 'GENERATE DESIGN';
    generateBtn.disabled = false;
  }
}

function renderAiResults(design, resultsEl) {
  resultsEl.innerHTML = '';

  if (design.reasoning) {
    const reason = document.createElement('div');
    reason.className = 'hint';
    reason.style.cssText = 'margin-bottom:10px;font-style:italic;color:var(--text);';
    reason.textContent = design.reasoning;
    resultsEl.appendChild(reason);
  }

  // Apply all button
  const applyAll = document.createElement('button');
  applyAll.className = 'btn-small';
  applyAll.style.cssText = 'width:100%;padding:10px;margin-bottom:12px;border-color:var(--accent);color:var(--accent);';
  applyAll.textContent = 'APPLY ALL SUGGESTIONS';
  applyAll.onclick = () => {
    applyAiDesign(design);
    document.querySelector('.ai-overlay')?.remove();
  };
  resultsEl.appendChild(applyAll);

  // Individual suggestion cards
  if (design.scheme) {
    const card = document.createElement('div');
    card.className = 'ai-suggestion-card';
    card.innerHTML = `<div class="label">Color Scheme</div><div class="value">${design.scheme}</div>`;
    card.onclick = () => {
      const scheme = getScheme(design.scheme);
      if (scheme) {
        currentState.scheme = { ...scheme, name: design.scheme };
        applySchemeToObjects(scheme);
        updateBackground(scheme, currentSize);
        logAction(`AI scheme: ${design.scheme}`);
        buildSidebar();
      }
    };
    resultsEl.appendChild(card);
  }

  if (design.lineArt && design.lineArt !== 'none') {
    const card = document.createElement('div');
    card.className = 'ai-suggestion-card';
    card.innerHTML = `<div class="label">Line Art</div><div class="value">${design.lineArt}</div>`;
    card.onclick = () => {
      currentState.overlays.lineArt.type = design.lineArt;
      updateBackground(getScheme(currentState.scheme.name || 'dark'), currentSize);
      logAction(`AI line art: ${design.lineArt}`);
    };
    resultsEl.appendChild(card);
  }

  if (design.fields) {
    for (const [id, text] of Object.entries(design.fields)) {
      const card = document.createElement('div');
      card.className = 'ai-suggestion-card';
      card.innerHTML = `<div class="label">${id}</div><div class="value">${text}</div>`;
      card.onclick = () => {
        const obj = fabricCanvas.getObjects().find(o => o.data?.id === id);
        if (obj) {
          obj.set('text', text);
          fabricCanvas.renderAll();
          logAction(`AI text: ${id}`);
        }
      };
      resultsEl.appendChild(card);
    }
  }
}

function applyAiDesign(design) {
  // Apply scheme
  if (design.scheme) {
    const scheme = getScheme(design.scheme);
    if (scheme) {
      currentState.scheme = { ...scheme, name: design.scheme };
      applySchemeToObjects(scheme);
    }
  }

  // Apply line art
  if (design.lineArt) {
    currentState.overlays.lineArt.type = design.lineArt;
  }
  if (design.lineArt2) {
    if (!currentState.overlays.lineArt2) {
      currentState.overlays.lineArt2 = { type: 'none', density: 30, opacity: 20, seed: 137 };
    }
    currentState.overlays.lineArt2.type = design.lineArt2;
  }

  // Apply text fields
  if (design.fields) {
    for (const [id, text] of Object.entries(design.fields)) {
      const obj = fabricCanvas.getObjects().find(o => o.data?.id === id);
      if (obj) obj.set('text', text);
      // Show hidden lineup objects if AI provides content
      if (id.startsWith('lineup-') && hiddenObjects.has(id)) {
        hiddenObjects.delete(id);
        if (obj) obj.set('visible', true);
      }
    }
  }

  // Refresh background and canvas
  updateBackground(getScheme(currentState.scheme.name || 'dark'), currentSize);
  fabricCanvas.renderAll();

  logAction('Applied AI design');
  pushUndoState(fabricCanvas, currentState, currentSize);
  buildSidebar();
}

/**
 * Build the Remix section for the sidebar.
 * Contains a prominent "Remix" button and a "Return to Gallery" link
 * if a previous remix session exists.
 */
function buildRemixButton() {
  const container = document.createElement('div');

  // Main remix button — full width, prominent
  const remixBtn = document.createElement('button');
  remixBtn.className = 'btn-small';
  remixBtn.style.cssText = `
    width:100%;padding:10px 14px;font-size:12px;letter-spacing:2px;
    font-weight:600;text-transform:uppercase;
    border:1px solid var(--accent);color:var(--accent);
    background:transparent;cursor:pointer;
    transition:background 0.2s, color 0.2s;
  `;
  remixBtn.textContent = 'REMIX DESIGN';
  remixBtn.title = 'Generate 5 alternative visual styles for your design (R)';
  remixBtn.onmouseenter = () => { remixBtn.style.background = 'var(--accent)'; remixBtn.style.color = '#000'; };
  remixBtn.onmouseleave = () => { remixBtn.style.background = 'transparent'; remixBtn.style.color = 'var(--accent)'; };
  remixBtn.onclick = () => openRemixModal(true);
  container.appendChild(remixBtn);

  // "Return to gallery" link if a previous session exists
  if (_lastRemixGallery) {
    const returnLink = document.createElement('button');
    returnLink.style.cssText = `
      display:block;width:100%;margin-top:6px;padding:4px;
      background:none;border:none;color:var(--text-dim);
      font-family:"DM Mono",monospace;font-size:10px;
      cursor:pointer;text-align:center;
    `;
    returnLink.textContent = 'Return to last gallery';
    returnLink.title = 'Re-open the previous remix gallery to pick a different variation';
    returnLink.onclick = () => openRemixModal(false);
    container.appendChild(returnLink);
  }

  // Hint
  const hint = document.createElement('div');
  hint.style.cssText = 'font-size:9px;color:var(--text-dim);text-align:center;margin-top:6px;';
  hint.textContent = 'Shortcut: R';
  container.appendChild(hint);

  return container;
}

// ─────────────────────────────────────────────────────────────────────────────
// PASTE & PARSE — Clipboard-to-Flyer Smart Import
// ─────────────────────────────────────────────────────────────────────────────
//
// Opens a modal where the user pastes unstructured event text (email, message,
// Facebook event, etc.). The parser extracts structured fields (title, date,
// time, venue, address, price, URL) and populates the active template.
//
// Layer 1: Regex pattern matching (dates, times, prices, URLs, emails, phones)
// Layer 2: Positional heuristics (title, venue, description detection)
// Runs entirely client-side — no API calls, no cost.

function buildPasteParseButton() {
  const container = document.createElement('div');

  const pasteBtn = document.createElement('button');
  pasteBtn.className = 'btn-small';
  pasteBtn.style.cssText = `
    width:100%;padding:10px 14px;font-size:12px;letter-spacing:2px;
    font-weight:600;text-transform:uppercase;
    border:1px dashed var(--accent);color:var(--accent);
    background:transparent;cursor:pointer;
    transition:background 0.2s, color 0.2s;
  `;
  pasteBtn.textContent = 'PASTE EVENT DETAILS';
  pasteBtn.title = 'Paste text from an email, message, or website to auto-fill your design';
  pasteBtn.onmouseenter = () => { pasteBtn.style.background = 'var(--accent)'; pasteBtn.style.color = '#000'; };
  pasteBtn.onmouseleave = () => { pasteBtn.style.background = 'transparent'; pasteBtn.style.color = 'var(--accent)'; };
  pasteBtn.onclick = () => openPasteParseModal();
  container.appendChild(pasteBtn);

  const hint = document.createElement('div');
  hint.style.cssText = 'font-size:9px;color:var(--text-dim);text-align:center;margin-top:6px;';
  hint.textContent = 'Paste an email, message, or event description';
  container.appendChild(hint);

  return container;
}

function openPasteParseModal() {
  // Remove existing modal
  document.querySelector('.paste-parse-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'paste-parse-overlay';
  overlay.style.cssText = `
    position:fixed;top:0;left:0;right:0;bottom:0;
    background:rgba(0,0,0,0.85);z-index:10000;
    display:flex;align-items:center;justify-content:center;
    animation:fadeIn 0.2s ease;
  `;

  const modal = document.createElement('div');
  modal.style.cssText = `
    background:var(--bg, #111);border:1px solid var(--border, #333);
    border-radius:8px;padding:24px 28px;max-width:640px;width:90vw;
    max-height:90vh;overflow-y:auto;position:relative;
    font-family:"DM Mono",monospace;color:var(--text, #eee);
  `;

  // Title
  const title = document.createElement('h2');
  title.style.cssText = 'margin:0 0 8px;font-size:16px;letter-spacing:2px;font-weight:600;';
  title.textContent = 'PASTE & PARSE';
  modal.appendChild(title);

  // Description
  const desc = document.createElement('p');
  desc.style.cssText = 'margin:0 0 16px;font-size:11px;color:var(--text-dim);line-height:1.5;';
  desc.textContent = 'Paste your event details below. We\'ll extract the title, date, venue, and other info automatically.';
  modal.appendChild(desc);

  // Textarea
  const textarea = document.createElement('textarea');
  textarea.style.cssText = `
    width:100%;min-height:140px;max-height:250px;padding:12px;
    background:rgba(255,255,255,0.05);border:1px solid var(--border);
    border-radius:4px;color:var(--text);font-family:"DM Mono",monospace;
    font-size:12px;line-height:1.6;resize:vertical;
    outline:none;transition:border-color 0.2s;
  `;
  textarea.placeholder = 'Paste an event description, email, message, or any text with your event details here...';
  textarea.onfocus = () => { textarea.style.borderColor = 'var(--accent)'; };
  textarea.onblur = () => { textarea.style.borderColor = 'var(--border)'; };
  modal.appendChild(textarea);

  // Parsed fields container (shows results)
  const fieldsContainer = document.createElement('div');
  fieldsContainer.style.cssText = 'margin-top:16px;display:none;';
  modal.appendChild(fieldsContainer);

  // Action buttons row
  const btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;gap:10px;margin-top:16px;';

  const parseBtn = document.createElement('button');
  parseBtn.className = 'btn-small';
  parseBtn.style.cssText = `
    flex:1;padding:10px 14px;font-size:12px;letter-spacing:2px;
    font-weight:600;text-transform:uppercase;
    border:1px solid var(--border);color:var(--text);
    background:transparent;cursor:pointer;
  `;
  parseBtn.textContent = 'PARSE';
  parseBtn.title = 'Extract event details from the pasted text';

  const applyBtn = document.createElement('button');
  applyBtn.className = 'btn-small';
  applyBtn.style.cssText = `
    flex:1;padding:10px 14px;font-size:12px;letter-spacing:2px;
    font-weight:600;text-transform:uppercase;
    border:1px solid var(--accent);color:var(--accent);
    background:transparent;cursor:pointer;display:none;
    transition:background 0.2s, color 0.2s;
  `;
  applyBtn.textContent = 'APPLY TO DESIGN';
  applyBtn.onmouseenter = () => { applyBtn.style.background = 'var(--accent)'; applyBtn.style.color = '#000'; };
  applyBtn.onmouseleave = () => { applyBtn.style.background = 'transparent'; applyBtn.style.color = 'var(--accent)'; };

  btnRow.appendChild(parseBtn);
  btnRow.appendChild(applyBtn);
  modal.appendChild(btnRow);

  // Close button
  const closeBtn = document.createElement('button');
  closeBtn.style.cssText = `
    position:absolute;top:12px;right:12px;background:none;border:none;
    color:var(--text-dim);cursor:pointer;font-size:18px;padding:4px 8px;
    font-family:"DM Mono",monospace;
  `;
  closeBtn.textContent = '\u00d7';
  closeBtn.onclick = () => overlay.remove();
  modal.appendChild(closeBtn);

  // State for parsed fields
  let currentParsed = null;
  let currentFieldEdits = {}; // user edits to extracted values

  // Confidence dot colors
  const CONF_COLORS = { high: '#00cc88', medium: '#e8a520', low: '#999' };

  function renderParsedFields(parsed) {
    fieldsContainer.innerHTML = '';
    fieldsContainer.style.display = 'block';
    currentFieldEdits = {};

    const labels = getFieldLabels();
    const fields = Object.entries(parsed).filter(([, v]) => v !== null);

    if (fields.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'padding:12px;color:var(--text-dim);font-size:11px;text-align:center;';
      empty.textContent = 'No event details detected. Try pasting a more complete description.';
      fieldsContainer.appendChild(empty);
      applyBtn.style.display = 'none';
      return;
    }

    // Header
    const header = document.createElement('div');
    header.style.cssText = 'font-size:10px;letter-spacing:1px;color:var(--text-dim);margin-bottom:10px;text-transform:uppercase;';
    header.textContent = 'Extracted Fields';
    fieldsContainer.appendChild(header);

    for (const [field, data] of fields) {
      const row = document.createElement('div');
      row.style.cssText = `
        display:flex;align-items:center;gap:8px;margin-bottom:8px;
        padding:6px 8px;border-radius:4px;
        background:rgba(255,255,255,0.03);
      `;

      // Confidence dot
      const dot = document.createElement('span');
      dot.style.cssText = `
        width:8px;height:8px;border-radius:50%;flex-shrink:0;
        background:${CONF_COLORS[data.confidence] || CONF_COLORS.low};
      `;
      dot.title = `${data.confidence} confidence`;
      row.appendChild(dot);

      // Label
      const label = document.createElement('span');
      label.style.cssText = 'font-size:10px;color:var(--text-dim);min-width:70px;flex-shrink:0;text-transform:uppercase;letter-spacing:1px;';
      label.textContent = labels[field] || field;
      row.appendChild(label);

      // Editable value
      const input = document.createElement('input');
      input.type = 'text';
      input.value = data.value;
      input.style.cssText = `
        flex:1;padding:4px 6px;background:transparent;border:1px solid transparent;
        border-radius:3px;color:var(--text);font-family:"DM Mono",monospace;
        font-size:11px;outline:none;
        transition:border-color 0.2s;
      `;
      input.onfocus = () => { input.style.borderColor = 'var(--accent)'; };
      input.onblur = () => { input.style.borderColor = 'transparent'; };
      input.oninput = () => { currentFieldEdits[field] = input.value; };
      row.appendChild(input);

      // Clear button
      const clearBtn = document.createElement('button');
      clearBtn.style.cssText = `
        background:none;border:none;color:var(--text-dim);cursor:pointer;
        font-size:14px;padding:2px 4px;flex-shrink:0;opacity:0.5;
      `;
      clearBtn.textContent = '\u00d7';
      clearBtn.title = 'Remove this field';
      clearBtn.onclick = () => {
        row.remove();
        currentFieldEdits[field] = ''; // mark as cleared
      };
      row.appendChild(clearBtn);

      fieldsContainer.appendChild(row);
    }

    applyBtn.style.display = 'block';
  }

  // Parse button handler
  parseBtn.onclick = () => {
    const raw = textarea.value.trim();
    if (!raw) {
      fieldsContainer.innerHTML = '';
      fieldsContainer.style.display = 'block';
      const empty = document.createElement('div');
      empty.style.cssText = 'padding:12px;color:var(--text-dim);font-size:11px;text-align:center;';
      empty.textContent = 'Paste some text above first, then click Parse.';
      fieldsContainer.appendChild(empty);
      return;
    }
    currentParsed = parseEventText(raw);
    renderParsedFields(currentParsed);
  };

  // Auto-parse on paste with debounce
  let parseTimer = null;
  textarea.addEventListener('paste', () => {
    clearTimeout(parseTimer);
    parseTimer = setTimeout(() => {
      const raw = textarea.value.trim();
      if (raw.length > 10) {
        currentParsed = parseEventText(raw);
        renderParsedFields(currentParsed);
      }
    }, 500);
  });

  // Apply button handler
  applyBtn.onclick = () => {
    if (!currentParsed) return;

    // Merge user edits back into parsed data
    const finalParsed = { ...currentParsed };
    for (const [field, editedValue] of Object.entries(currentFieldEdits)) {
      if (editedValue === '') {
        finalParsed[field] = null; // user cleared this field
      } else if (finalParsed[field]) {
        finalParsed[field] = { ...finalParsed[field], value: editedValue };
      }
    }

    // Map to template fields
    const template = TEMPLATES[currentState.template];
    const { textEdits, unmapped } = mapToTemplate(finalParsed, template.objects);

    // Apply text edits to canvas
    pushUndoState(fabricCanvas, currentState, currentSize);
    fabricCanvas.getObjects().forEach(obj => {
      if (obj.data?.id && textEdits[obj.data.id]) {
        obj.set('text', textEdits[obj.data.id]);
      }
    });
    fabricCanvas.renderAll();

    // Rebuild sidebar to reflect changes
    buildSidebar();

    // Close modal
    overlay.remove();

    logAction('Applied parsed event details');

    // Show unmapped fields as a brief notification if any
    if (unmapped.length > 0) {
      console.log('Unmapped fields:', unmapped);
    }
  };

  overlay.appendChild(modal);
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  document.body.appendChild(overlay);

  // Focus textarea
  setTimeout(() => textarea.focus(), 100);

  // ESC to close
  const escHandler = (e) => {
    if (e.key === 'Escape') {
      overlay.remove();
      document.removeEventListener('keydown', escHandler);
    }
  };
  document.addEventListener('keydown', escHandler);
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARE DESIGN LINK
// ─────────────────────────────────────────────────────────────────────────────

function buildShareButton() {
  const container = document.createElement('div');

  const shareBtn = document.createElement('button');
  shareBtn.className = 'btn-small';
  shareBtn.style.cssText = `
    width:100%;padding:10px 14px;font-size:12px;letter-spacing:2px;
    font-weight:600;text-transform:uppercase;
    border:1px solid var(--border);color:var(--text);
    background:transparent;cursor:pointer;
    transition:background 0.2s, color 0.2s;
  `;
  shareBtn.textContent = 'SHARE DESIGN';
  shareBtn.title = 'Generate a shareable link to this design';
  shareBtn.onmouseenter = () => { shareBtn.style.background = 'var(--text)'; shareBtn.style.color = 'var(--bg)'; };
  shareBtn.onmouseleave = () => { shareBtn.style.background = 'transparent'; shareBtn.style.color = 'var(--text)'; };
  shareBtn.onclick = () => openShareModal();
  container.appendChild(shareBtn);

  const hint = document.createElement('div');
  hint.style.cssText = 'font-size:9px;color:var(--text-dim);text-align:center;margin-top:6px;';
  hint.textContent = 'Anyone with the link gets a copy to customize';
  container.appendChild(hint);

  return container;
}

async function openShareModal() {
  // Remove existing modal
  document.querySelector('.share-modal-overlay')?.remove();

  // Build payload
  const user = getUser();
  const payload = buildSharePayload({
    currentState,
    captureTextEdits,
    textStyleOverrides,
    groupAlignOverrides,
    userTextObjects,
    userId: user?.id || null,
    mangleSeed: getMangleSeed(),
    currentSize,
  });

  // Encode
  const result = await encodeShareLink(payload);

  // Build modal
  const overlay = document.createElement('div');
  overlay.className = 'share-modal-overlay';
  overlay.style.cssText = `
    position:fixed;top:0;left:0;right:0;bottom:0;
    background:rgba(0,0,0,0.85);z-index:10000;
    display:flex;align-items:center;justify-content:center;
    animation:fadeIn 0.2s ease;
  `;

  const modal = document.createElement('div');
  modal.style.cssText = `
    background:var(--bg, #111);border:1px solid var(--border, #333);
    border-radius:8px;padding:24px 28px;max-width:560px;width:90vw;
    position:relative;font-family:"DM Mono",monospace;color:var(--text, #eee);
  `;

  // Title
  const title = document.createElement('h2');
  title.style.cssText = 'margin:0 0 8px;font-size:16px;letter-spacing:2px;font-weight:600;';
  title.textContent = 'SHARE DESIGN';
  modal.appendChild(title);

  // Description
  const desc = document.createElement('p');
  desc.style.cssText = 'margin:0 0 16px;font-size:11px;color:var(--text-dim);line-height:1.5;';
  desc.textContent = 'Anyone with this link gets their own copy of your design to customize. Your original stays untouched.';
  modal.appendChild(desc);

  if (result.error) {
    // Too large — show error
    const errBox = document.createElement('div');
    errBox.style.cssText = `
      padding:12px;border:1px solid #ff4444;border-radius:4px;
      color:#ff4444;font-size:11px;margin-bottom:12px;
    `;
    errBox.textContent = result.error;
    modal.appendChild(errBox);
  } else {
    // URL display box
    const urlBox = document.createElement('div');
    urlBox.style.cssText = `
      background:rgba(255,255,255,0.05);border:1px solid var(--border);
      border-radius:4px;padding:10px 12px;margin-bottom:12px;
      font-size:10px;word-break:break-all;line-height:1.5;
      color:var(--text-dim);max-height:80px;overflow-y:auto;
      cursor:text;user-select:all;
    `;
    urlBox.textContent = result.url;
    modal.appendChild(urlBox);

    // Copy button
    const copyBtn = document.createElement('button');
    copyBtn.className = 'btn-small';
    copyBtn.style.cssText = `
      width:100%;padding:10px 14px;font-size:12px;letter-spacing:2px;
      font-weight:600;text-transform:uppercase;
      border:1px solid var(--accent);color:var(--accent);
      background:transparent;cursor:pointer;
      transition:background 0.2s, color 0.2s;
      margin-bottom:12px;
    `;
    copyBtn.textContent = 'COPY LINK';
    copyBtn.onclick = async () => {
      try {
        await navigator.clipboard.writeText(result.url);
        copyBtn.textContent = 'COPIED!';
        copyBtn.style.background = 'var(--accent)';
        copyBtn.style.color = '#000';
        setTimeout(() => {
          copyBtn.textContent = 'COPY LINK';
          copyBtn.style.background = 'transparent';
          copyBtn.style.color = 'var(--accent)';
        }, 2000);
      } catch (e) {
        // Fallback: select text
        const range = document.createRange();
        range.selectNodeContents(urlBox);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        copyBtn.textContent = 'SELECT & COPY MANUALLY';
      }
    };
    modal.appendChild(copyBtn);

    // URL length info
    const lenInfo = document.createElement('div');
    lenInfo.style.cssText = 'font-size:9px;color:var(--text-dim);margin-bottom:8px;';
    lenInfo.textContent = `Link length: ${result.length} characters`;
    modal.appendChild(lenInfo);

    // Warning if applicable
    if (result.warning) {
      const warn = document.createElement('div');
      warn.style.cssText = `
        padding:8px 12px;border:1px solid #e8a520;border-radius:4px;
        color:#e8a520;font-size:10px;margin-bottom:12px;line-height:1.4;
      `;
      warn.textContent = result.warning;
      modal.appendChild(warn);
    }
  }

  // Close button
  const closeBtn = document.createElement('button');
  closeBtn.style.cssText = `
    position:absolute;top:12px;right:12px;background:none;border:none;
    color:var(--text-dim);cursor:pointer;font-size:18px;padding:4px 8px;
    font-family:"DM Mono",monospace;
  `;
  closeBtn.textContent = '\u00d7';
  closeBtn.onclick = () => overlay.remove();
  modal.appendChild(closeBtn);

  overlay.appendChild(modal);
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  document.body.appendChild(overlay);

  // ESC to close
  const escHandler = (e) => {
    if (e.key === 'Escape') {
      overlay.remove();
      document.removeEventListener('keydown', escHandler);
    }
  };
  document.addEventListener('keydown', escHandler);
}

function buildPresetsControls() {
  const container = document.createElement('div');

  // Save row
  const saveRow = document.createElement('div');
  saveRow.className = 'seed-row';
  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.className = 'field-input';
  nameInput.placeholder = 'Preset name';
  nameInput.title = 'Name for this preset (used to identify it later)';
  const saveBtn = document.createElement('button');
  saveBtn.className = 'btn-small';
  saveBtn.textContent = 'Save';
  saveBtn.title = 'Save the current design settings as a reusable preset';
  saveBtn.onclick = () => {
    const name = nameInput.value.trim();
    if (!name) return;
    captureUserTextObjects();
    const snap = captureSnapshot(fabricCanvas, currentState, currentSize, getMangleSeed(), {
      wordmark: {
        text: currentState._wordmarkText,
        font: currentState._wordmarkFont,
        color: currentState._wordmarkColor,
        bold: currentState._wordmarkBold,
        italic: currentState._wordmarkItalic,
        uppercase: currentState._wordmarkUppercase,
        accent: currentState._wordmarkAccent,
      },
      mangleParams: currentState._mangleParams,
      userTextObjects: [...userTextObjects],
    });
    savePreset(name, snap);
    nameInput.value = '';
    buildSidebar();
  };
  saveRow.appendChild(nameInput);
  saveRow.appendChild(saveBtn);
  container.appendChild(saveRow);

  // Preset list
  const names = listPresets();
  if (names.length === 0) {
    const hint = document.createElement('div');
    hint.className = 'hint';
    hint.textContent = 'No saved presets';
    hint.style.marginTop = '6px';
    container.appendChild(hint);
  } else {
    for (const name of names) {
      const row = document.createElement('div');
      row.className = 'seed-row';
      row.style.marginTop = '4px';
      const loadBtn = document.createElement('button');
      loadBtn.className = 'btn-small';
      loadBtn.style.flex = '1';
      loadBtn.style.textAlign = 'left';
      loadBtn.textContent = name;
      loadBtn.title = 'Load this preset — restores all saved design settings';
      loadBtn.onclick = () => {
        const snap = loadPreset(name);
        if (snap) {
          if (snap.mangleSeed != null) setMangleSeed(snap.mangleSeed);
          applySnapshotToEditor(snap);
        }
      };
      const delBtn = document.createElement('button');
      delBtn.className = 'btn-small';
      delBtn.textContent = '\u00d7';
      delBtn.title = 'Delete this saved preset permanently';
      delBtn.onclick = () => {
        deletePreset(name);
        buildSidebar();
      };
      row.appendChild(loadBtn);
      row.appendChild(delBtn);
      container.appendChild(row);
    }
  }

  return container;
}

/**
 * Build hidden objects list — allows showing them again.
 */
function buildHiddenObjectsList() {
  const container = document.createElement('div');
  const template = TEMPLATES[currentState.template];
  if (!template) return container;

  for (const id of hiddenObjects) {
    const tmplObj = template.objects.find(o => o.id === id);
    const row = document.createElement('div');
    row.className = 'seed-row';
    row.style.marginTop = '3px';

    const label = document.createElement('span');
    label.className = 'hint';
    label.style.flex = '1';
    label.textContent = tmplObj ? tmplObj.id : id;

    const showBtn = document.createElement('button');
    showBtn.className = 'btn-small';
    showBtn.textContent = 'Show';
    showBtn.onclick = () => {
      hiddenObjects.delete(id);
      fabricCanvas.getObjects().forEach(o => {
        if (o.data?.id === id) o.set('visible', true);
      });
      fabricCanvas.renderAll();
      buildSidebar();
    };

    row.appendChild(label);
    row.appendChild(showBtn);
    container.appendChild(row);
  }
  return container;
}

/**
 * Wire up Fabric.js selection events to populate the object properties panel.
 */
function setupSelectionHandlers() {
  fabricCanvas.on('selection:created', updateObjectPanel);
  fabricCanvas.on('selection:updated', updateObjectPanel);
  fabricCanvas.on('selection:cleared', clearObjectPanel);
}

/**
 * Right-click context menu on the canvas.
 * Shows "Add Text Object" and, if a user-created object is targeted, "Delete Object".
 */
function setupCanvasContextMenu() {
  const canvasEl = fabricCanvas.upperCanvasEl || fabricCanvas.wrapperEl;
  let menuEl = null;

  function dismissMenu() {
    if (menuEl) { menuEl.remove(); menuEl = null; }
  }

  function menuItem(label, opts = {}) {
    const el = document.createElement('div');
    el.textContent = label;
    const border = opts.border ? 'border-top:1px solid rgba(255,255,255,0.08);' : '';
    el.style.cssText = `padding:8px 14px; cursor:pointer; color:${opts.color || '#e0e0e0'}; transition:background 0.1s; font-size:11px; ${border}`;
    const hoverBg = opts.hoverBg || 'rgba(224,64,160,0.15)';
    const hoverColor = opts.hoverColor || '#e040a0';
    el.onmouseenter = () => { el.style.background = hoverBg; el.style.color = hoverColor; };
    el.onmouseleave = () => { el.style.background = 'transparent'; el.style.color = opts.color || '#e0e0e0'; };
    el.onclick = () => { dismissMenu(); opts.action?.(); };
    return el;
  }

  document.addEventListener('click', dismissMenu);
  document.addEventListener('contextmenu', (e) => {
    if (!canvasEl.contains(e.target)) dismissMenu();
  });

  canvasEl.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    dismissMenu();

    const rect = canvasEl.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;

    const found = fabricCanvas.findTarget(e);
    const activeObj = fabricCanvas.getActiveObject();
    const target = (found && found.data?.id) ? found : activeObj;

    // Gather targets: multi-select (ActiveSelection) or single object
    const isMulti = activeObj?.type === 'activeselection';
    const targets = isMulti
      ? activeObj.getObjects().filter(o => o.data?.id && !o.data.id.startsWith('__'))
      : (target?.data?.id && !target.data.id.startsWith('__') ? [target] : []);
    const hasTargets = targets.length > 0;
    const allUserCreated = targets.length > 0 && targets.every(o => o.data?.userCreated);
    const anyUserCreated = targets.some(o => o.data?.userCreated);
    const allLocked = targets.length > 0 && targets.every(o => o.data?.fxLocked);
    const suffix = isMulti ? ` (${targets.length})` : '';

    menuEl = document.createElement('div');
    menuEl.style.cssText = `
      position:fixed; left:${e.clientX}px; top:${e.clientY}px; z-index:9999;
      background:#1a1a1a; border:1px solid rgba(255,255,255,0.15); min-width:170px;
      font-family:'DM Mono',monospace; letter-spacing:0.5px;
      box-shadow:0 4px 20px rgba(0,0,0,0.6);
    `;

    // ── Object-targeted actions ──
    if (hasTargets) {
      // Lock / Unlock
      menuEl.appendChild(menuItem((allLocked ? 'UNLOCK' : 'LOCK') + suffix, {
        action: () => {
          const newLocked = !allLocked;
          for (const obj of targets) {
            if (obj.data) obj.data.fxLocked = newLocked;
            obj.set({
              lockMovementX: newLocked,
              lockMovementY: newLocked,
              lockRotation: newLocked,
              lockScalingX: newLocked,
              lockScalingY: newLocked,
              hasControls: !newLocked,
            });
          }
          fabricCanvas.renderAll();
          logAction(newLocked ? `Locked ${targets.length}` : `Unlocked ${targets.length}`);
          updateObjectPanel();
        },
      }));

      // Hide
      menuEl.appendChild(menuItem('HIDE' + suffix, {
        border: true,
        action: () => {
          for (const obj of targets) {
            if (obj.data?.id) {
              hiddenObjects.add(obj.data.id);
              obj.set('visible', false);
            }
          }
          fabricCanvas.discardActiveObject();
          fabricCanvas.renderAll();
          buildSidebar();
          logAction(`Hid ${targets.length} object(s)`);
        },
      }));

      // Duplicate
      menuEl.appendChild(menuItem('DUPLICATE' + suffix, {
        border: true,
        action: () => duplicateObjects(targets),
      }));

      // Z-order controls
      menuEl.appendChild(menuItem('PULL FORWARD' + suffix, {
        border: true,
        action: () => {
          for (const obj of targets) fabricCanvas.bringObjectForward(obj);
          fabricCanvas.renderAll();
          logAction('Pulled forward');
        },
      }));
      menuEl.appendChild(menuItem('PUSH BACKWARD' + suffix, {
        action: () => {
          for (const obj of [...targets].reverse()) fabricCanvas.sendObjectBackwards(obj);
          fabricCanvas.renderAll();
          logAction('Pushed backward');
        },
      }));
      menuEl.appendChild(menuItem('BRING TO FRONT' + suffix, {
        action: () => {
          for (const obj of targets) fabricCanvas.bringObjectToFront(obj);
          fabricCanvas.renderAll();
          logAction('Brought to front');
        },
      }));
      menuEl.appendChild(menuItem('SEND TO BACK' + suffix, {
        action: () => {
          for (const obj of [...targets].reverse()) fabricCanvas.sendObjectToBack(obj);
          fabricCanvas.renderAll();
          logAction('Sent to back');
        },
      }));

      // Delete — only when all targets are user-created
      if (anyUserCreated) {
        const userTargets = targets.filter(o => o.data?.userCreated);
        const delLabel = allUserCreated ? 'DELETE' + suffix : `DELETE USER OBJECTS (${userTargets.length})`;
        menuEl.appendChild(menuItem(delLabel, {
          border: true,
          color: '#ff4444',
          hoverBg: 'rgba(255,60,60,0.15)',
          hoverColor: '#ff4444',
          action: () => {
            for (const obj of userTargets) removeUserTextObject(obj.data.id);
            fabricCanvas.discardActiveObject();
            clearObjectPanel();
            buildSidebar();
          },
        }));
      }

      // Separator before canvas actions
      const sep = document.createElement('div');
      sep.style.cssText = 'height:1px; background:rgba(255,255,255,0.08); margin:4px 0;';
      menuEl.appendChild(sep);
    }

    // ── Canvas-level actions (always available) ──
    menuEl.appendChild(menuItem('UNDO', {
      color: canUndo() ? '#e0e0e0' : '#555',
      action: () => {
        if (!canUndo()) return;
        undo(applySnapshotToEditor);
        logAction('Undo');
      },
    }));
    menuEl.appendChild(menuItem('REDO', {
      color: canRedo() ? '#e0e0e0' : '#555',
      action: () => {
        if (!canRedo()) return;
        redo(applySnapshotToEditor);
        logAction('Redo');
      },
    }));

    menuEl.appendChild(menuItem('ADD TEXT OBJECT', {
      border: true,
      action: () => addUserTextObject(canvasX, canvasY),
    }));

    // Add Blend Layer
    const blendCount = (currentState.blendLayers || []).filter(Boolean).length;
    if (blendCount < 3) {
      menuEl.appendChild(menuItem('ADD BLEND LAYER', {
        action: () => {
          // Find the first empty slot
          const layers = currentState.blendLayers || [];
          let slot = layers.findIndex((l, i) => !l);
          if (slot === -1) slot = layers.length;
          addBlendLayer(slot);
        },
      }));
    }

    document.body.appendChild(menuEl);

    // Clamp menu to viewport
    const menuRect = menuEl.getBoundingClientRect();
    if (menuRect.right > window.innerWidth) menuEl.style.left = `${window.innerWidth - menuRect.width - 8}px`;
    if (menuRect.bottom > window.innerHeight) menuEl.style.top = `${window.innerHeight - menuRect.height - 8}px`;
  });
}

function updateObjectPanel() {
  const panel = document.getElementById('object-properties');
  if (!panel) return;
  const obj = fabricCanvas.getActiveObject();
  if (!obj || !obj.data?.id) { clearObjectPanel(); return; }
  // Allow blend layers to show controls, skip other internal objects
  if (obj.data.id?.startsWith('__') && obj.data.stencilType !== 'blendLayer') { clearObjectPanel(); return; }

  panel.innerHTML = '';

  // ── Object type indicator ──
  const typeIndicator = document.createElement('div');
  typeIndicator.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;';
  const typeLabel = document.createElement('span');
  typeLabel.style.cssText = 'font-size:9px;color:var(--text-dim);font-family:"DM Mono",monospace;letter-spacing:1px;text-transform:uppercase;';
  const roleLabel = obj.data.role ? ` \u2014 ${obj.data.role}` : '';
  if (obj.type === 'textbox') {
    typeLabel.textContent = `TEXT${roleLabel}`;
  } else if (obj.data?.stencilType === 'blendLayer') {
    typeLabel.textContent = `BLEND LAYER ${(obj.data.index || 0) + 1}`;
  } else if (obj.type === 'rect') {
    typeLabel.textContent = `SHAPE${roleLabel}`;
  } else if (obj.type === 'image') {
    typeLabel.textContent = `IMAGE${roleLabel}`;
  } else if (obj.type === 'group') {
    typeLabel.textContent = `GROUP${roleLabel}`;
  } else {
    typeLabel.textContent = `OBJECT (${obj.type})${roleLabel}`;
  }
  const idLabel = document.createElement('span');
  idLabel.style.cssText = 'font-size:8px;color:#444;font-family:"DM Mono",monospace;';
  idLabel.textContent = obj.data.id || '';
  idLabel.title = 'Internal object identifier';
  typeIndicator.appendChild(typeLabel);
  typeIndicator.appendChild(idLabel);
  panel.appendChild(typeIndicator);

  if (obj.type === 'textbox') {
    // Text content
    const contentLabel = document.createElement('label');
    contentLabel.className = 'field-label';
    contentLabel.textContent = 'Text';
    const contentInput = document.createElement('textarea');
    contentInput.className = 'field-textarea';
    contentInput.value = obj.text;
    contentInput.rows = 3;
    contentInput.title = 'Edit the text content of this object';
    contentInput.oninput = () => {
      obj.set('text', contentInput.value);
      fabricCanvas.renderAll();
    };

    // Font family
    const fontLabel = document.createElement('label');
    fontLabel.className = 'field-label';
    fontLabel.textContent = 'Font';
    const fontSelect = document.createElement('select');
    fontSelect.className = 'field-select';
    fontSelect.title = 'Choose font family for this text';
    const fonts = WORDMARK_FONTS;
    for (const f of fonts) {
      const opt = document.createElement('option');
      opt.value = f;
      opt.textContent = f;
      opt.selected = obj.fontFamily === f;
      fontSelect.appendChild(opt);
    }
    fontSelect.onchange = () => {
      obj.set('fontFamily', fontSelect.value);
      obj.removeStyle('fontFamily');
      fabricCanvas.renderAll();
    };

    // Font size
    const sizeLabel = document.createElement('label');
    sizeLabel.className = 'field-label';
    sizeLabel.textContent = 'Size';
    const sizeInput = document.createElement('input');
    sizeInput.type = 'number';
    sizeInput.className = 'field-input';
    sizeInput.value = obj.fontSize;
    sizeInput.title = 'Font size in pixels';
    sizeInput.oninput = () => {
      obj.set('fontSize', parseInt(sizeInput.value));
      obj.removeStyle('fontSize');
      fabricCanvas.renderAll();
    };

    // Style buttons: Bold, Italic, Underline
    const styleLabel = document.createElement('label');
    styleLabel.className = 'field-label';
    styleLabel.textContent = 'Style';
    const styleRow = document.createElement('div');
    styleRow.className = 'seed-row';

    const boldBtn = document.createElement('button');
    boldBtn.className = 'btn-small' + (obj.fontWeight === 'bold' ? ' active' : '');
    boldBtn.textContent = 'B';
    boldBtn.style.fontWeight = 'bold';
    boldBtn.title = 'Toggle bold';
    boldBtn.onclick = () => {
      const nw = obj.fontWeight === 'bold' ? 'normal' : 'bold';
      obj.set('fontWeight', nw);
      obj.removeStyle('fontWeight');
      boldBtn.className = 'btn-small' + (nw === 'bold' ? ' active' : '');
      fabricCanvas.renderAll();
    };

    const italicBtn = document.createElement('button');
    italicBtn.className = 'btn-small' + (obj.fontStyle === 'italic' ? ' active' : '');
    italicBtn.textContent = 'I';
    italicBtn.style.fontStyle = 'italic';
    italicBtn.title = 'Toggle italic';
    italicBtn.onclick = () => {
      const ns = obj.fontStyle === 'italic' ? 'normal' : 'italic';
      obj.set('fontStyle', ns);
      obj.removeStyle('fontStyle');
      italicBtn.className = 'btn-small' + (ns === 'italic' ? ' active' : '');
      fabricCanvas.renderAll();
    };

    const underlineBtn = document.createElement('button');
    underlineBtn.className = 'btn-small' + (obj.underline ? ' active' : '');
    underlineBtn.textContent = 'U';
    underlineBtn.style.textDecoration = 'underline';
    underlineBtn.title = 'Toggle underline';
    underlineBtn.onclick = () => {
      obj.set('underline', !obj.underline);
      obj.removeStyle('underline');
      underlineBtn.className = 'btn-small' + (obj.underline ? ' active' : '');
      fabricCanvas.renderAll();
    };

    styleRow.appendChild(boldBtn);
    styleRow.appendChild(italicBtn);
    styleRow.appendChild(underlineBtn);

    // Text align
    const alignLabel = document.createElement('label');
    alignLabel.className = 'field-label';
    alignLabel.textContent = 'Align';
    const alignRow = document.createElement('div');
    alignRow.className = 'seed-row';
    for (const align of ['left', 'center', 'right']) {
      const btn = document.createElement('button');
      btn.className = 'btn-small' + (obj.textAlign === align ? ' active' : '');
      btn.textContent = align === 'left' ? '\u2190' : align === 'center' ? '\u2194' : '\u2192';
      btn.title = align;
      btn.onclick = () => {
        obj.set('textAlign', align);
        alignRow.querySelectorAll('.btn-small').forEach(b => b.className = 'btn-small');
        btn.className = 'btn-small active';
        fabricCanvas.renderAll();
      };
      alignRow.appendChild(btn);
    }

    // Color
    const colorLabel = document.createElement('label');
    colorLabel.className = 'field-label';
    colorLabel.textContent = 'Color';
    const colorInput = document.createElement('input');
    colorInput.type = 'color';
    colorInput.className = 'field-color';
    colorInput.value = obj.fill || '#ffffff';
    colorInput.title = 'Text color';
    colorInput.oninput = () => {
      obj.set('fill', colorInput.value);
      obj.removeStyle('fill');
      fabricCanvas.renderAll();
    };

    // ── Text Background ──
    const bgSection = document.createElement('div');
    bgSection.style.cssText = 'margin-top:10px;padding-top:8px;border-top:1px solid var(--border);';

    if (!obj.data.textBg) obj.data.textBg = { enabled: false, color: '#000000', opacity: 0.5 };
    const bg = obj.data.textBg;

    const bgCheck = document.createElement('input');
    bgCheck.type = 'checkbox';
    bgCheck.checked = bg.enabled;
    bgCheck.id = 'textbg-enable';
    const bgLabel = document.createElement('label');
    bgLabel.htmlFor = 'textbg-enable';
    bgLabel.className = 'checkbox-label';
    bgLabel.textContent = ' Text Background';
    bgLabel.title = 'Add a solid color rectangle behind this text for readability';

    const bgControls = document.createElement('div');
    bgControls.style.cssText = 'display:flex;gap:6px;align-items:center;margin-top:4px;';
    bgControls.style.display = bg.enabled ? 'flex' : 'none';

    const bgColor = document.createElement('input');
    bgColor.type = 'color';
    bgColor.className = 'field-color';
    bgColor.value = bg.color || '#000000';
    bgColor.title = 'Background rectangle color';
    bgColor.oninput = () => {
      obj.data.textBg.color = bgColor.value;
      applyTextBackground(obj);
      fabricCanvas.renderAll();
    };

    const bgOpacity = document.createElement('input');
    bgOpacity.type = 'range';
    bgOpacity.className = 'field-range';
    bgOpacity.style.flex = '1';
    bgOpacity.min = 0; bgOpacity.max = 100;
    bgOpacity.title = 'Background rectangle transparency';
    bgOpacity.value = Math.round((bg.opacity ?? 0.5) * 100);
    bgOpacity.oninput = () => {
      obj.data.textBg.opacity = parseInt(bgOpacity.value) / 100;
      applyTextBackground(obj);
      fabricCanvas.renderAll();
    };

    bgCheck.onchange = () => {
      obj.data.textBg.enabled = bgCheck.checked;
      bgControls.style.display = bgCheck.checked ? 'flex' : 'none';
      if (bgCheck.checked) {
        applyTextBackground(obj);
      } else {
        const existing = fabricCanvas.getObjects().find(o => o.data?.id === `__textbg_${obj.data.id}`);
        if (existing) fabricCanvas.remove(existing);
      }
      fabricCanvas.renderAll();
    };

    bgControls.appendChild(bgColor);
    bgControls.appendChild(bgOpacity);

    const bgRow = document.createElement('div');
    bgRow.className = 'check-row';
    bgRow.appendChild(bgCheck);
    bgRow.appendChild(bgLabel);
    bgSection.appendChild(bgRow);
    bgSection.appendChild(bgControls);

    // ── Text Slice / Chop ──
    const sliceSection = document.createElement('div');
    sliceSection.style.cssText = 'margin-top:8px;padding-top:8px;border-top:1px solid var(--border);';

    if (!obj.data.sliceEffect) obj.data.sliceEffect = { enabled: false, count: 5, gap: 3 };
    const slice = obj.data.sliceEffect;

    const sliceCheck = document.createElement('input');
    sliceCheck.type = 'checkbox';
    sliceCheck.checked = slice.enabled;
    sliceCheck.id = 'slice-enable';
    const sliceLabel = document.createElement('label');
    sliceLabel.htmlFor = 'slice-enable';
    sliceLabel.className = 'checkbox-label';
    sliceLabel.textContent = ' Text Slice';
    sliceLabel.title = 'Slice the text into horizontal bands with gaps for a glitch/scan-line effect';

    const sliceControls = document.createElement('div');
    sliceControls.style.display = slice.enabled ? 'block' : 'none';
    sliceControls.style.marginTop = '4px';

    // Slice count
    const countLabel = document.createElement('label');
    countLabel.className = 'field-label';
    countLabel.textContent = 'Cuts';
    const countSlider = document.createElement('input');
    countSlider.type = 'range';
    countSlider.className = 'field-range';
    countSlider.min = 1; countSlider.max = 20;
    countSlider.value = slice.count;
    countSlider.title = 'Number of horizontal cuts through the text';

    // Gap size
    const gapLabel = document.createElement('label');
    gapLabel.className = 'field-label';
    gapLabel.textContent = 'Gap';
    const gapSlider = document.createElement('input');
    gapSlider.type = 'range';
    gapSlider.className = 'field-range';
    gapSlider.min = 1; gapSlider.max = 15;
    gapSlider.value = slice.gap;
    gapSlider.title = 'Width of the gaps between text slices in pixels';

    function updateSlice() {
      obj.data.sliceEffect.count = parseInt(countSlider.value);
      obj.data.sliceEffect.gap = parseInt(gapSlider.value);
      applySliceEffect(obj);
      fabricCanvas.renderAll();
    }

    sliceCheck.onchange = () => {
      obj.data.sliceEffect.enabled = sliceCheck.checked;
      sliceControls.style.display = sliceCheck.checked ? 'block' : 'none';
      if (sliceCheck.checked) {
        updateSlice();
      } else {
        removeSliceEffect(obj);
        fabricCanvas.renderAll();
      }
    };

    countSlider.oninput = updateSlice;
    gapSlider.oninput = updateSlice;

    sliceControls.appendChild(countLabel);
    sliceControls.appendChild(countSlider);
    sliceControls.appendChild(gapLabel);
    sliceControls.appendChild(gapSlider);

    const sliceRow = document.createElement('div');
    sliceRow.className = 'check-row';
    sliceRow.appendChild(sliceCheck);
    sliceRow.appendChild(sliceLabel);
    sliceSection.appendChild(sliceRow);
    sliceSection.appendChild(sliceControls);

    // ── Vertical Slice / Shatter ──
    if (!obj.data.vSliceEffect) obj.data.vSliceEffect = { enabled: false, count: 5, gap: 3, spread: 0, seed: 42 };
    const vSlice = obj.data.vSliceEffect;

    const vSliceCheck = document.createElement('input');
    vSliceCheck.type = 'checkbox';
    vSliceCheck.checked = vSlice.enabled;
    vSliceCheck.id = 'vslice-enable';
    const vSliceLabel = document.createElement('label');
    vSliceLabel.htmlFor = 'vslice-enable';
    vSliceLabel.className = 'checkbox-label';
    vSliceLabel.textContent = ' Vertical Slice';
    vSliceLabel.title = 'Slice the text into vertical columns with gaps';

    const vSliceControls = document.createElement('div');
    vSliceControls.style.display = vSlice.enabled ? 'block' : 'none';
    vSliceControls.style.marginTop = '4px';

    const vCountLabel = document.createElement('label');
    vCountLabel.className = 'field-label';
    vCountLabel.textContent = 'Cuts';
    const vCountSlider = document.createElement('input');
    vCountSlider.type = 'range'; vCountSlider.className = 'field-range';
    vCountSlider.min = 1; vCountSlider.max = 20; vCountSlider.value = vSlice.count;
    vCountSlider.title = 'Number of vertical cuts';

    const vGapLabel = document.createElement('label');
    vGapLabel.className = 'field-label';
    vGapLabel.textContent = 'Gap';
    const vGapSlider = document.createElement('input');
    vGapSlider.type = 'range'; vGapSlider.className = 'field-range';
    vGapSlider.min = 1; vGapSlider.max = 15; vGapSlider.value = vSlice.gap;
    vGapSlider.title = 'Width of gaps between vertical slices';

    const spreadLabel = document.createElement('label');
    spreadLabel.className = 'field-label';
    spreadLabel.textContent = 'Spread (Shatter)';
    const spreadSlider = document.createElement('input');
    spreadSlider.type = 'range'; spreadSlider.className = 'field-range';
    spreadSlider.min = 0; spreadSlider.max = 20; spreadSlider.value = vSlice.spread;
    spreadSlider.title = 'Random vertical offset per column — 0 is clean, higher values create a shatter/explode effect';

    const reshatterBtn = document.createElement('button');
    reshatterBtn.className = 'btn-small';
    reshatterBtn.textContent = 'Reshatter';
    reshatterBtn.style.cssText = 'margin-top:4px;width:100%;';
    reshatterBtn.title = 'Randomize the shatter pattern (new seed)';

    function updateVSlice() {
      obj.data.vSliceEffect.count = parseInt(vCountSlider.value);
      obj.data.vSliceEffect.gap = parseInt(vGapSlider.value);
      obj.data.vSliceEffect.spread = parseInt(spreadSlider.value);
      applySliceEffect(obj);
      fabricCanvas.renderAll();
    }

    vSliceCheck.onchange = () => {
      obj.data.vSliceEffect.enabled = vSliceCheck.checked;
      vSliceControls.style.display = vSliceCheck.checked ? 'block' : 'none';
      if (vSliceCheck.checked) {
        updateVSlice();
      } else {
        applySliceEffect(obj); // re-evaluate — horizontal may still be on
        fabricCanvas.renderAll();
      }
    };

    vCountSlider.oninput = updateVSlice;
    vGapSlider.oninput = updateVSlice;
    spreadSlider.oninput = updateVSlice;
    reshatterBtn.onclick = () => {
      obj.data.vSliceEffect.seed = Math.floor(Math.random() * 100000);
      updateVSlice();
    };

    vSliceControls.appendChild(vCountLabel);
    vSliceControls.appendChild(vCountSlider);
    vSliceControls.appendChild(vGapLabel);
    vSliceControls.appendChild(vGapSlider);
    vSliceControls.appendChild(spreadLabel);
    vSliceControls.appendChild(spreadSlider);
    vSliceControls.appendChild(reshatterBtn);

    const vSliceRow = document.createElement('div');
    vSliceRow.className = 'check-row';
    vSliceRow.appendChild(vSliceCheck);
    vSliceRow.appendChild(vSliceLabel);
    sliceSection.appendChild(vSliceRow);
    sliceSection.appendChild(vSliceControls);

    // ── FX Lock ──
    const lockSection = document.createElement('div');
    lockSection.style.cssText = 'margin-top:8px;padding-top:8px;border-top:1px solid var(--border);';

    const lockCheck = document.createElement('input');
    lockCheck.type = 'checkbox';
    lockCheck.checked = obj.data.fxLocked || false;
    lockCheck.id = 'fx-lock';
    const lockLabel = document.createElement('label');
    lockLabel.htmlFor = 'fx-lock';
    lockLabel.className = 'checkbox-label';
    lockLabel.textContent = ' Lock from Algo FX';
    lockLabel.title = 'Prevent mangle and algorithmic effects from modifying this object';
    lockCheck.onchange = () => {
      obj.data.fxLocked = lockCheck.checked;
      logAction(lockCheck.checked ? `Locked ${obj.data.id} from FX` : `Unlocked ${obj.data.id}`);
    };

    const lockRow = document.createElement('div');
    lockRow.className = 'check-row';
    lockRow.appendChild(lockCheck);
    lockRow.appendChild(lockLabel);
    lockSection.appendChild(lockRow);

    // ── Hide button ──
    const hideBtn = document.createElement('button');
    hideBtn.className = 'btn-small';
    hideBtn.textContent = 'Hide Object';
    hideBtn.style.marginTop = '8px';
    hideBtn.style.width = '100%';
    hideBtn.title = 'Hide this object from view (restore from Hidden Objects section below)';
    hideBtn.onclick = () => {
      if (obj.data?.id) {
        hiddenObjects.add(obj.data.id);
        obj.set('visible', false);
        fabricCanvas.discardActiveObject();
        fabricCanvas.renderAll();
        buildSidebar();
      }
    };

    panel.appendChild(contentLabel);
    panel.appendChild(contentInput);
    panel.appendChild(fontLabel);
    panel.appendChild(fontSelect);
    panel.appendChild(sizeLabel);
    panel.appendChild(sizeInput);
    panel.appendChild(styleLabel);
    panel.appendChild(styleRow);
    panel.appendChild(alignLabel);
    panel.appendChild(alignRow);
    panel.appendChild(colorLabel);
    panel.appendChild(colorInput);
    panel.appendChild(bgSection);
    panel.appendChild(sliceSection);
    panel.appendChild(lockSection);
    panel.appendChild(hideBtn);

    // Delete button — only for user-created text objects
    if (obj.data?.userCreated) {
      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'btn-small';
      deleteBtn.textContent = 'Delete Object';
      deleteBtn.style.cssText = 'margin-top:4px;width:100%;color:#ff4444;border-color:rgba(255,68,68,0.3);';
      deleteBtn.title = 'Permanently delete this user-created text object';
      deleteBtn.onclick = () => {
        removeUserTextObject(obj.data.id);
        fabricCanvas.discardActiveObject();
        clearObjectPanel();
        buildSidebar();
      };
      panel.appendChild(deleteBtn);
    }
  }

  // ── Blend layer selected ──
  if (obj.data?.stencilType === 'blendLayer') {
    const idx = obj.data.index;
    const layer = currentState.blendLayers?.[idx];
    if (!layer) return;

    const info = document.createElement('div');
    info.className = 'hint';
    info.textContent = `Blend Layer ${idx + 1} — drag to reposition, resize with handles`;
    info.style.marginBottom = '8px';
    panel.appendChild(info);

    // Blend mode
    const modeLabel = document.createElement('label');
    modeLabel.className = 'field-label';
    modeLabel.textContent = 'Blend Mode';
    const modeSelect = document.createElement('select');
    modeSelect.className = 'field-select';
    for (const mode of BLEND_MODES) {
      const opt = document.createElement('option');
      opt.value = mode;
      opt.textContent = mode;
      opt.selected = layer.blendMode === mode;
      modeSelect.appendChild(opt);
    }
    modeSelect.onchange = () => { updateBlendLayerProp(idx, 'blendMode', modeSelect.value); };

    const colorLabel = document.createElement('label');
    colorLabel.className = 'field-label';
    colorLabel.textContent = 'Color';
    const colorInput = document.createElement('input');
    colorInput.type = 'color';
    colorInput.className = 'field-color';
    colorInput.value = layer.color;
    colorInput.oninput = () => updateBlendLayerProp(idx, 'color', colorInput.value);

    const opLabel = document.createElement('label');
    opLabel.className = 'field-label';
    opLabel.textContent = 'Opacity';
    const opSlider = document.createElement('input');
    opSlider.type = 'range';
    opSlider.className = 'field-range';
    opSlider.min = 0; opSlider.max = 100;
    opSlider.value = Math.round(layer.opacity * 100);
    opSlider.oninput = () => updateBlendLayerProp(idx, 'opacity', parseInt(opSlider.value) / 100);

    panel.appendChild(modeLabel);
    panel.appendChild(modeSelect);
    panel.appendChild(colorLabel);
    panel.appendChild(colorInput);
    panel.appendChild(opLabel);
    panel.appendChild(opSlider);
  }

  // ── Generic object controls (non-text, non-blend) ──
  if (obj.type !== 'textbox' && obj.data?.stencilType !== 'blendLayer') {
    // Position controls
    const posLabel = document.createElement('label');
    posLabel.className = 'field-label';
    posLabel.textContent = 'Position';
    posLabel.title = 'X and Y coordinates of this object on the canvas';
    const posRow = document.createElement('div');
    posRow.style.cssText = 'display:flex;gap:6px;';

    const xInput = document.createElement('input');
    xInput.type = 'number';
    xInput.className = 'field-input';
    xInput.style.flex = '1';
    xInput.value = Math.round(obj.left);
    xInput.title = 'X position (horizontal)';
    xInput.oninput = () => { obj.set('left', parseInt(xInput.value) || 0); fabricCanvas.renderAll(); };

    const yInput = document.createElement('input');
    yInput.type = 'number';
    yInput.className = 'field-input';
    yInput.style.flex = '1';
    yInput.value = Math.round(obj.top);
    yInput.title = 'Y position (vertical)';
    yInput.oninput = () => { obj.set('top', parseInt(yInput.value) || 0); fabricCanvas.renderAll(); };

    posRow.appendChild(xInput);
    posRow.appendChild(yInput);
    panel.appendChild(posLabel);
    panel.appendChild(posRow);

    // Size controls
    const dimLabel = document.createElement('label');
    dimLabel.className = 'field-label';
    dimLabel.textContent = 'Size';
    dimLabel.title = 'Width and height of this object in pixels';
    const dimRow = document.createElement('div');
    dimRow.style.cssText = 'display:flex;gap:6px;';

    const wInput = document.createElement('input');
    wInput.type = 'number';
    wInput.className = 'field-input';
    wInput.style.flex = '1';
    wInput.value = Math.round(obj.width * (obj.scaleX || 1));
    wInput.title = 'Width';
    wInput.oninput = () => {
      const newW = parseInt(wInput.value) || 1;
      obj.set({ width: newW, scaleX: 1 });
      fabricCanvas.renderAll();
    };

    const hInput = document.createElement('input');
    hInput.type = 'number';
    hInput.className = 'field-input';
    hInput.style.flex = '1';
    hInput.value = Math.round(obj.height * (obj.scaleY || 1));
    hInput.title = 'Height';
    hInput.oninput = () => {
      const newH = parseInt(hInput.value) || 1;
      obj.set({ height: newH, scaleY: 1 });
      fabricCanvas.renderAll();
    };

    dimRow.appendChild(wInput);
    dimRow.appendChild(hInput);
    panel.appendChild(dimLabel);
    panel.appendChild(dimRow);

    // Opacity
    const opLabel = document.createElement('label');
    opLabel.className = 'field-label';
    opLabel.textContent = 'Opacity';
    opLabel.title = 'Transparency of this object (0 = invisible, 100 = fully opaque)';
    const opSlider = document.createElement('input');
    opSlider.type = 'range';
    opSlider.className = 'field-range';
    opSlider.min = 0; opSlider.max = 100;
    opSlider.value = Math.round((obj.opacity ?? 1) * 100);
    opSlider.title = 'Drag to adjust object transparency';
    opSlider.oninput = () => {
      obj.set('opacity', parseInt(opSlider.value) / 100);
      fabricCanvas.renderAll();
    };
    panel.appendChild(opLabel);
    panel.appendChild(opSlider);

    // Fill color (if applicable)
    if (obj.fill && typeof obj.fill === 'string') {
      const fillLabel = document.createElement('label');
      fillLabel.className = 'field-label';
      fillLabel.textContent = 'Fill Color';
      fillLabel.title = 'The fill color of this shape';
      const fillInput = document.createElement('input');
      fillInput.type = 'color';
      fillInput.className = 'field-color';
      fillInput.value = obj.fill;
      fillInput.title = 'Click to change fill color';
      fillInput.oninput = () => {
        obj.set('fill', fillInput.value);
        fabricCanvas.renderAll();
      };
      panel.appendChild(fillLabel);
      panel.appendChild(fillInput);
    }

    // Angle
    const angleLabel = document.createElement('label');
    angleLabel.className = 'field-label';
    angleLabel.textContent = 'Rotation';
    angleLabel.title = 'Rotation angle in degrees (0-360)';
    const angleInput = document.createElement('input');
    angleInput.type = 'number';
    angleInput.className = 'field-input';
    angleInput.value = Math.round(obj.angle || 0);
    angleInput.min = 0; angleInput.max = 360;
    angleInput.title = 'Rotation angle in degrees';
    angleInput.oninput = () => {
      obj.set('angle', parseInt(angleInput.value) || 0);
      fabricCanvas.renderAll();
    };
    panel.appendChild(angleLabel);
    panel.appendChild(angleInput);

    // FX Lock (same as text objects)
    const lockSection = document.createElement('div');
    lockSection.style.cssText = 'margin-top:8px;padding-top:8px;border-top:1px solid var(--border);';
    const lockCheck = document.createElement('input');
    lockCheck.type = 'checkbox';
    lockCheck.checked = obj.data.fxLocked || false;
    lockCheck.id = 'fx-lock-shape';
    const lockLabel = document.createElement('label');
    lockLabel.htmlFor = 'fx-lock-shape';
    lockLabel.className = 'checkbox-label';
    lockLabel.textContent = ' Lock from Algo FX';
    lockLabel.title = 'Prevent mangle and algorithmic effects from modifying this object';
    lockCheck.onchange = () => {
      obj.data.fxLocked = lockCheck.checked;
      logAction(lockCheck.checked ? `Locked ${obj.data.id} from FX` : `Unlocked ${obj.data.id}`);
    };
    const lockRow = document.createElement('div');
    lockRow.className = 'check-row';
    lockRow.appendChild(lockCheck);
    lockRow.appendChild(lockLabel);
    lockSection.appendChild(lockRow);
    panel.appendChild(lockSection);

    // Hide button
    const hideBtn = document.createElement('button');
    hideBtn.className = 'btn-small';
    hideBtn.textContent = 'Hide Object';
    hideBtn.style.cssText = 'margin-top:8px;width:100%;';
    hideBtn.title = 'Hide this object from view (restore from Hidden Objects section)';
    hideBtn.onclick = () => {
      if (obj.data?.id) {
        hiddenObjects.add(obj.data.id);
        obj.set('visible', false);
        fabricCanvas.discardActiveObject();
        fabricCanvas.renderAll();
        buildSidebar();
      }
    };
    panel.appendChild(hideBtn);
  }
}

function clearObjectPanel() {
  const panel = document.getElementById('object-properties');
  if (panel) panel.innerHTML = '<div class="hint">Click an object on the canvas to edit its properties</div>';
}

// --- Accessor API (for cross-module access) ---

export function getCanvas() { return fabricCanvas; }
export function getState() { return currentState; }
export function getSize() { return currentSize; }

/**
 * Apply a state snapshot to the editor, restoring the full design.
 */
export function applySnapshotToEditor(snapshot) {
  currentState.template = snapshot.template;
  currentState.scheme = { ...snapshot.scheme };
  currentState.overlays = JSON.parse(JSON.stringify(snapshot.overlays));
  currentState.watermark = snapshot.watermark;
  currentState.eventDateISO = snapshot.eventDateISO || '';
  currentState.blendLayers = snapshot.blendLayers ? JSON.parse(JSON.stringify(snapshot.blendLayers)) : [];
  currentSize = snapshot.size;

  // Restore mangle params if present
  if (snapshot.mangleParams) {
    currentState._mangleParams = snapshot.mangleParams;
  }

  // Restore wordmark settings if present
  if (snapshot.wordmark) {
    currentState._wordmarkText = snapshot.wordmark.text;
    currentState._wordmarkFont = snapshot.wordmark.font;
    currentState._wordmarkColor = snapshot.wordmark.color;
    currentState._wordmarkBold = snapshot.wordmark.bold;
    currentState._wordmarkItalic = snapshot.wordmark.italic;
    currentState._wordmarkUppercase = snapshot.wordmark.uppercase;
    currentState._wordmarkAccent = snapshot.wordmark.accent;
  }

  // Restore user-created text objects if present
  if (snapshot.userTextObjects) {
    userTextObjects = JSON.parse(JSON.stringify(snapshot.userTextObjects));
  }

  // Extract text edits from the snapshot's serialized objects
  // so hydrateTemplate uses the snapshot's text, not the current canvas text
  const textEditsFromSnapshot = {};
  if (snapshot.objects) {
    for (const obj of snapshot.objects) {
      if (obj.content && obj.id) {
        textEditsFromSnapshot[obj.id] = obj.content;
      }
    }
  }

  const template = TEMPLATES[snapshot.template];
  if (template) {
    hydrateTemplate(template, snapshot.scheme, snapshot.size, textEditsFromSnapshot);
  }

  // Restore font settings, positions, and styles from the snapshot
  // hydrateTemplate rebuilds from template defaults — this overrides with saved state
  if (snapshot.objects) {
    const canvasW = fabricCanvas.width;
    const canvasH = fabricCanvas.height;
    for (const saved of snapshot.objects) {
      const obj = fabricCanvas.getObjects().find(o => o.data?.id === saved.id);
      if (!obj || obj.type !== 'textbox') continue;

      const updates = {};
      if (saved.fontFamily) updates.fontFamily = saved.fontFamily;
      if (saved.fontSize) updates.fontSize = saved.fontSize;
      if (saved.fill) updates.fill = saved.fill;
      if (saved.fontWeight) updates.fontWeight = saved.fontWeight;
      if (saved.fontStyle) updates.fontStyle = saved.fontStyle;
      if (saved.underline != null) updates.underline = saved.underline;
      if (saved.textAlign) updates.textAlign = saved.textAlign;

      // Restore position
      if (saved.x != null) updates.left = saved.x;
      if (saved.y != null) updates.top = saved.y;
      if (saved.width) updates.width = saved.width;
      if (saved.angle != null) updates.angle = saved.angle;

      // Restore data flags
      if (obj.data) {
        if (saved.fxLocked != null) obj.data.fxLocked = saved.fxLocked;
        if (saved.textBg) obj.data.textBg = saved.textBg;
        if (saved.sliceEffect) obj.data.sliceEffect = saved.sliceEffect;
        if (saved.vSliceEffect) obj.data.vSliceEffect = saved.vSliceEffect;
      }

      obj.set(updates);
    }
    fabricCanvas.renderAll();
  }

  buildSidebar();
}

/**
 * Detect browser, OS, and device type from user agent.
 */
function detectEnvironment() {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('OPR/') || ua.includes('Opera/')) browser = 'Opera';
  else if (ua.includes('Chrome/') && !ua.includes('Edg/')) browser = 'Chrome';
  else if (ua.includes('Safari/') && !ua.includes('Chrome/')) browser = 'Safari';

  let os = 'Unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Linux') && !ua.includes('Android')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  else if (ua.includes('CrOS')) os = 'ChromeOS';

  let deviceType = 'desktop';
  if (/mobile|android|iphone|ipod/i.test(ua)) deviceType = 'mobile';
  else if (/ipad|tablet/i.test(ua)) deviceType = 'tablet';

  return { browser, os, deviceType };
}

/**
 * Feedback button — fixed bottom-right, opens a modal for bug reports / suggestions.
 * Sends feedback to /api/feedback with full environment context.
 * QA users get an additional "My Feedback" panel to track their submissions.
 */
function setupFeedbackButton() {
  const btn = document.createElement('button');
  btn.className = 'feedback-btn';
  btn.textContent = isQA() ? 'QA FEEDBACK' : 'FEEDBACK';
  document.body.appendChild(btn);

  btn.onclick = () => openFeedbackModal();

  // QA users also get a "My Reports" button
  if (isQA()) {
    const reportsBtn = document.createElement('button');
    reportsBtn.className = 'feedback-btn qa-reports-btn';
    reportsBtn.textContent = 'MY REPORTS';
    reportsBtn.style.right = '120px';
    document.body.appendChild(reportsBtn);
    reportsBtn.onclick = () => openQAReportsPanel();
  }
}

function openFeedbackModal() {
  const overlay = document.createElement('div');
  overlay.className = 'feedback-modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'feedback-modal';

  const title = document.createElement('h3');
  title.textContent = isQA() ? 'QA REPORT' : 'FEEDBACK';

  const typeSelect = document.createElement('select');
  typeSelect.className = 'field-select';
  typeSelect.style.marginBottom = '10px';
  for (const t of ['Bug Report', 'Feature Request', 'General Feedback']) {
    const opt = document.createElement('option');
    opt.value = t;
    opt.textContent = t;
    typeSelect.appendChild(opt);
  }

  const textarea = document.createElement('textarea');
  textarea.placeholder = isQA()
    ? 'Describe the issue in detail. Include steps to reproduce if possible...'
    : 'Describe the issue or suggestion...';

  // QA users see environment info preview
  const env = detectEnvironment();
  const envInfo = document.createElement('div');
  envInfo.className = 'hint';
  envInfo.style.marginTop = '8px';
  envInfo.style.fontSize = '10px';
  envInfo.style.lineHeight = '1.6';
  envInfo.innerHTML = `<span style="color:var(--accent)">ENV:</span> ${env.browser} / ${env.os} / ${env.deviceType} / ${window.innerWidth}x${window.innerHeight}`
    + `<br><span style="color:var(--accent)">CTX:</span> ${currentState?.template || 'none'} / ${currentSize} / ${currentState?.scheme?.name || 'none'}`;

  const actions = document.createElement('div');
  actions.className = 'feedback-modal-actions';

  const cancelBtn = document.createElement('button');
  cancelBtn.textContent = 'Cancel';
  cancelBtn.onclick = () => overlay.remove();

  const submitBtn = document.createElement('button');
  submitBtn.className = 'submit-btn';
  submitBtn.textContent = 'Submit';
  submitBtn.onclick = async () => {
    const message = textarea.value.trim();
    if (!message) { textarea.style.borderColor = '#ef4444'; return; }

    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';

    const payload = {
      type: typeSelect.value,
      message,
      browser: env.browser,
      deviceType: env.deviceType,
      os: env.os,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      currentTemplate: currentState?.template || null,
      currentSize: currentSize,
      currentScheme: currentState?.scheme?.name || null,
      userAgent: navigator.userAgent,
      appVersion: document.querySelector('meta[name="version"]')?.content || null,
    };

    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Failed');

      modal.innerHTML = '';
      const thanks = document.createElement('h3');
      thanks.textContent = 'THANK YOU';
      thanks.style.color = '#22c55e';
      const msg = document.createElement('div');
      msg.className = 'hint';
      msg.textContent = isQA()
        ? 'Report submitted. Track it in MY REPORTS.'
        : 'Your feedback has been recorded.';
      msg.style.marginTop = '8px';
      modal.appendChild(thanks);
      modal.appendChild(msg);
      setTimeout(() => overlay.remove(), 1800);
    } catch (err) {
      // Fallback to localStorage if API fails
      const existing = JSON.parse(localStorage.getItem('stencil-feedback') || '[]');
      existing.push({ ...payload, timestamp: new Date().toISOString() });
      localStorage.setItem('stencil-feedback', JSON.stringify(existing));

      modal.innerHTML = '';
      const saved = document.createElement('h3');
      saved.textContent = 'SAVED LOCALLY';
      const msg = document.createElement('div');
      msg.className = 'hint';
      msg.textContent = 'Could not reach server. Feedback saved to your browser.';
      msg.style.marginTop = '8px';
      modal.appendChild(saved);
      modal.appendChild(msg);
      setTimeout(() => overlay.remove(), 2000);
    }
  };

  actions.appendChild(cancelBtn);
  actions.appendChild(submitBtn);

  modal.appendChild(title);
  modal.appendChild(typeSelect);
  modal.appendChild(textarea);
  modal.appendChild(envInfo);
  modal.appendChild(actions);
  overlay.appendChild(modal);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });

  document.body.appendChild(overlay);
  textarea.focus();
}

/**
 * QA Reports Panel — shows all feedback submitted by this QA user
 * with real-time status updates (read/unread/important/in-progress/complete).
 */
function openQAReportsPanel() {
  const overlay = document.createElement('div');
  overlay.className = 'feedback-modal-overlay';

  const panel = document.createElement('div');
  panel.className = 'feedback-modal qa-reports-panel';
  panel.style.maxWidth = '640px';
  panel.style.maxHeight = '80vh';
  panel.style.overflowY = 'auto';

  const title = document.createElement('h3');
  title.textContent = 'MY REPORTS';
  panel.appendChild(title);

  const list = document.createElement('div');
  list.innerHTML = '<div class="hint" style="text-align:center;padding:20px;">Loading...</div>';
  panel.appendChild(list);

  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'Close';
  closeBtn.style.marginTop = '12px';
  closeBtn.onclick = () => overlay.remove();
  panel.appendChild(closeBtn);

  overlay.appendChild(panel);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  document.body.appendChild(overlay);

  // Fetch reports
  fetch('/api/feedback/mine', { credentials: 'include' })
    .then(r => r.json())
    .then(data => {
      if (!data.feedback?.length) {
        list.innerHTML = '<div class="hint" style="text-align:center;padding:20px;">No reports yet</div>';
        return;
      }
      list.innerHTML = '';
      for (const fb of data.feedback) {
        const card = document.createElement('div');
        card.style.cssText = 'background:#1a1a1a;border:1px solid #262626;border-radius:4px;padding:12px;margin-bottom:8px;';

        const statusColor = {
          unread: '#888', read: '#60a5fa', in_progress: '#eab308', complete: '#22c55e',
        }[fb.status] || '#888';

        const badges = [];
        badges.push(`<span style="color:${statusColor};font-size:10px;letter-spacing:1px;text-transform:uppercase;">${fb.status.replace('_', ' ')}</span>`);
        if (fb.is_important) badges.push('<span style="color:#ef4444;font-size:10px;">IMPORTANT</span>');
        if (fb.is_in_progress) badges.push('<span style="color:#eab308;font-size:10px;">IN PROGRESS</span>');

        const date = new Date(fb.created_at * 1000);
        const dateStr = date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        card.innerHTML = `
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
            <span style="font-size:11px;color:var(--accent);letter-spacing:1px;">${fb.type.toUpperCase()}</span>
            <span style="font-size:10px;color:#666;">${dateStr}</span>
          </div>
          <div style="font-size:13px;line-height:1.5;margin-bottom:8px;">${fb.message.length > 200 ? fb.message.slice(0, 200) + '...' : fb.message}</div>
          <div style="display:flex;gap:8px;align-items:center;">${badges.join(' ')}</div>
          <div style="font-size:9px;color:#555;margin-top:4px;">${fb.device_type || ''} / ${fb.browser || ''} / ${fb.current_template || ''} / ${fb.current_size || ''}</div>
        `;
        list.appendChild(card);
      }
    })
    .catch(() => {
      list.innerHTML = '<div class="hint" style="text-align:center;padding:20px;color:#ef4444;">Failed to load reports</div>';
    });
}

/**
 * Auth UI — header login button (Option A) + dismissable banner (Option C).
 *
 * - Anonymous users see: SIGN IN button in header + banner below header
 * - Authenticated users see: avatar dropdown in header, no banner
 * - Banner can be dismissed with X (persisted in sessionStorage)
 */
function setupAuthUI() {
  const signinBtn = document.getElementById('signin-btn');
  const userMenu = document.getElementById('user-menu');
  const userMenuBtn = document.getElementById('user-menu-btn');
  const userDropdown = document.getElementById('user-dropdown');
  const banner = document.getElementById('auth-banner');
  const bannerSignin = document.getElementById('banner-signin');
  const bannerDismiss = document.getElementById('banner-dismiss');

  const campaignsBtn = document.getElementById('campaigns-btn');
  const viewPageBtn = document.getElementById('view-page-btn');

  const user = getUser();

  if (user) {
    // --- Logged in state ---
    if (signinBtn) signinBtn.style.display = 'none';
    if (banner) banner.style.display = 'none';

    // Show Campaigns and View Page for pro/ultra users
    if (isPro() || isUltra()) {
      if (campaignsBtn) {
        campaignsBtn.style.display = '';
        campaignsBtn.onclick = () => openCampaignManager();
      }
      if (viewPageBtn) {
        viewPageBtn.style.display = '';
        viewPageBtn.onclick = () => {
          const sub = user.subdomain;
          if (sub) {
            window.open(`https://${sub}.stencil.cloud`, '_blank');
          } else {
            alert('No subdomain configured. Set one in your account settings.');
          }
        };
      }
    }

    if (userMenu && userMenuBtn) {
      userMenu.style.display = 'block';
      // Avatar initial
      const initial = (user.displayName || user.email || '?')[0].toUpperCase();
      userMenuBtn.textContent = initial;
      if (user.avatarUrl) {
        userMenuBtn.style.backgroundImage = `url(${user.avatarUrl})`;
        userMenuBtn.style.backgroundSize = 'cover';
        userMenuBtn.textContent = '';
      }

      userMenuBtn.onclick = (e) => {
        e.stopPropagation();
        const open = userDropdown.style.display !== 'none';
        if (open) {
          userDropdown.style.display = 'none';
          return;
        }

        const rect = userMenuBtn.getBoundingClientRect();
        userDropdown.style.top = (rect.bottom + 4) + 'px';
        userDropdown.style.right = (window.innerWidth - rect.right) + 'px';
        userDropdown.style.left = 'auto';
        userDropdown.innerHTML = '';

        // User info
        const info = document.createElement('div');
        info.style.cssText = 'padding:10px 14px;border-bottom:1px solid var(--border);';
        const name = document.createElement('div');
        name.style.cssText = 'font-size:12px;color:#fff;font-family:"DM Mono",monospace;letter-spacing:0.5px;';
        name.textContent = user.displayName || user.email;
        info.appendChild(name);

        if (user.tier && user.tier !== 'free') {
          const badge = document.createElement('span');
          badge.className = `tier-badge tier-${user.tier}`;
          badge.textContent = user.tier.toUpperCase();
          name.appendChild(badge);
        }

        const sub = document.createElement('div');
        sub.style.cssText = 'font-size:9px;color:var(--text-dim);margin-top:3px;letter-spacing:0.5px;font-family:"DM Mono",monospace;';
        sub.textContent = `${user.subdomain}.stencil.cloud`;
        info.appendChild(sub);
        userDropdown.appendChild(info);

        // Menu items
        const items = [
          { label: 'MY DESIGNS', action: () => openMyDesignsModal() },
          { label: 'ANALYTICS', action: () => openAnalyticsModal() },
          { label: 'PAGE SETTINGS', action: () => openPageSettingsModal() },
          { label: 'ACCOUNT', action: () => openAccountModal() },
          { label: 'DESIGNER KIT', action: () => openDesignerKitModal() },
        ];

        // Special section — role-based
        if (user.realTier === 'qa' || isQA()) {
          const currentQA = getQATier();
          const label = currentQA === 'ultra' ? 'TESTING: ULTRA' : 'TESTING: FREE';
          items.push({ label, action: () => openQATierModal(), accent: true });
        }
        const adminId = user.id;
        // Admin link (only for admin users — server validates separately)
        if (user.tier === 'ultra' || user.realTier === 'qa') {
          items.push({ label: 'ADMIN', action: () => window.open('https://admin.stencil.cloud', '_blank') });
        }

        items.push({ label: 'SIGN OUT', action: () => logout() });

        for (const item of items) {
          const btn = document.createElement('button');
          btn.textContent = item.label;
          if (item.accent) btn.style.color = 'var(--accent)';
          btn.onclick = () => { userDropdown.style.display = 'none'; item.action(); };
          userDropdown.appendChild(btn);
        }

        userDropdown.style.display = 'block';

        const close = (ev) => {
          if (!userDropdown.contains(ev.target) && ev.target !== userMenuBtn) {
            userDropdown.style.display = 'none';
            document.removeEventListener('click', close);
          }
        };
        setTimeout(() => document.addEventListener('click', close), 0);
      };
    }
  } else {
    // --- Logged out state ---
    if (userMenu) userMenu.style.display = 'none';

    // Show banner unless dismissed this session
    if (banner && !sessionStorage.getItem('stencil-banner-dismissed')) {
      banner.style.display = 'flex';
    }

    // Sign in buttons (both header and banner) open the modal
    const openModal = () => showSigninModal();
    if (signinBtn) signinBtn.onclick = openModal;
    if (bannerSignin) bannerSignin.onclick = openModal;

    // Banner dismiss
    if (bannerDismiss) {
      bannerDismiss.onclick = () => {
        banner.style.display = 'none';
        sessionStorage.setItem('stencil-banner-dismissed', '1');
      };
    }
  }
}

/**
 * Show the sign-in/register modal.
 */
function showSigninModal() {
  // Remove any existing modal
  document.querySelector('.signin-modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'signin-modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'signin-modal';

  let isRegister = false;

  function render() {
    modal.innerHTML = '';

    const title = document.createElement('h2');
    title.textContent = isRegister ? 'CREATE ACCOUNT' : 'SIGN IN';
    modal.appendChild(title);

    // Google OAuth button
    const googleBtn = document.createElement('button');
    googleBtn.className = 'social-btn';
    googleBtn.textContent = 'Continue with Google';
    googleBtn.onclick = () => loginWithGoogle();
    modal.appendChild(googleBtn);

    // Divider
    const divider = document.createElement('div');
    divider.className = 'divider';
    divider.textContent = 'OR';
    modal.appendChild(divider);

    // Email field
    const emailField = document.createElement('div');
    emailField.className = 'field';
    const emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.placeholder = 'Email';
    emailInput.autocomplete = 'email';
    emailField.appendChild(emailInput);
    modal.appendChild(emailField);

    // Password field
    const passField = document.createElement('div');
    passField.className = 'field';
    const passInput = document.createElement('input');
    passInput.type = 'password';
    passInput.placeholder = 'Password';
    passInput.autocomplete = isRegister ? 'new-password' : 'current-password';
    passField.appendChild(passInput);
    modal.appendChild(passField);

    // Display name (register only)
    let nameInput = null;
    if (isRegister) {
      const nameField = document.createElement('div');
      nameField.className = 'field';
      nameInput = document.createElement('input');
      nameInput.type = 'text';
      nameInput.placeholder = 'Display name (optional)';
      nameField.appendChild(nameInput);
      modal.appendChild(nameField);
    }

    // Turnstile widget
    const turnstileDiv = document.createElement('div');
    turnstileDiv.id = 'turnstile-widget';
    turnstileDiv.style.marginTop = '10px';
    modal.appendChild(turnstileDiv);

    // Error/success message
    const msgEl = document.createElement('div');
    modal.appendChild(msgEl);

    // Submit button
    const submitBtn = document.createElement('button');
    submitBtn.className = 'submit-btn';
    submitBtn.textContent = isRegister ? 'CREATE ACCOUNT' : 'SIGN IN';
    submitBtn.onclick = async () => {
      const email = emailInput.value.trim();
      const password = passInput.value;
      const turnstileToken = window.turnstile?.getResponse?.('#turnstile-widget') || '';

      if (!email || !password) {
        msgEl.className = 'error-msg';
        msgEl.textContent = 'Email and password are required';
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = 'LOADING...';

      let result;
      if (isRegister) {
        result = await registerWithEmail(email, password, nameInput?.value?.trim(), turnstileToken);
      } else {
        result = await loginWithEmail(email, password, turnstileToken);
      }

      if (result.ok) {
        if (isRegister) {
          // Auto-login on registration — reload to reflect auth state
          overlay.remove();
          window.location.reload();
        } else {
          overlay.remove();
          window.location.reload();
        }
      } else {
        msgEl.className = 'error-msg';
        msgEl.textContent = result.error || 'Something went wrong';
        submitBtn.disabled = false;
        submitBtn.textContent = isRegister ? 'CREATE ACCOUNT' : 'SIGN IN';
        // Reset turnstile
        window.turnstile?.reset?.('#turnstile-widget');
      }
    };
    modal.appendChild(submitBtn);

    // Toggle register/login
    const toggleBtn = document.createElement('button');
    toggleBtn.className = 'toggle-mode';
    toggleBtn.textContent = isRegister
      ? 'Already have an account? Sign in'
      : 'No account? Create one';
    toggleBtn.onclick = () => { isRegister = !isRegister; render(); };
    modal.appendChild(toggleBtn);

    // Render Turnstile widget (if API is loaded and a real site key is configured)
    // Using Cloudflare's always-pass test key for development.
    // Replace with your actual Turnstile site key in production.
    const TURNSTILE_SITE_KEY = '0x4AAAAAAC1ko6TrSIFbFF89wyuRU_vkiiU';
    if (window.turnstile) {
      setTimeout(() => {
        try {
          window.turnstile.render('#turnstile-widget', {
            sitekey: TURNSTILE_SITE_KEY,
            theme: 'dark',
            size: 'flexible',
          });
        } catch (e) {
          console.warn('Turnstile render failed:', e);
        }
      }, 50);
    }
  }

  render();
  overlay.appendChild(modal);

  // Close on overlay click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });

  // Close on Escape
  const escHandler = (e) => {
    if (e.key === 'Escape') { overlay.remove(); document.removeEventListener('keydown', escHandler); }
  };
  document.addEventListener('keydown', escHandler);

  document.body.appendChild(overlay);
}

/**
 * Show upgrade modal with pricing tiers.
 */
function showUpgradeModal() {
  document.querySelector('.signin-modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'signin-modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'signin-modal';
  modal.style.minWidth = '500px';
  modal.style.maxWidth = '560px';

  const title = document.createElement('h2');
  title.textContent = 'UPGRADE';
  modal.appendChild(title);

  const tiers = [
    {
      name: 'DESIGNER KIT',
      plans: [
        { key: 'designer-monthly', label: '$5 / MONTH' },
        { key: 'designer-perpetual', label: '$49 LIFETIME', badge: 'BEST VALUE' },
      ],
      features: ['50+ line art patterns', 'Film grain', 'Mangle layout', 'Batch export', 'No watermark'],
    },
    {
      name: 'ULTRA KIT',
      plans: [
        { key: 'ultra-monthly', label: '$8 / MONTH' },
        { key: 'ultra-perpetual', label: '$89 LIFETIME', badge: 'BEST VALUE' },
      ],
      features: ['Everything in Designer', 'Scheduler', 'Campaign pages', 'AI Designer', 'Custom subdomains (x3)'],
    },
  ];

  for (const tier of tiers) {
    const card = document.createElement('div');
    card.style.cssText = 'border:1px solid var(--border);padding:16px;margin-bottom:12px;';

    const tierName = document.createElement('div');
    tierName.style.cssText = 'font-size:13px;color:#fff;font-family:"DM Mono",monospace;letter-spacing:2px;margin-bottom:10px;';
    tierName.textContent = tier.name;
    card.appendChild(tierName);

    const features = document.createElement('div');
    features.style.cssText = 'font-size:10px;color:var(--text-dim);margin-bottom:12px;line-height:1.6;font-family:"DM Mono",monospace;';
    features.textContent = tier.features.join(' \u00b7 ');
    card.appendChild(features);

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;gap:8px;';

    for (const plan of tier.plans) {
      const btn = document.createElement('button');
      btn.style.cssText = `
        flex:1;padding:8px;border:1px solid var(--border);background:var(--bg);
        color:var(--text);font-family:"DM Mono",monospace;font-size:10px;
        letter-spacing:1px;cursor:pointer;transition:border-color 0.15s,color 0.15s;
        position:relative;
      `;
      btn.textContent = plan.label;
      btn.onmouseenter = () => { btn.style.borderColor = 'var(--accent)'; btn.style.color = 'var(--accent)'; };
      btn.onmouseleave = () => { btn.style.borderColor = 'var(--border)'; btn.style.color = 'var(--text)'; };
      btn.onclick = async () => {
        btn.textContent = 'LOADING...';
        try {
          const resp = await fetch('/api/subscription/checkout', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ plan: plan.key }),
          });
          const data = await resp.json();
          if (data.url) window.location.href = data.url;
          else { btn.textContent = data.error || 'Error'; }
        } catch (e) { btn.textContent = 'Error'; }
      };

      if (plan.badge) {
        const badge = document.createElement('span');
        badge.style.cssText = `
          position:absolute;top:-8px;right:-1px;background:var(--accent);color:#0a0a0a;
          font-size:8px;padding:2px 5px;letter-spacing:1px;font-weight:700;
        `;
        badge.textContent = plan.badge;
        btn.style.position = 'relative';
        btn.appendChild(badge);
      }

      btnRow.appendChild(btn);
    }

    card.appendChild(btnRow);
    modal.appendChild(card);
  }

  overlay.appendChild(modal);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
}

// ─────────────────────────────────────────────────
// SCHEDULER — Ultra Kit feature
// ─────────────────────────────────────────────────

/**
 * Render the current canvas to a PNG Blob (full resolution).
 * Reuses the same composite strategy as exportPNG but returns a Blob.
 */
function renderCanvasToBlob() {
  return new Promise((resolve, reject) => {
    const sizeInfo = TEMPLATE_SIZES[currentSize];
    const fullW = sizeInfo.width;
    const fullH = sizeInfo.height;
    const multiplier = 1 / displayScale;

    const bgUrl = _cachedBgDataUrl || renderBackground({
      width: fullW,
      height: fullH,
      scheme: getScheme(currentState.scheme.name || 'dark'),
      overlays: currentState.overlays,
      watermark: shouldShowWatermark(),
    });

    const savedBg = fabricCanvas.backgroundImage;
    fabricCanvas.backgroundImage = null;
    const objectsDataUrl = fabricCanvas.toDataURL({
      format: 'png',
      quality: 1,
      multiplier,
    });
    fabricCanvas.backgroundImage = savedBg;
    fabricCanvas.renderAll();

    const bgImg = new Image();
    bgImg.onload = () => {
      const offscreen = document.createElement('canvas');
      offscreen.width = fullW;
      offscreen.height = fullH;
      const ctx = offscreen.getContext('2d');
      ctx.drawImage(bgImg, 0, 0, fullW, fullH);

      const objImg = new Image();
      objImg.onload = () => {
        ctx.drawImage(objImg, 0, 0, fullW, fullH);
        offscreen.toBlob(blob => {
          blob ? resolve(blob) : reject(new Error('toBlob returned null'));
        }, 'image/png');
      };
      objImg.onerror = reject;
      objImg.src = objectsDataUrl;
    };
    bgImg.onerror = reject;
    bgImg.src = bgUrl;
  });
}

/**
 * Get the ISO date from the canvas date text object (if a date picker was used)
 * or from currentState.eventDateISO.
 */
function getEventDateISO() {
  return currentState.eventDateISO || '';
}

/**
 * Get the event title from the canvas (first text with role "title").
 */
function getEventTitle() {
  if (!fabricCanvas) return 'Untitled';
  const titleObj = fabricCanvas.getObjects().find(o => o.data?.role === 'title');
  return titleObj?.text || 'Untitled';
}

/**
 * Format an ISO date (YYYY-MM-DD) for display as MM.DD.YY
 */
function formatDateShort(iso) {
  if (!iso || !iso.includes('-')) return iso || '—';
  const [y, m, d] = iso.split('-');
  return `${m}.${d}.${y.slice(-2)}`;
}

/**
 * Relative time ago string.
 */
function timeAgo(isoStr) {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/**
 * Open the scheduler modal.
 */
function openScheduler() {
  // Remove existing
  document.querySelector('.scheduler-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'scheduler-overlay';

  const panel = document.createElement('div');
  panel.className = 'scheduler-panel';

  const title = document.createElement('h3');
  title.textContent = 'scheduler';
  panel.appendChild(title);

  // Status bar
  const statusEl = document.createElement('div');
  statusEl.className = 'scheduler-status';
  statusEl.textContent = 'loading…';
  panel.appendChild(statusEl);

  // Action buttons
  const actions = document.createElement('div');
  actions.className = 'scheduler-actions';

  const schedBtn = document.createElement('button');
  schedBtn.className = 'primary';
  schedBtn.textContent = 'schedule current design';
  schedBtn.onclick = () => scheduleCurrentDesign(body, statusEl);
  actions.appendChild(schedBtn);

  const pushBtn = document.createElement('button');
  pushBtn.textContent = 'push current live';
  pushBtn.onclick = () => pushCurrentLive(body, statusEl);
  actions.appendChild(pushBtn);

  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'close';
  closeBtn.style.marginLeft = 'auto';
  closeBtn.onclick = () => overlay.remove();
  actions.appendChild(closeBtn);

  panel.appendChild(actions);

  // Schedule list body
  const body = document.createElement('div');
  body.innerHTML = '<div class="scheduler-empty">loading schedule…</div>';
  panel.appendChild(body);

  overlay.appendChild(panel);

  // Close on backdrop click
  overlay.addEventListener('click', e => {
    if (e.target === overlay) overlay.remove();
  });

  // Close on Escape
  const escHandler = e => {
    if (e.key === 'Escape') { overlay.remove(); document.removeEventListener('keydown', escHandler); }
  };
  document.addEventListener('keydown', escHandler);

  document.body.appendChild(overlay);

  // Load data
  loadScheduleStatus(statusEl);
  loadScheduleList(body, statusEl);
}

/**
 * Load and display campaign + schedule status summary.
 */
async function loadScheduleStatus(statusEl) {
  try {
    const res = await fetch('/api/schedule/status', { credentials: 'include' });
    if (!res.ok) { statusEl.textContent = ''; return; }
    const s = await res.json();

    const parts = [];
    if (s.isManual && s.pushedAt) {
      parts.push(`<span class="dot green"></span> <b>live:</b> ${s.label || 'manual push'} <i>(manual, ${timeAgo(s.pushedAt)})</i>`);
    } else if (s.activeItem) {
      const src = s.source === 'cron' ? 'auto' : 'activated';
      parts.push(`<span class="dot green"></span> <b>live:</b> ${s.activeItem.title || s.activeItem.date || 'unknown'} <i>(${src}, ${timeAgo(s.pushedAt)})</i>`);
    } else if (s.pushedAt) {
      parts.push(`<span class="dot green"></span> <b>live:</b> ${s.label || 'unknown'} <i>(${timeAgo(s.pushedAt)})</i>`);
    } else {
      parts.push('<span class="dot grey"></span> no campaign live');
    }

    if (s.nextUp) {
      parts.push(`<span class="dot yellow"></span> <b>next:</b> ${s.nextUp.title || formatDateShort(s.nextUp.date)} on ${formatDateShort(s.nextUp.date)}`);
    }

    parts.push(`${s.upcomingCount} upcoming / ${s.totalScheduled} total`);
    statusEl.innerHTML = parts.join(' <span style="color:#333">|</span> ');
  } catch (e) {
    statusEl.innerHTML = '<span style="color:#666">could not load status</span>';
  }
}

/**
 * Load and render the schedule item list.
 */
async function loadScheduleList(body, statusEl) {
  try {
    const res = await fetch('/api/schedule', { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to load');
    const data = await res.json();
    const items = data.items || [];

    if (items.length === 0) {
      body.innerHTML = '<div class="scheduler-empty">no scheduled campaigns yet — pick an event date and click "schedule current design"</div>';
      return;
    }

    renderScheduleList(items, body, statusEl);
  } catch (e) {
    body.innerHTML = `<div class="scheduler-empty">could not load schedule — ${e.message}</div>`;
  }
}

/**
 * Render the list of scheduled items.
 */
function renderScheduleList(items, body, statusEl) {
  const todayStr = new Date().toISOString().split('T')[0];
  items.sort((a, b) => (a.event_date || '').localeCompare(b.event_date || ''));

  body.innerHTML = '';

  for (const item of items) {
    let badgeClass = 'upcoming';
    let badgeLabel = 'upcoming';

    if (item.status === 'active') { badgeClass = 'live'; badgeLabel = 'live'; }
    else if (item.status === 'expired') { badgeClass = 'expired'; badgeLabel = 'expired'; }
    else if (item.event_date < todayStr) { badgeClass = 'past'; badgeLabel = 'past'; }

    const row = document.createElement('div');
    row.className = 'sched-item';

    // Date
    const dateEl = document.createElement('div');
    dateEl.className = 'sched-item-date';
    dateEl.textContent = formatDateShort(item.event_date);
    row.appendChild(dateEl);

    // Info
    const info = document.createElement('div');
    info.className = 'sched-item-info';
    const titleEl = document.createElement('div');
    titleEl.className = 'title';
    titleEl.textContent = item.title || 'untitled';
    info.appendChild(titleEl);
    row.appendChild(info);

    // Status badge
    const badge = document.createElement('div');
    badge.className = `sched-item-badge ${badgeClass}`;
    badge.textContent = badgeLabel;
    row.appendChild(badge);

    // Action buttons
    const btns = document.createElement('div');
    btns.className = 'sched-item-actions';

    const pushLiveBtn = document.createElement('button');
    pushLiveBtn.textContent = 'push live';
    pushLiveBtn.onclick = () => activateScheduleItem(item.id, body, statusEl);
    btns.appendChild(pushLiveBtn);

    const delBtn = document.createElement('button');
    delBtn.className = 'del';
    delBtn.textContent = '×';
    delBtn.onclick = () => deleteScheduleItem(item.id, body, statusEl);
    btns.appendChild(delBtn);

    row.appendChild(btns);
    body.appendChild(row);
  }
}

/**
 * Schedule the current canvas design.
 * Uses the eventDateISO from the sidebar date picker.
 */
async function scheduleCurrentDesign(body, statusEl) {
  const isoDate = getEventDateISO();
  if (!isoDate) {
    alert('Set an event date first using the date picker in the sidebar.');
    return;
  }

  const title = getEventTitle();
  statusEl.innerHTML = '<span style="color:var(--accent)">rendering & uploading…</span>';

  try {
    const blob = await renderCanvasToBlob();
    const formData = new FormData();
    formData.append('image', blob, 'campaign.png');
    formData.append('state', JSON.stringify(currentState));
    formData.append('eventDate', isoDate);
    formData.append('title', title);

    const res = await fetch('/api/schedule', {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }

    // Refresh list and status
    await Promise.all([
      loadScheduleList(body, statusEl),
      loadScheduleStatus(statusEl),
    ]);
  } catch (e) {
    alert('Schedule failed: ' + e.message);
    statusEl.innerHTML = '<span style="color:var(--danger)">schedule failed</span>';
  }
}

/**
 * Push current design live immediately (manual campaign push).
 */
async function pushCurrentLive(body, statusEl) {
  statusEl.innerHTML = '<span style="color:var(--accent)">rendering & pushing live…</span>';

  try {
    const blob = await renderCanvasToBlob();
    const title = getEventTitle();
    const formData = new FormData();
    formData.append('image', blob, 'campaign.png');
    formData.append('state', JSON.stringify(currentState));
    formData.append('title', title);

    const res = await fetch('/api/campaign/push', {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }

    const data = await res.json();
    statusEl.innerHTML = `<span class="dot green"></span> <b>live!</b> <a href="${data.url}" target="_blank" style="color:var(--accent)">${data.url}</a>`;

    // Refresh list
    loadScheduleList(body, statusEl);
  } catch (e) {
    alert('Push failed: ' + e.message);
    statusEl.innerHTML = '<span style="color:var(--danger)">push failed</span>';
  }
}

/**
 * Manually activate a scheduled item (push live).
 */
async function activateScheduleItem(id, body, statusEl) {
  try {
    const res = await fetch(`/api/schedule/${id}/activate`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) throw new Error(await res.text());
    await Promise.all([
      loadScheduleList(body, statusEl),
      loadScheduleStatus(statusEl),
    ]);
  } catch (e) {
    alert('Activate failed: ' + e.message);
  }
}

/**
 * Delete a scheduled item.
 */
async function deleteScheduleItem(id, body, statusEl) {
  if (!confirm('Delete this scheduled campaign?')) return;
  try {
    const res = await fetch(`/api/schedule/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!res.ok) throw new Error(await res.text());
    await Promise.all([
      loadScheduleList(body, statusEl),
      loadScheduleStatus(statusEl),
    ]);
  } catch (e) {
    alert('Delete failed: ' + e.message);
  }
}

// ─────────────────────────────────────────────────
// DATE PICKER — sidebar control synced to canvas
// ─────────────────────────────────────────────────

/**
 * Date format presets for the canvas.
 * Each formatter takes a Date and returns an uppercase string.
 */
const DATE_FORMATS = [
  { id: 'short',    label: 'SAT APR 12',               fn: d => { const days=['SUN','MON','TUE','WED','THU','FRI','SAT']; const months=['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC']; return `${days[d.getDay()]} ${months[d.getMonth()]} ${d.getDate()}`; } },
  { id: 'full',     label: 'SATURDAY, APRIL 12, 2026',  fn: d => { const days=['SUNDAY','MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY']; const months=['JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER']; return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`; } },
  { id: 'compact',  label: 'APR 12 2026',               fn: d => { const months=['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC']; return `${months[d.getMonth()]} ${d.getDate()} ${d.getFullYear()}`; } },
  { id: 'numeric',  label: '04.12.2026',                fn: d => `${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}.${d.getFullYear()}` },
  { id: 'slash',    label: '04/12/2026',                fn: d => `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}` },
  { id: 'dayMonth', label: 'SAT APR 12 2026',           fn: d => { const days=['SUN','MON','TUE','WED','THU','FRI','SAT']; const months=['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC']; return `${days[d.getDay()]} ${months[d.getMonth()]} ${d.getDate()} ${d.getFullYear()}`; } },
];

/**
 * Build the event date picker for the sidebar.
 * Calendar input + format selector → updates the canvas text with role "date".
 * Also stores the ISO date for the scheduler auto-activation feature.
 */
function buildDatePickerControls() {
  const container = document.createElement('div');

  // Date format default
  if (!currentState.dateFormat) currentState.dateFormat = 'dayMonth';

  // --- Calendar input row ---
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;gap:6px;align-items:center;';

  const dateInput = document.createElement('input');
  dateInput.type = 'date';
  dateInput.className = 'field-input';
  dateInput.style.cssText = 'flex:1;background:var(--bg);border:1px solid var(--border);color:var(--text);padding:6px 8px;font-family:"DM Mono",monospace;font-size:11px;border-radius:3px;cursor:pointer;';
  dateInput.value = currentState.eventDateISO || '';
  dateInput.title = 'Select the event date — displayed on the design and used by the scheduler';

  // Today button
  const todayBtn = document.createElement('button');
  todayBtn.className = 'btn-small';
  todayBtn.textContent = 'Today';
  todayBtn.style.cssText = 'padding:4px 8px;font-size:10px;white-space:nowrap;';
  todayBtn.title = 'Set the event date to today';
  todayBtn.onclick = () => {
    const today = new Date();
    dateInput.value = today.toISOString().slice(0, 10);
    dateInput.dispatchEvent(new Event('change'));
  };

  row.appendChild(dateInput);
  row.appendChild(todayBtn);
  container.appendChild(row);

  // --- Format selector ---
  const formatRow = document.createElement('div');
  formatRow.style.cssText = 'display:flex;gap:4px;margin-top:6px;align-items:center;';

  const formatLabel = document.createElement('label');
  formatLabel.className = 'field-label';
  formatLabel.style.cssText = 'font-size:9px;margin:0;min-width:40px;';
  formatLabel.textContent = 'Format';

  const formatSelect = document.createElement('select');
  formatSelect.className = 'field-input';
  formatSelect.style.cssText = 'flex:1;background:var(--bg);border:1px solid var(--border);color:var(--text);padding:4px 6px;font-family:"DM Mono",monospace;font-size:10px;border-radius:3px;cursor:pointer;';
  formatSelect.title = "How the date is displayed on the design (e.g. 'APR 7' vs '04/07/2026')";
  for (const fmt of DATE_FORMATS) {
    const opt = document.createElement('option');
    opt.value = fmt.id;
    opt.textContent = fmt.label;
    if (fmt.id === currentState.dateFormat) opt.selected = true;
    formatSelect.appendChild(opt);
  }

  formatRow.appendChild(formatLabel);
  formatRow.appendChild(formatSelect);
  container.appendChild(formatRow);

  // --- Preview display ---
  const displayEl = document.createElement('div');
  displayEl.style.cssText = 'font-size:11px;color:var(--accent);margin-top:6px;letter-spacing:0.5px;font-weight:600;';

  /** Format the current date and update canvas + preview */
  function applyDate() {
    const val = dateInput.value;
    currentState.eventDateISO = val;
    currentState.dateFormat = formatSelect.value;

    if (!val) {
      displayEl.textContent = '';
      return;
    }

    const d = new Date(val + 'T12:00:00');
    const fmt = DATE_FORMATS.find(f => f.id === formatSelect.value) || DATE_FORMATS[5];
    const formatted = fmt.fn(d);

    // Update the canvas text object with role "date"
    if (fabricCanvas) {
      const dateObj = fabricCanvas.getObjects().find(o => o.data?.role === 'date');
      if (dateObj) {
        dateObj.set('text', formatted);
        fabricCanvas.renderAll();
        pushUndoState(fabricCanvas, currentState, currentSize);
      }
    }

    displayEl.textContent = formatted;
  }

  dateInput.onchange = applyDate;
  formatSelect.onchange = applyDate;

  // Initialize display if we have a saved date
  if (currentState.eventDateISO) {
    const d = new Date(currentState.eventDateISO + 'T12:00:00');
    const fmt = DATE_FORMATS.find(f => f.id === currentState.dateFormat) || DATE_FORMATS[5];
    displayEl.textContent = fmt.fn(d);
  }

  container.appendChild(displayEl);

  // --- Time input (optional, for scheduler precision) ---
  const timeRow = document.createElement('div');
  timeRow.style.cssText = 'display:flex;gap:6px;align-items:center;margin-top:6px;';

  const timeLabel = document.createElement('label');
  timeLabel.className = 'field-label';
  timeLabel.style.cssText = 'font-size:9px;margin:0;min-width:40px;';
  timeLabel.textContent = 'Time';

  const timeInput = document.createElement('input');
  timeInput.type = 'time';
  timeInput.className = 'field-input';
  timeInput.style.cssText = 'flex:1;background:var(--bg);border:1px solid var(--border);color:var(--text);padding:4px 6px;font-family:"DM Mono",monospace;font-size:10px;border-radius:3px;cursor:pointer;';
  timeInput.value = currentState.eventTime || '';
  timeInput.title = 'Event start time — displayed on the design if set';
  timeInput.onchange = () => {
    currentState.eventTime = timeInput.value;
  };

  timeRow.appendChild(timeLabel);
  timeRow.appendChild(timeInput);
  container.appendChild(timeRow);

  // Hint text
  const hint = document.createElement('div');
  hint.style.cssText = 'font-size:9px;color:var(--text-dim);margin-top:6px;opacity:0.6;';
  hint.textContent = 'Date syncs to canvas • Time used by scheduler';
  container.appendChild(hint);

  return container;
}

// ─────────────────────────────────────────────────
// EXPORT DROPDOWN — PNG / PDF (pro) / ZIP (pro)
// ─────────────────────────────────────────────────

function setupExportDropdown() {
  const wrap = document.getElementById('export-menu');
  const btn = document.getElementById('export-btn');
  const dropdown = document.getElementById('export-dropdown');
  if (!wrap || !btn || !dropdown) return;

  const items = [
    { label: 'PNG', id: 'export-png', pro: false },
    { label: 'PDF', id: 'export-pdf', pro: true },
    { label: 'ZIP (batch)', id: 'export-zip', pro: true },
  ];

  dropdown.innerHTML = '';
  for (const item of items) {
    const el = document.createElement('button');
    el.className = 'header-dropdown-item';
    el.id = item.id;
    el.textContent = item.label;
    if (item.pro) {
      const badge = document.createElement('span');
      badge.className = 'header-dropdown-badge';
      badge.textContent = 'PRO';
      el.appendChild(badge);
    }
    el.onclick = () => {
      dropdown.style.display = 'none';
      if (item.pro && !isPro() && !isUltra() && !isQA()) {
        openPlanModal(item.label);
        return;
      }
      if (item.id === 'export-png') exportPNG();
      else if (item.id === 'export-pdf') exportPDF();
      else if (item.id === 'export-zip') openBatchExportModal();
    };
    dropdown.appendChild(el);
  }

  // Toggle dropdown
  btn.onclick = (e) => {
    e.stopPropagation();
    const isOpen = dropdown.style.display !== 'none';
    dropdown.style.display = isOpen ? 'none' : '';
  };

  // Close on outside click
  document.addEventListener('click', (e) => {
    if (!wrap.contains(e.target)) {
      dropdown.style.display = 'none';
    }
  });
}

/**
 * Export current design as PDF.
 * Renders the canvas to a PNG blob, then wraps it in a minimal PDF.
 */
async function exportPDF() {
  const sizeInfo = TEMPLATE_SIZES[currentSize];
  const fullW = sizeInfo.width;
  const fullH = sizeInfo.height;

  try {
    const blob = await exportPNGToBlob(currentSize, sizeInfo);
    const arrayBuf = await blob.arrayBuffer();
    const imgBytes = new Uint8Array(arrayBuf);

    // Build a minimal single-page PDF with the PNG image
    const pdfBytes = buildMinimalPDF(imgBytes, fullW, fullH);
    const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });

    const template = TEMPLATES[currentState.template];
    const templateName = template ? template.name.replace(/\s+/g, '-').toLowerCase() : 'design';
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.download = `stencil-${templateName}-${currentSize}.pdf`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
    logAction(`Exported PDF: ${currentSize}`);
  } catch (err) {
    console.error('PDF export failed:', err);
    alert('PDF export failed. Please try again.');
  }
}

/**
 * Build a minimal valid PDF containing a single PNG image filling one page.
 * No external library needed — constructs the binary format directly.
 */
function buildMinimalPDF(pngBytes, widthPx, heightPx) {
  // Convert pixels to PDF points (72 dpi)
  const ptW = (widthPx / 96) * 72;
  const ptH = (heightPx / 96) * 72;

  const enc = new TextEncoder();
  const imgLen = pngBytes.length;

  // PDF objects
  const header = '%PDF-1.4\n';
  const obj1 = '1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n';
  const obj2 = `2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n`;
  const obj3 = `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${ptW.toFixed(2)} ${ptH.toFixed(2)}] /Contents 5 0 R /Resources << /XObject << /Img 4 0 R >> >> >>\nendobj\n`;
  const obj4Head = `4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${widthPx} /Height ${heightPx} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /FlateDecode /Length `;
  // We'll store raw PNG stream — actually we need raw pixel data or use DCTDecode.
  // Simpler approach: embed the PNG as-is using a stream and let the PDF viewer handle it.
  // But PDFs don't natively support PNG filter. Let's use the canvas to get JPEG instead.

  // Fallback: open in a new tab with a proper PDF rendering approach
  // For a true minimal PDF, we convert to JPEG via canvas
  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d');

  // We already have the PNG blob, so let's take a simpler approach:
  // Create a data URL and open a print-friendly page
  const pngB64 = btoa(String.fromCharCode(...pngBytes));
  const jpegDataUrl = `data:image/png;base64,${pngB64}`;

  // Build a printable HTML page via Blob URL and trigger print-to-PDF
  const htmlContent = `<!DOCTYPE html>
<html><head><title>stencil.cloud export</title>
<link rel="icon" type="image/svg+xml" href="https://stencil.cloud/favicon.svg">
<style>
@page { size: ${ptW.toFixed(0)}pt ${ptH.toFixed(0)}pt; margin: 0; }
* { margin: 0; padding: 0; }
body { width: ${ptW.toFixed(0)}pt; height: ${ptH.toFixed(0)}pt; }
img { width: 100%; height: 100%; display: block; }
</style></head>
<body><img src="${jpegDataUrl}" onload="setTimeout(()=>{window.print();},200)"></body></html>`;

  const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
  const htmlUrl = URL.createObjectURL(htmlBlob);
  const printWin = window.open(htmlUrl, '_blank');
  if (!printWin) {
    URL.revokeObjectURL(htmlUrl);
    alert('Please allow pop-ups to export PDF.');
    return new Uint8Array(0);
  }
  // Clean up blob URL after a delay
  setTimeout(() => URL.revokeObjectURL(htmlUrl), 30000);

  return new Uint8Array(0); // actual PDF handled by print dialog
}

/**
 * Plan/upgrade modal — shown when user tries a pro feature without a plan.
 */
function openPlanModal(featureName) {
  document.querySelector('.plan-modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'plan-modal-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.8);z-index:10000;display:flex;align-items:center;justify-content:center;';

  const panel = document.createElement('div');
  panel.style.cssText = `
    background:var(--surface,#1a1a1a);border:1px solid var(--border,rgba(255,255,255,0.12));
    padding:40px 48px;max-width:420px;width:90%;text-align:center;
    font-family:'DM Mono',monospace;color:var(--text,#e0e0e0);
  `;

  const title = document.createElement('h3');
  title.textContent = `${featureName} — Pro Feature`;
  title.style.cssText = 'font-size:16px;letter-spacing:1px;margin-bottom:16px;font-weight:400;';
  panel.appendChild(title);

  const desc = document.createElement('p');
  desc.textContent = 'Upgrade to a Pro or Ultra plan to unlock PDF export, batch ZIP export, campaigns, custom subdomains, and more.';
  desc.style.cssText = 'font-size:12px;line-height:1.6;color:#999;margin-bottom:24px;';
  panel.appendChild(desc);

  const upgradeBtn = document.createElement('button');
  upgradeBtn.className = 'export-btn';
  upgradeBtn.textContent = 'VIEW PLANS';
  upgradeBtn.style.cssText = 'margin-right:12px;';
  upgradeBtn.onclick = () => {
    window.open('https://stencil.cloud/pricing', '_blank');
    overlay.remove();
  };
  panel.appendChild(upgradeBtn);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'header-link header-link--action';
  closeBtn.textContent = 'CLOSE';
  closeBtn.onclick = () => overlay.remove();
  panel.appendChild(closeBtn);

  overlay.appendChild(panel);
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };

  const escHandler = (e) => {
    if (e.key === 'Escape') { overlay.remove(); document.removeEventListener('keydown', escHandler); }
  };
  document.addEventListener('keydown', escHandler);

  document.body.appendChild(overlay);
}

// ─────────────────────────────────────────────────
// CAMPAIGN MANAGER — unified scheduler + push + management
// ─────────────────────────────────────────────────

function openCampaignManager() {
  document.querySelector('.campaign-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'campaign-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:10000;display:flex;align-items:center;justify-content:center;';

  const panel = document.createElement('div');
  panel.style.cssText = `
    background:var(--surface,#1a1a1a);border:1px solid var(--border,rgba(255,255,255,0.12));
    width:90%;max-width:820px;max-height:85vh;display:flex;flex-direction:column;
    font-family:'DM Mono',monospace;color:var(--text,#e0e0e0);
  `;

  // ── Header ──
  const header = document.createElement('div');
  header.style.cssText = 'padding:20px 24px 12px;border-bottom:1px solid rgba(255,255,255,0.08);display:flex;align-items:center;gap:16px;flex-shrink:0;';

  const titleEl = document.createElement('h3');
  titleEl.textContent = 'CAMPAIGNS';
  titleEl.style.cssText = 'font-size:14px;letter-spacing:2px;font-weight:400;margin:0;';
  header.appendChild(titleEl);

  // Subdomain selector (if multiple)
  const subdomainSelect = document.createElement('select');
  subdomainSelect.id = 'campaign-subdomain';
  subdomainSelect.style.cssText = 'background:var(--bg,#0d0d0d);border:1px solid var(--border);color:var(--text);font-family:inherit;font-size:11px;padding:4px 8px;display:none;';
  header.appendChild(subdomainSelect);

  const spacer = document.createElement('div');
  spacer.style.flex = '1';
  header.appendChild(spacer);

  // Status bar
  const statusEl = document.createElement('div');
  statusEl.style.cssText = 'font-size:10px;color:#888;letter-spacing:0.5px;';
  statusEl.textContent = 'loading…';
  header.appendChild(statusEl);

  const closeBtn = document.createElement('button');
  closeBtn.textContent = '✕';
  closeBtn.style.cssText = 'background:none;border:none;color:#666;font-size:18px;cursor:pointer;padding:4px 8px;';
  closeBtn.onclick = () => overlay.remove();
  header.appendChild(closeBtn);

  panel.appendChild(header);

  // ── Actions bar ──
  const actionsBar = document.createElement('div');
  actionsBar.style.cssText = 'padding:12px 24px;border-bottom:1px solid rgba(255,255,255,0.08);display:flex;gap:8px;flex-wrap:wrap;flex-shrink:0;';

  const schedBtn = document.createElement('button');
  schedBtn.className = 'export-btn';
  schedBtn.textContent = 'SCHEDULE CURRENT';
  schedBtn.style.cssText = 'font-size:10px;padding:6px 14px;';
  schedBtn.onclick = () => scheduleCurrentDesign(cardsContainer, statusEl);
  actionsBar.appendChild(schedBtn);

  const pushBtn = document.createElement('button');
  pushBtn.className = 'header-link header-link--action';
  pushBtn.textContent = 'PUSH LIVE';
  pushBtn.style.cssText = 'font-size:10px;padding:6px 14px;';
  pushBtn.onclick = () => pushCurrentLive(cardsContainer, statusEl);
  actionsBar.appendChild(pushBtn);

  const viewPageBtn = document.createElement('button');
  viewPageBtn.className = 'header-link header-link--action';
  viewPageBtn.textContent = 'VIEW PAGE';
  viewPageBtn.style.cssText = 'font-size:10px;padding:6px 14px;';
  viewPageBtn.onclick = () => {
    const user = getUser();
    const sub = user?.subdomain;
    if (sub) window.open(`https://${sub}.stencil.cloud`, '_blank');
    else alert('No subdomain configured.');
  };
  actionsBar.appendChild(viewPageBtn);

  panel.appendChild(actionsBar);

  // ── Cards container (scrollable) ──
  const cardsContainer = document.createElement('div');
  cardsContainer.style.cssText = 'padding:16px 24px;overflow-y:auto;flex:1;';
  cardsContainer.innerHTML = '<div style="text-align:center;color:#666;font-size:11px;padding:40px 0;">loading campaigns…</div>';
  panel.appendChild(cardsContainer);

  overlay.appendChild(panel);
  // Prevent accidental backdrop clicks from destroying form state.
  // Only close via the explicit ✕ button or Escape key.
  overlay.onclick = (e) => { e.stopPropagation(); };

  const escHandler = (e) => {
    if (e.key === 'Escape') { overlay.remove(); document.removeEventListener('keydown', escHandler); }
  };
  document.addEventListener('keydown', escHandler);

  document.body.appendChild(overlay);

  // Load data
  loadScheduleStatus(statusEl);
  loadCampaignList(cardsContainer, statusEl, subdomainSelect);
}

/**
 * Load campaign list and render as cards.
 */
async function loadCampaignList(container, statusEl, subdomainSelect) {
  try {
    const res = await fetch('/api/schedule', { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to load');
    const data = await res.json();
    const items = data.items || [];

    // Populate subdomain selector if multiple subdomains
    const subdomains = [...new Set(items.map(i => i.subdomain).filter(Boolean))];
    if (subdomains.length > 1 && subdomainSelect) {
      subdomainSelect.style.display = '';
      subdomainSelect.innerHTML = '<option value="">All subdomains</option>' +
        subdomains.map(s => `<option value="${s}">${s}</option>`).join('');
      subdomainSelect.onchange = () => {
        const filtered = subdomainSelect.value
          ? items.filter(i => i.subdomain === subdomainSelect.value)
          : items;
        renderCampaignCards(filtered, container, statusEl);
      };
    }

    if (items.length === 0) {
      container.innerHTML = '<div style="text-align:center;color:#666;font-size:11px;padding:40px 0;">no campaigns yet — schedule your first design above</div>';
      return;
    }

    renderCampaignCards(items, container, statusEl);
  } catch (e) {
    container.innerHTML = `<div style="text-align:center;color:#666;font-size:11px;padding:40px 0;">could not load campaigns — ${e.message}</div>`;
  }
}

/**
 * Render campaign items as a card grid with thumbnails and inline actions.
 */
function renderCampaignCards(items, container, statusEl) {
  const todayStr = new Date().toISOString().split('T')[0];
  items.sort((a, b) => (b.event_date || '').localeCompare(a.event_date || ''));

  container.innerHTML = '';

  const grid = document.createElement('div');
  grid.style.cssText = 'display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:16px;';

  for (const item of items) {
    let badgeClass = 'upcoming';
    let badgeLabel = 'UPCOMING';
    let badgeColor = '#f59e0b';

    if (item.status === 'active') { badgeLabel = 'LIVE'; badgeColor = '#22c55e'; }
    else if (item.status === 'expired') { badgeLabel = 'EXPIRED'; badgeColor = '#666'; }
    else if (item.event_date && item.event_date < todayStr) { badgeLabel = 'PAST'; badgeColor = '#666'; }

    const card = document.createElement('div');
    card.style.cssText = `
      background:var(--bg,#0d0d0d);border:1px solid var(--border,rgba(255,255,255,0.08));
      overflow:hidden;position:relative;transition:border-color 0.2s;cursor:default;
    `;
    card.onmouseenter = () => { card.style.borderColor = 'var(--accent,#fff)'; };
    card.onmouseleave = () => { card.style.borderColor = 'var(--border,rgba(255,255,255,0.08))'; };

    // Thumbnail
    const thumbWrap = document.createElement('div');
    thumbWrap.style.cssText = 'width:100%;aspect-ratio:4/3;background:#111;overflow:hidden;position:relative;';
    if (item.image_url) {
      const img = document.createElement('img');
      img.src = item.image_url;
      img.style.cssText = 'width:100%;height:100%;object-fit:cover;';
      img.loading = 'lazy';
      thumbWrap.appendChild(img);
    } else {
      const placeholder = document.createElement('div');
      placeholder.style.cssText = 'width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#333;font-size:10px;letter-spacing:1px;';
      placeholder.textContent = 'NO PREVIEW';
      thumbWrap.appendChild(placeholder);
    }

    // Status badge overlay
    const badge = document.createElement('span');
    badge.textContent = badgeLabel;
    badge.style.cssText = `
      position:absolute;top:8px;right:8px;background:${badgeColor};color:#000;
      font-size:9px;font-weight:700;letter-spacing:1px;padding:2px 6px;
      font-family:'DM Mono',monospace;
    `;
    thumbWrap.appendChild(badge);

    // Visibility toggle overlay
    const visBtn = document.createElement('button');
    visBtn.textContent = item.hidden ? '🔒' : '👁';
    visBtn.title = item.hidden ? 'Hidden from public' : 'Visible to public';
    visBtn.style.cssText = 'position:absolute;top:8px;left:8px;background:rgba(0,0,0,0.6);border:none;font-size:14px;cursor:pointer;padding:2px 6px;';
    visBtn.onclick = async (e) => {
      e.stopPropagation();
      try {
        const res = await fetch(`/api/schedule/${item.id}`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hidden: !item.hidden }),
        });
        if (res.ok) {
          item.hidden = !item.hidden;
          visBtn.textContent = item.hidden ? '🔒' : '👁';
          visBtn.title = item.hidden ? 'Hidden from public' : 'Visible to public';
        }
      } catch (_) { /* silent */ }
    };
    thumbWrap.appendChild(visBtn);

    card.appendChild(thumbWrap);

    // Info section
    const info = document.createElement('div');
    info.style.cssText = 'padding:10px 12px;';

    // Editable title
    const titleRow = document.createElement('div');
    titleRow.style.cssText = 'display:flex;align-items:center;gap:6px;margin-bottom:6px;';
    const titleInput = document.createElement('input');
    titleInput.type = 'text';
    titleInput.value = item.title || 'untitled';
    titleInput.style.cssText = `
      background:transparent;border:1px solid transparent;color:var(--text);
      font-family:'DM Mono',monospace;font-size:11px;letter-spacing:0.5px;
      padding:2px 4px;flex:1;min-width:0;transition:border-color 0.2s;
    `;
    titleInput.onfocus = () => { titleInput.style.borderColor = 'var(--accent)'; };
    titleInput.onblur = async () => {
      titleInput.style.borderColor = 'transparent';
      if (titleInput.value !== (item.title || 'untitled')) {
        try {
          await fetch(`/api/schedule/${item.id}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title: titleInput.value }),
          });
          item.title = titleInput.value;
        } catch (_) { /* silent */ }
      }
    };
    titleRow.appendChild(titleInput);
    info.appendChild(titleRow);

    // Date + editable date
    const dateRow = document.createElement('div');
    dateRow.style.cssText = 'display:flex;align-items:center;gap:8px;margin-bottom:4px;';
    const dateInput = document.createElement('input');
    dateInput.type = 'date';
    dateInput.value = item.event_date || '';
    dateInput.style.cssText = `
      background:transparent;border:1px solid transparent;color:#999;
      font-family:'DM Mono',monospace;font-size:10px;padding:2px;cursor:pointer;
    `;
    dateInput.onfocus = () => { dateInput.style.borderColor = 'var(--accent)'; };
    dateInput.onblur = async () => {
      dateInput.style.borderColor = 'transparent';
      if (dateInput.value !== item.event_date) {
        try {
          await fetch(`/api/schedule/${item.id}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ event_date: dateInput.value }),
          });
          item.event_date = dateInput.value;
        } catch (_) { /* silent */ }
      }
    };
    dateRow.appendChild(dateInput);

    // Created date (read-only)
    if (item.created_at) {
      const created = document.createElement('span');
      created.style.cssText = 'font-size:9px;color:#555;';
      created.textContent = `created ${timeAgo(item.created_at)}`;
      dateRow.appendChild(created);
    }
    info.appendChild(dateRow);

    // Action buttons
    const actRow = document.createElement('div');
    actRow.style.cssText = 'display:flex;gap:6px;margin-top:8px;';

    const pushLiveBtn = document.createElement('button');
    pushLiveBtn.textContent = 'PUSH LIVE';
    pushLiveBtn.style.cssText = `
      background:none;border:1px solid var(--accent,#fff);color:var(--accent,#fff);
      font-family:'DM Mono',monospace;font-size:9px;letter-spacing:1px;
      padding:3px 8px;cursor:pointer;transition:all 0.2s;
    `;
    pushLiveBtn.onmouseenter = () => { pushLiveBtn.style.background = 'var(--accent)'; pushLiveBtn.style.color = '#000'; };
    pushLiveBtn.onmouseleave = () => { pushLiveBtn.style.background = 'none'; pushLiveBtn.style.color = 'var(--accent)'; };
    pushLiveBtn.onclick = () => activateScheduleItem(item.id, container, statusEl);
    actRow.appendChild(pushLiveBtn);

    const delBtn = document.createElement('button');
    delBtn.textContent = '✕';
    delBtn.style.cssText = `
      background:none;border:1px solid rgba(255,255,255,0.1);color:#666;
      font-size:11px;padding:3px 8px;cursor:pointer;transition:color 0.2s;
      font-family:'DM Mono',monospace;
    `;
    delBtn.onmouseenter = () => { delBtn.style.color = '#ef4444'; delBtn.style.borderColor = '#ef4444'; };
    delBtn.onmouseleave = () => { delBtn.style.color = '#666'; delBtn.style.borderColor = 'rgba(255,255,255,0.1)'; };
    delBtn.onclick = () => deleteScheduleItem(item.id, container, statusEl);
    actRow.appendChild(delBtn);

    info.appendChild(actRow);
    card.appendChild(info);
    grid.appendChild(card);
  }

  container.appendChild(grid);
}

/**
 * Share button — opens a dropdown with social sharing links.
 */
function setupShareButton() {
  const btn = document.getElementById('share-btn');
  if (!btn) return;

  const url = encodeURIComponent('https://stencil.cloud');
  const title = encodeURIComponent('stencil.cloud — Free Generative Design Kit');
  const desc = encodeURIComponent('Create flyers, posters, and social graphics with 50+ generative art patterns. No signup required.');

  const platforms = [
    { name: 'X', href: `https://x.com/intent/post?text=${desc}&url=${url}` },
    { name: 'Bluesky', href: `https://bsky.app/intent/compose?text=${desc}%20${url}` },
    { name: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${url}` },
    { name: 'Reddit', href: `https://www.reddit.com/submit?url=${url}&title=${title}` },
    { name: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}` },
    { name: 'Threads', href: `https://www.threads.net/intent/post?text=${desc}%20${url}` },
    { name: 'Instagram', href: '#copy-for-insta' },
    { name: 'TikTok', href: '#copy-for-tiktok' },
    { name: 'Email', href: `mailto:?subject=${title}&body=${desc}%0A%0A${url}` },
    { name: 'Copy Link', href: '#copy' },
  ];

  let dropdown = null;

  btn.onclick = (e) => {
    e.stopPropagation();
    // Toggle existing dropdown
    if (dropdown) {
      dropdown.remove();
      dropdown = null;
      return;
    }

    dropdown = document.createElement('div');
    // Position relative to the share button, appended to body to avoid header overflow clipping
    const rect = btn.getBoundingClientRect();
    dropdown.style.cssText = `
      position: fixed; top: ${rect.bottom + 4}px; left: ${rect.left}px; z-index: 9000;
      background: var(--surface); border: 1px solid var(--border);
      min-width: 190px; box-shadow: 0 4px 20px rgba(0,0,0,0.5);
    `;

    for (const p of platforms) {
      const link = document.createElement('a');
      link.textContent = p.name;
      link.href = p.href;
      link.style.cssText = `
        display: block; padding: 8px 14px; color: var(--text);
        font-family: 'DM Mono', monospace; font-size: 11px;
        text-decoration: none; letter-spacing: 1px;
        transition: background 0.15s, color 0.15s;
      `;
      link.onmouseenter = () => { link.style.background = 'var(--bg)'; link.style.color = 'var(--accent)'; };
      link.onmouseleave = () => { link.style.background = ''; link.style.color = 'var(--text)'; };

      if (p.href === '#copy' || p.href === '#copy-for-insta' || p.href === '#copy-for-tiktok') {
        link.onclick = (ev) => {
          ev.preventDefault();
          const copyText = 'https://stencil.cloud';
          navigator.clipboard.writeText(copyText).then(() => {
            const originalName = p.name;
            link.textContent = 'Link copied!';
            setTimeout(() => { link.textContent = originalName; }, 1500);
          });
        };
        // Show hint for platforms without share URLs
        if (p.href !== '#copy') {
          link.title = 'Copies link — paste in your bio or post';
        }
      } else {
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      }

      dropdown.appendChild(link);
    }

    document.body.appendChild(dropdown);

    // Close on outside click
    const close = (ev) => {
      if (dropdown && !dropdown.contains(ev.target) && ev.target !== btn) {
        dropdown.remove();
        dropdown = null;
        document.removeEventListener('click', close);
      }
    };
    setTimeout(() => document.addEventListener('click', close), 0);
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// MY DESIGNS MODAL — list, load, and manage saved designs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Open the My Designs modal.
 * Lists saved designs from the API. Each design can be loaded into the editor
 * or deleted. Loading replaces the current editor state entirely.
 */
async function openMyDesignsModal() {
  document.querySelector('.signin-modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'signin-modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'signin-modal';
  modal.style.minWidth = '460px';
  modal.style.maxWidth = '540px';
  modal.style.maxHeight = '80vh';
  modal.style.overflowY = 'auto';

  const title = document.createElement('h2');
  title.textContent = 'MY DESIGNS';
  modal.appendChild(title);

  if (!isAuthenticated()) {
    const msg = document.createElement('div');
    msg.style.cssText = 'text-align:center;color:var(--text-dim);font-size:11px;font-family:"DM Mono",monospace;padding:20px 0;';
    msg.textContent = 'Sign in to save and load designs.';
    modal.appendChild(msg);
    overlay.appendChild(modal);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
    document.body.appendChild(overlay);
    return;
  }

  // Loading state
  const loading = document.createElement('div');
  loading.style.cssText = 'text-align:center;color:var(--text-dim);font-size:11px;font-family:"DM Mono",monospace;padding:20px 0;';
  loading.textContent = 'Loading designs...';
  modal.appendChild(loading);

  overlay.appendChild(modal);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);

  // Save current design button
  const saveRow = document.createElement('div');
  saveRow.style.cssText = 'display:flex;gap:6px;margin-bottom:16px;';

  const nameInput = document.createElement('input');
  nameInput.className = 'field-input';
  nameInput.style.flex = '1';
  nameInput.placeholder = 'Design name...';
  nameInput.value = currentState.template ? currentState.template.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Untitled';

  const saveCurrentBtn = document.createElement('button');
  saveCurrentBtn.className = 'btn-small';
  saveCurrentBtn.style.cssText = 'border-color:var(--accent);color:var(--accent);white-space:nowrap;';
  saveCurrentBtn.textContent = 'SAVE CURRENT';
  saveCurrentBtn.onclick = async () => {
    saveCurrentBtn.textContent = 'SAVING...';
    saveCurrentBtn.disabled = true;
    try {
      const stateData = {
        template: currentState.template,
        scheme: currentState.scheme?.name,
        size: currentSize,
        overlays: currentState.overlays,
        textEdits: captureTextEdits(),
        wordmark: {
          text: currentState._wordmarkText,
          font: currentState._wordmarkFont,
          color: currentState._wordmarkColor,
          bold: currentState._wordmarkBold,
          italic: currentState._wordmarkItalic,
          uppercase: currentState._wordmarkUppercase,
          accent: currentState._wordmarkAccent,
        },
        mangleSeed: getMangleSeed(),
        mangleParams: currentState._mangleParams,
        blendLayers: currentState.blendLayers || [],
        editorMode,
        eventDateISO: currentState.eventDateISO,
        dateFormat: currentState.dateFormat,
        eventTime: currentState.eventTime,
      };

      const resp = await fetch('/api/designs', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: nameInput.value || 'Untitled',
          template: currentState.template,
          state: stateData,
        }),
      });
      const data = await resp.json();
      if (data.ok) {
        logAction('Design saved: ' + (nameInput.value || 'Untitled'));
        // Refresh the list
        await loadDesignList();
      } else {
        alert(data.error || 'Failed to save');
      }
    } catch (e) {
      alert('Failed to save: ' + e.message);
    }
    saveCurrentBtn.textContent = 'SAVE CURRENT';
    saveCurrentBtn.disabled = false;
  };

  saveRow.appendChild(nameInput);
  saveRow.appendChild(saveCurrentBtn);

  // Design list container
  const listContainer = document.createElement('div');

  async function loadDesignList() {
    listContainer.innerHTML = '';
    try {
      const resp = await fetch('/api/designs', { credentials: 'include' });
      const data = await resp.json();
      const designs = data.designs || [];

      if (designs.length === 0) {
        const empty = document.createElement('div');
        empty.style.cssText = 'text-align:center;color:var(--text-dim);font-size:10px;font-family:"DM Mono",monospace;padding:16px 0;';
        empty.textContent = 'No saved designs yet. Save your current work above.';
        listContainer.appendChild(empty);
        return;
      }

      for (const design of designs) {
        const row = document.createElement('div');
        row.style.cssText = `
          display:flex;align-items:center;gap:10px;padding:10px;
          border:1px solid var(--border);margin-bottom:6px;
          transition:border-color 0.15s;cursor:default;
        `;

        const info = document.createElement('div');
        info.style.cssText = 'flex:1;min-width:0;';

        const dTitle = document.createElement('div');
        dTitle.style.cssText = 'font-size:12px;color:#fff;font-family:"DM Mono",monospace;letter-spacing:0.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
        dTitle.textContent = design.title || 'Untitled';
        info.appendChild(dTitle);

        const dMeta = document.createElement('div');
        dMeta.style.cssText = 'font-size:9px;color:var(--text-dim);margin-top:3px;font-family:"DM Mono",monospace;';
        const date = new Date(design.updated_at * 1000);
        dMeta.textContent = `${design.template} · ${date.toLocaleDateString()}`;
        info.appendChild(dMeta);

        const loadBtn = document.createElement('button');
        loadBtn.className = 'btn-small';
        loadBtn.textContent = 'LOAD';
        loadBtn.style.cssText = 'border-color:var(--accent);color:var(--accent);padding:4px 12px;';
        loadBtn.onclick = async () => {
          loadBtn.textContent = '...';
          try {
            const resp = await fetch(`/api/designs/${design.id}`, { credentials: 'include' });
            const data = await resp.json();
            if (data.state && data.design) {
              applyDesignState(data.state, data.design.template);
              logAction('Loaded design: ' + (design.title || 'Untitled'));
              overlay.remove();
            } else {
              alert('Design state not found');
            }
          } catch (e) {
            alert('Failed to load: ' + e.message);
          }
          loadBtn.textContent = 'LOAD';
        };

        const delBtn = document.createElement('button');
        delBtn.className = 'btn-small';
        delBtn.textContent = '\u2715';
        delBtn.style.cssText = 'color:var(--danger);border-color:var(--danger);padding:4px 8px;';
        delBtn.onclick = async () => {
          if (!confirm(`Delete "${design.title || 'Untitled'}"?`)) return;
          try {
            await fetch(`/api/designs/${design.id}`, { method: 'DELETE', credentials: 'include' });
            logAction('Deleted design: ' + (design.title || 'Untitled'));
            await loadDesignList();
          } catch (e) {
            alert('Failed to delete: ' + e.message);
          }
        };

        row.appendChild(info);
        row.appendChild(loadBtn);
        row.appendChild(delBtn);
        listContainer.appendChild(row);
      }
    } catch (e) {
      listContainer.innerHTML = '<div style="color:var(--danger);font-size:10px;font-family:\'DM Mono\',monospace;">Failed to load designs</div>';
    }
  }

  // Remove loading, add save row + list
  loading.remove();
  modal.appendChild(saveRow);
  modal.appendChild(listContainer);

  await loadDesignList();
}

/**
 * Apply a loaded design state to the editor.
 * Restores template, scheme, overlays, text edits, wordmark, mangle, etc.
 */
function applyDesignState(state, templateKey) {
  const template = TEMPLATES[templateKey || state.template];
  if (!template) {
    alert('Template not found: ' + (templateKey || state.template));
    return;
  }

  currentState.template = templateKey || state.template;
  currentSize = state.size || currentSize;
  const schemeName = state.scheme || 'dark';
  const scheme = getScheme(schemeName);
  currentState.scheme = { ...scheme, name: schemeName };

  if (state.overlays) currentState.overlays = state.overlays;
  if (state.eventDateISO) currentState.eventDateISO = state.eventDateISO;
  if (state.dateFormat) currentState.dateFormat = state.dateFormat;
  if (state.eventTime) currentState.eventTime = state.eventTime;
  if (state.mangleParams) currentState._mangleParams = state.mangleParams;
  if (state.blendLayers) currentState.blendLayers = state.blendLayers;
  if (state.editorMode) editorMode = state.editorMode;

  // Restore wordmark config
  if (state.wordmark) {
    currentState._wordmarkText = state.wordmark.text;
    currentState._wordmarkFont = state.wordmark.font;
    currentState._wordmarkColor = state.wordmark.color;
    currentState._wordmarkBold = state.wordmark.bold;
    currentState._wordmarkItalic = state.wordmark.italic;
    currentState._wordmarkUppercase = state.wordmark.uppercase;
    currentState._wordmarkAccent = state.wordmark.accent;
  }

  if (state.mangleSeed != null) setMangleSeed(state.mangleSeed);
  if (state.groupAlignOverrides) Object.assign(groupAlignOverrides, state.groupAlignOverrides);

  buildSidebar();
  hydrateTemplate(template, scheme, currentSize);

  // Apply text edits after hydration
  if (state.textEdits) {
    fabricCanvas.getObjects().forEach(obj => {
      if (obj.data?.id && state.textEdits[obj.data.id]) {
        obj.set('text', state.textEdits[obj.data.id]);
      }
    });
    fabricCanvas.renderAll();
  }

  logAction('Design state restored');
}

// ─────────────────────────────────────────────────────────────────────────────
// ANALYTICS MODAL — subdomain page performance data
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN REMIX MODAL
// ─────────────────────────────────────────────────────────────────────────────
//
// Opens a gallery window showing 3–5 remixed versions of the current design.
// Each thumbnail previews a different scheme + overlay + font pairing combo.
// The user can:
//   - Click a thumbnail to apply that variation
//   - Click "Shuffle" to generate a fresh set of variations
//   - Close and re-open to see the same gallery (return-to-gallery feature)
//   - The original design is always shown as the first card for easy revert
//
// Works in both Template Mode and Advanced Mode — the remix only changes
// visual parameters (scheme, overlay, seed, grain, fonts), never text content
// or object positions.

/**
 * Open the Design Remix modal.
 * @param {boolean} forceNew - if true, generate fresh variations even if a gallery exists
 */
function openRemixModal(forceNew = false) {
  // Remove any existing modal
  document.querySelector('.remix-modal-overlay')?.remove();

  // Snapshot the current state BEFORE any changes so we can revert
  const originalSnapshot = {
    schemeKey: currentState.scheme?.name || 'dark',
    overlays: JSON.parse(JSON.stringify(currentState.overlays || {})),
    // We don't snapshot font overrides here — fonts are only changed if user selects a variation
  };

  // Generate or reuse variations
  let variations, thumbnails;

  if (!forceNew && _lastRemixGallery) {
    // Return to the existing gallery
    variations = _lastRemixGallery.variations;
    thumbnails = _lastRemixGallery.thumbnails;
  } else {
    // Generate fresh variations (7 + current = 8 cards)
    const count = 7;
    variations = generateVariations(currentState, count);

    // Render thumbnails
    thumbnails = variations.map(v =>
      renderVariationThumbnail(v, currentState, currentSize)
    );

    // Store for return-to-gallery
    _lastRemixGallery = { variations, thumbnails, originalSnapshot, masterSeed: Date.now() };
  }

  // ── Build the modal ──
  const overlay = document.createElement('div');
  overlay.className = 'remix-modal-overlay';
  overlay.style.cssText = `
    position:fixed;top:0;left:0;right:0;bottom:0;
    background:rgba(0,0,0,0.85);z-index:10000;
    display:flex;align-items:center;justify-content:center;
    animation:fadeIn 0.2s ease;
  `;

  const modal = document.createElement('div');
  modal.style.cssText = `
    background:var(--bg, #111);border:1px solid var(--border, #333);
    border-radius:8px;padding:24px;max-width:900px;width:90vw;
    max-height:90vh;overflow-y:auto;position:relative;
    font-family:"DM Mono",monospace;color:var(--text, #eee);
  `;

  // Header
  const header = document.createElement('div');
  header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;';

  const title = document.createElement('h2');
  title.style.cssText = 'margin:0;font-size:16px;letter-spacing:2px;font-weight:600;';
  title.textContent = 'DESIGN REMIX';
  header.appendChild(title);

  const headerRight = document.createElement('div');
  headerRight.style.cssText = 'display:flex;gap:8px;align-items:center;';

  // Shuffle button — generates a fresh set
  const shuffleBtn = document.createElement('button');
  shuffleBtn.className = 'btn-small';
  shuffleBtn.style.cssText = 'padding:6px 14px;font-size:11px;letter-spacing:1px;';
  shuffleBtn.textContent = 'SHUFFLE';
  shuffleBtn.title = 'Generate a fresh set of variations';
  shuffleBtn.onclick = () => {
    overlay.remove();
    openRemixModal(true);
  };
  headerRight.appendChild(shuffleBtn);

  // Close button
  const closeBtn = document.createElement('button');
  closeBtn.style.cssText = `
    background:none;border:none;color:var(--text-dim, #888);
    font-size:18px;cursor:pointer;padding:4px 8px;line-height:1;
  `;
  closeBtn.textContent = '\u00d7';
  closeBtn.title = 'Close (Escape)';
  closeBtn.onclick = () => overlay.remove();
  headerRight.appendChild(closeBtn);
  header.appendChild(headerRight);
  modal.appendChild(header);

  // Subtitle
  const subtitle = document.createElement('p');
  subtitle.style.cssText = 'margin:0 0 16px;font-size:11px;color:var(--text-dim, #888);line-height:1.4;';
  subtitle.textContent = 'Each card shows a different scheme + overlay + font combo. Click one to apply it. Your text stays the same.';
  modal.appendChild(subtitle);

  // ── Thumbnail grid ──
  const grid = document.createElement('div');
  grid.style.cssText = `
    display:grid;
    grid-template-columns:repeat(auto-fill, minmax(200px, 1fr));
    gap:14px;margin-bottom:16px;
  `;

  // Card 0: Current design (always first)
  const currentCard = buildRemixCard({
    thumbUrl: null, // we'll render the current design thumbnail
    label: `Current: ${currentState.scheme?.name || 'Dark'}`,
    isOriginal: true,
    onClick: () => {
      // Revert to original state
      applyRemixVariation(originalSnapshot, overlay);
    },
  });
  // Render current design thumbnail
  const currentThumb = renderVariationThumbnail({
    schemeKey: originalSnapshot.schemeKey,
    overlayType: currentState.overlays?.lineArt?.type || 'flow',
    density: currentState.overlays?.lineArt?.density ?? 50,
    opacity: currentState.overlays?.lineArt?.opacity ?? 30,
    seed: currentState.overlays?.lineArt?.seed ?? 42,
    grainDensity: currentState.overlays?.grain?.density ?? 40,
    grainOpacity: currentState.overlays?.grain?.opacity ?? 15,
    fontPairing: null,
  }, currentState, currentSize);
  const currentImg = currentCard.querySelector('img');
  if (currentImg) currentImg.src = currentThumb;
  grid.appendChild(currentCard);

  // Cards 1–N: Variations
  variations.forEach((variation, i) => {
    const card = buildRemixCard({
      thumbUrl: thumbnails[i],
      label: variation.label,
      fontLabel: variation.fontPairing?.label || null,
      isOriginal: false,
      onClick: () => {
        const patch = variationToStatePatch(variation);
        applyRemixVariation(patch, overlay);
      },
    });
    grid.appendChild(card);
  });

  modal.appendChild(grid);

  // ── Footer hint ──
  const hint = document.createElement('div');
  hint.style.cssText = 'font-size:10px;color:var(--text-dim, #666);text-align:center;';
  hint.textContent = 'Press R to remix anytime \u2022 ESC to close \u2022 Shuffle for more options';
  modal.appendChild(hint);

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  // Close on ESC or click outside
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  const escHandler = (e) => {
    if (e.key === 'Escape') { overlay.remove(); document.removeEventListener('keydown', escHandler); }
  };
  document.addEventListener('keydown', escHandler);

  logAction('Opened remix gallery');
}

/**
 * Build a single remix gallery card element.
 */
function buildRemixCard({ thumbUrl, label, fontLabel, isOriginal, onClick }) {
  const card = document.createElement('div');
  card.style.cssText = `
    border:2px solid ${isOriginal ? 'var(--accent, #ff4444)' : 'var(--border, #333)'};
    border-radius:6px;cursor:pointer;overflow:hidden;
    transition:border-color 0.15s, transform 0.15s;
    background:var(--surface, #1a1a1a);
  `;
  card.onmouseenter = () => { card.style.borderColor = 'var(--accent, #ff4444)'; card.style.transform = 'scale(1.02)'; };
  card.onmouseleave = () => { card.style.borderColor = isOriginal ? 'var(--accent, #ff4444)' : 'var(--border, #333)'; card.style.transform = 'scale(1)'; };

  // Thumbnail image
  const img = document.createElement('img');
  img.style.cssText = 'width:100%;display:block;aspect-ratio:auto;';
  if (thumbUrl) img.src = thumbUrl;
  img.alt = label;
  card.appendChild(img);

  // Label bar
  const labelBar = document.createElement('div');
  labelBar.style.cssText = 'padding:8px 10px;';

  const mainLabel = document.createElement('div');
  mainLabel.style.cssText = 'font-size:10px;letter-spacing:0.5px;color:var(--text, #eee);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
  mainLabel.textContent = label;
  labelBar.appendChild(mainLabel);

  if (fontLabel) {
    const fontEl = document.createElement('div');
    fontEl.style.cssText = 'font-size:9px;color:var(--text-dim, #888);margin-top:2px;';
    fontEl.textContent = fontLabel;
    labelBar.appendChild(fontEl);
  }

  if (isOriginal) {
    const badge = document.createElement('span');
    badge.style.cssText = 'font-size:8px;letter-spacing:1px;color:var(--accent, #ff4444);margin-top:3px;display:block;';
    badge.textContent = 'CURRENT';
    labelBar.appendChild(badge);
  }

  card.appendChild(labelBar);
  card.onclick = onClick;
  return card;
}

/**
 * Apply a remix variation (or revert to original) to the live editor.
 *
 * This modifies the scheme, overlays, and optionally the font pairing on the
 * live canvas. Text content is NEVER changed.
 *
 * @param {object} patch - { schemeKey, overlays, fontPairing } or original snapshot
 * @param {HTMLElement} modalOverlay - the modal overlay element to close
 */
function applyRemixVariation(patch, modalOverlay) {
  // Push undo state before applying
  pushUndoState(fabricCanvas, currentState, currentSize);

  // Apply scheme
  const schemeKey = patch.schemeKey;
  const scheme = getScheme(schemeKey);
  currentState.scheme = { ...scheme, name: schemeKey };

  // Apply overlays
  if (patch.overlays) {
    currentState.overlays = JSON.parse(JSON.stringify(patch.overlays));
  }

  // Apply font pairing (if present) — updates textStyleOverrides for title/body roles
  if (patch.fontPairing) {
    const titleFont = patch.fontPairing.title;
    const bodyFont = patch.fontPairing.body;

    // Map roles to title vs body font
    const TITLE_ROLES = new Set(['title', 'venue', 'lineup']);
    fabricCanvas.getObjects().forEach(obj => {
      if (!obj.data?.id || obj.data.stencilType !== 'text') return;
      const role = obj.data.role;
      if (TITLE_ROLES.has(role)) {
        if (!textStyleOverrides[obj.data.id]) textStyleOverrides[obj.data.id] = {};
        textStyleOverrides[obj.data.id].fontFamily = titleFont;
      } else if (role && role !== 'user') {
        if (!textStyleOverrides[obj.data.id]) textStyleOverrides[obj.data.id] = {};
        textStyleOverrides[obj.data.id].fontFamily = bodyFont;
      }
    });
  }

  // Re-render — hydrateTemplate in template mode, or just re-render background + scheme in advanced mode
  if (editorMode === 'template') {
    const template = TEMPLATES[currentState.template];
    if (template) {
      hydrateTemplate(template, scheme, currentSize);
    }
  } else {
    // Advanced mode: update colors on existing objects + re-render background
    applySchemeToObjects(scheme);
  }

  // Re-render background overlay
  updateBackground(scheme, currentSize);
  buildSidebar();

  logAction(`Applied remix: ${scheme.name || schemeKey}`);

  // Close modal
  if (modalOverlay) modalOverlay.remove();
}

/**
 * Open the Analytics modal.
 * Ultra users see full analytics; other tiers see a blurred teaser
 * with an upgrade CTA overlaid.
 */
async function openAnalyticsModal() {
  document.querySelector('.signin-modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'signin-modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'signin-modal';
  modal.style.minWidth = '560px';
  modal.style.maxWidth = '640px';
  modal.style.maxHeight = '85vh';
  modal.style.overflowY = 'auto';
  modal.style.position = 'relative';

  const title = document.createElement('h2');
  title.textContent = 'ANALYTICS';
  modal.appendChild(title);

  // Range selector
  const rangeRow = document.createElement('div');
  rangeRow.style.cssText = 'display:flex;gap:6px;justify-content:center;margin-bottom:16px;';
  const ranges = [
    { key: '7d', label: '7 DAYS' },
    { key: '30d', label: '30 DAYS' },
    { key: '90d', label: '90 DAYS' },
    { key: 'all', label: 'ALL TIME' },
  ];
  let activeRange = '30d';

  for (const r of ranges) {
    const btn = document.createElement('button');
    btn.className = 'btn-small';
    btn.style.cssText = `padding:4px 10px;font-size:9px;${r.key === activeRange ? 'border-color:var(--accent);color:var(--accent);' : ''}`;
    btn.textContent = r.label;
    btn.dataset.range = r.key;
    btn.onclick = () => {
      activeRange = r.key;
      rangeRow.querySelectorAll('button').forEach(b => {
        b.style.borderColor = b.dataset.range === activeRange ? 'var(--accent)' : 'var(--border)';
        b.style.color = b.dataset.range === activeRange ? 'var(--accent)' : 'var(--text-dim)';
      });
      loadAnalyticsData();
    };
    rangeRow.appendChild(btn);
  }
  modal.appendChild(rangeRow);

  // Content area (will be populated or blurred)
  const content = document.createElement('div');
  content.style.cssText = 'position:relative;';
  modal.appendChild(content);

  overlay.appendChild(modal);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);

  async function loadAnalyticsData() {
    content.innerHTML = '<div style="text-align:center;padding:30px;color:var(--text-dim);font-size:11px;font-family:\'DM Mono\',monospace;">Loading analytics...</div>';

    try {
      const resp = await fetch(`/api/analytics?range=${activeRange}`, { credentials: 'include' });
      const data = await resp.json();

      content.innerHTML = '';

      if (data.ultraRequired) {
        // Render blurred teaser for non-Ultra users
        renderAnalyticsTeaser(content, data);
      } else {
        // Full analytics for Ultra users
        renderFullAnalytics(content, data);
      }
    } catch (e) {
      content.innerHTML = '<div style="text-align:center;padding:30px;color:var(--danger);font-size:11px;font-family:\'DM Mono\',monospace;">Failed to load analytics</div>';
    }
  }

  await loadAnalyticsData();
}

/**
 * Render a blurred analytics teaser for non-Ultra users.
 * Shows real summary numbers but fake detailed data, blurred out,
 * with an upgrade CTA overlay.
 */
function renderAnalyticsTeaser(container, data) {
  // Show real summary stats (not blurred)
  const summaryRow = document.createElement('div');
  summaryRow.style.cssText = 'display:flex;gap:12px;margin-bottom:16px;';

  const stats = [
    { label: 'TOTAL VIEWS', value: data.totalViews || 0 },
    { label: 'UNIQUE VISITORS', value: data.uniqueVisitors || 0 },
  ];

  for (const stat of stats) {
    const card = document.createElement('div');
    card.style.cssText = 'flex:1;border:1px solid var(--border);padding:14px;text-align:center;';

    const val = document.createElement('div');
    val.style.cssText = 'font-size:24px;color:#fff;font-family:"DM Mono",monospace;letter-spacing:1px;';
    val.textContent = stat.value.toLocaleString();
    card.appendChild(val);

    const lbl = document.createElement('div');
    lbl.style.cssText = 'font-size:9px;color:var(--text-dim);font-family:"DM Mono",monospace;letter-spacing:1px;margin-top:4px;';
    lbl.textContent = stat.label;
    card.appendChild(lbl);

    summaryRow.appendChild(card);
  }
  container.appendChild(summaryRow);

  // Blurred detailed section with fake data
  const blurWrap = document.createElement('div');
  blurWrap.style.cssText = 'position:relative;';

  const blurContent = document.createElement('div');
  blurContent.style.cssText = 'filter:blur(6px);pointer-events:none;user-select:none;opacity:0.5;';

  // Fake chart area
  const fakeChart = document.createElement('div');
  fakeChart.style.cssText = 'border:1px solid var(--border);padding:16px;margin-bottom:12px;';

  const chartTitle = document.createElement('div');
  chartTitle.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;letter-spacing:1px;margin-bottom:10px;';
  chartTitle.textContent = 'DAILY VIEWS';
  fakeChart.appendChild(chartTitle);

  // Fake bar chart
  const barRow = document.createElement('div');
  barRow.style.cssText = 'display:flex;align-items:flex-end;gap:3px;height:80px;';
  const fakeBars = [30, 45, 60, 35, 80, 55, 70, 40, 90, 65, 50, 75, 45, 85];
  for (const h of fakeBars) {
    const bar = document.createElement('div');
    bar.style.cssText = `flex:1;background:var(--accent);height:${h}%;border-radius:1px;`;
    barRow.appendChild(bar);
  }
  fakeChart.appendChild(barRow);
  blurContent.appendChild(fakeChart);

  // Fake breakdown rows
  const sections = ['TOP REFERRERS', 'COUNTRIES', 'DEVICES', 'PEAK HOURS'];
  for (const section of sections) {
    const sec = document.createElement('div');
    sec.style.cssText = 'border:1px solid var(--border);padding:12px;margin-bottom:8px;';

    const secTitle = document.createElement('div');
    secTitle.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;letter-spacing:1px;margin-bottom:8px;';
    secTitle.textContent = section;
    sec.appendChild(secTitle);

    for (let i = 0; i < 3; i++) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid var(--border);';

      const label = document.createElement('span');
      label.style.cssText = 'font-size:10px;color:#fff;font-family:"DM Mono",monospace;';
      label.textContent = ['instagram.com', 'United States', 'Desktop', '8:00 PM'][sections.indexOf(section)];

      const value = document.createElement('span');
      value.style.cssText = 'font-size:10px;color:var(--accent);font-family:"DM Mono",monospace;';
      value.textContent = Math.floor(Math.random() * 200 + 50);

      row.appendChild(label);
      row.appendChild(value);
      sec.appendChild(row);
    }
    blurContent.appendChild(sec);
  }
  blurWrap.appendChild(blurContent);

  // Upgrade CTA overlay
  const ctaOverlay = document.createElement('div');
  ctaOverlay.style.cssText = `
    position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
    text-align:center;z-index:10;background:rgba(10,10,10,0.85);
    padding:28px 36px;border:1px solid var(--accent);
  `;

  const ctaIcon = document.createElement('div');
  ctaIcon.style.cssText = 'font-size:28px;margin-bottom:10px;';
  ctaIcon.textContent = '\u2605';
  ctaOverlay.appendChild(ctaIcon);

  const ctaTitle = document.createElement('div');
  ctaTitle.style.cssText = 'font-size:14px;color:#fff;font-family:"DM Mono",monospace;letter-spacing:2px;margin-bottom:8px;';
  ctaTitle.textContent = 'ULTRA ANALYTICS';
  ctaOverlay.appendChild(ctaTitle);

  const ctaDesc = document.createElement('div');
  ctaDesc.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;margin-bottom:14px;line-height:1.5;max-width:240px;';
  ctaDesc.textContent = 'Referrer tracking, country breakdown, device analytics, peak hours, and daily trends.';
  ctaOverlay.appendChild(ctaDesc);

  const ctaBtn = document.createElement('button');
  ctaBtn.className = 'btn-small';
  ctaBtn.style.cssText = 'border-color:var(--accent);color:var(--accent);padding:8px 24px;font-size:11px;';
  ctaBtn.textContent = 'UPGRADE TO ULTRA';
  ctaBtn.onclick = () => {
    document.querySelector('.signin-modal-overlay')?.remove();
    showUpgradeModal();
  };
  ctaOverlay.appendChild(ctaBtn);

  blurWrap.appendChild(ctaOverlay);
  container.appendChild(blurWrap);
}

/**
 * Render full analytics for Ultra users.
 */
function renderFullAnalytics(container, data) {
  // ── Summary cards ──
  const summaryRow = document.createElement('div');
  summaryRow.style.cssText = 'display:flex;gap:8px;margin-bottom:16px;flex-wrap:wrap;';

  const statCards = [
    { label: 'TOTAL VIEWS', value: data.totalViews || 0 },
    { label: 'UNIQUE VISITORS', value: data.uniqueVisitors || 0 },
    { label: 'QR SCANS', value: data.qrScans || 0 },
  ];

  for (const stat of statCards) {
    const card = document.createElement('div');
    card.style.cssText = 'flex:1;min-width:100px;border:1px solid var(--border);padding:12px;text-align:center;';

    const val = document.createElement('div');
    val.style.cssText = 'font-size:22px;color:#fff;font-family:"DM Mono",monospace;letter-spacing:1px;';
    val.textContent = stat.value.toLocaleString();
    card.appendChild(val);

    const lbl = document.createElement('div');
    lbl.style.cssText = 'font-size:9px;color:var(--text-dim);font-family:"DM Mono",monospace;letter-spacing:1px;margin-top:3px;';
    lbl.textContent = stat.label;
    card.appendChild(lbl);

    summaryRow.appendChild(card);
  }
  container.appendChild(summaryRow);

  // ── Daily views chart (simple bar chart) ──
  if (data.daily && data.daily.length > 0) {
    const chartSection = document.createElement('div');
    chartSection.style.cssText = 'border:1px solid var(--border);padding:14px;margin-bottom:12px;';

    const chartTitle = document.createElement('div');
    chartTitle.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;letter-spacing:1px;margin-bottom:10px;';
    chartTitle.textContent = 'DAILY VIEWS';
    chartSection.appendChild(chartTitle);

    const maxViews = Math.max(...data.daily.map(d => d.views), 1);
    const barContainer = document.createElement('div');
    barContainer.style.cssText = 'display:flex;align-items:flex-end;gap:2px;height:100px;';

    // Show last 30 days max for readability
    const displayDays = data.daily.slice(-30);
    for (const day of displayDays) {
      const barWrap = document.createElement('div');
      barWrap.style.cssText = 'flex:1;display:flex;flex-direction:column;align-items:center;height:100%;justify-content:flex-end;';

      const bar = document.createElement('div');
      const heightPct = Math.max((day.views / maxViews) * 100, 2);
      bar.style.cssText = `width:100%;background:var(--accent);height:${heightPct}%;min-height:2px;border-radius:1px;`;
      bar.title = `${day.date}: ${day.views} views, ${day.unique_visitors} unique`;
      barWrap.appendChild(bar);
      barContainer.appendChild(barWrap);
    }
    chartSection.appendChild(barContainer);

    // Date labels (first and last)
    const dateLabels = document.createElement('div');
    dateLabels.style.cssText = 'display:flex;justify-content:space-between;margin-top:4px;';
    const firstDate = document.createElement('span');
    firstDate.style.cssText = 'font-size:8px;color:var(--text-dim);font-family:"DM Mono",monospace;';
    firstDate.textContent = displayDays[0]?.date || '';
    const lastDate = document.createElement('span');
    lastDate.style.cssText = 'font-size:8px;color:var(--text-dim);font-family:"DM Mono",monospace;';
    lastDate.textContent = displayDays[displayDays.length - 1]?.date || '';
    dateLabels.appendChild(firstDate);
    dateLabels.appendChild(lastDate);
    chartSection.appendChild(dateLabels);

    container.appendChild(chartSection);
  }

  // ── Helper to build breakdown sections ──
  function renderBreakdown(titleText, items, labelKey, valueKey, formatValue) {
    if (!items || items.length === 0) return;

    const section = document.createElement('div');
    section.style.cssText = 'border:1px solid var(--border);padding:12px;margin-bottom:8px;';

    const secTitle = document.createElement('div');
    secTitle.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;letter-spacing:1px;margin-bottom:8px;';
    secTitle.textContent = titleText;
    section.appendChild(secTitle);

    const total = items.reduce((sum, item) => sum + (item[valueKey] || 0), 0);

    for (const item of items) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:8px;padding:4px 0;border-bottom:1px solid var(--border);';

      // Progress bar background
      const barBg = document.createElement('div');
      barBg.style.cssText = 'position:absolute;left:0;top:0;bottom:0;background:var(--accent);opacity:0.07;';
      barBg.style.width = `${total > 0 ? (item[valueKey] / total) * 100 : 0}%`;

      const label = document.createElement('span');
      label.style.cssText = 'flex:1;font-size:10px;color:#fff;font-family:"DM Mono",monospace;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
      label.textContent = formatValue ? formatValue(item[labelKey]) : (item[labelKey] || 'Unknown');

      const value = document.createElement('span');
      value.style.cssText = 'font-size:10px;color:var(--accent);font-family:"DM Mono",monospace;white-space:nowrap;';
      value.textContent = `${item[valueKey]} (${total > 0 ? Math.round((item[valueKey] / total) * 100) : 0}%)`;

      row.appendChild(label);
      row.appendChild(value);
      section.appendChild(row);
    }

    container.appendChild(section);
  }

  // ── Referrers ──
  renderBreakdown('TOP REFERRERS', data.referrers, 'referrer', 'count', (ref) => {
    try {
      return new URL(ref).hostname;
    } catch (_) {
      return ref || 'Direct';
    }
  });

  // ── Countries ──
  renderBreakdown('COUNTRIES', data.countries, 'country', 'count');

  // ── Devices ──
  renderBreakdown('DEVICES', data.devices, 'device_type', 'count', (d) => (d || 'unknown').toUpperCase());

  // ── Peak hours ──
  if (data.hourly && data.hourly.length > 0) {
    const hourSection = document.createElement('div');
    hourSection.style.cssText = 'border:1px solid var(--border);padding:12px;margin-bottom:8px;';

    const hourTitle = document.createElement('div');
    hourTitle.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;letter-spacing:1px;margin-bottom:8px;';
    hourTitle.textContent = 'PEAK HOURS (UTC)';
    hourSection.appendChild(hourTitle);

    const maxHourly = Math.max(...data.hourly.map(h => h.count), 1);
    const hourChart = document.createElement('div');
    hourChart.style.cssText = 'display:flex;align-items:flex-end;gap:1px;height:50px;';

    // Fill all 24 hours
    for (let h = 0; h < 24; h++) {
      const entry = data.hourly.find(e => e.hour === h);
      const count = entry ? entry.count : 0;
      const bar = document.createElement('div');
      const pct = Math.max((count / maxHourly) * 100, 2);
      bar.style.cssText = `flex:1;background:var(--accent);height:${pct}%;min-height:1px;opacity:${count > 0 ? 1 : 0.2};`;
      bar.title = `${h}:00 UTC — ${count} views`;
      hourChart.appendChild(bar);
    }
    hourSection.appendChild(hourChart);

    const hourLabels = document.createElement('div');
    hourLabels.style.cssText = 'display:flex;justify-content:space-between;margin-top:3px;';
    for (const lbl of ['0', '6', '12', '18', '23']) {
      const s = document.createElement('span');
      s.style.cssText = 'font-size:7px;color:var(--text-dim);font-family:"DM Mono",monospace;';
      s.textContent = lbl;
      hourLabels.appendChild(s);
    }
    hourSection.appendChild(hourLabels);

    container.appendChild(hourSection);
  }

  // ── Per-subdomain breakdown (if multiple subdomains) ──
  if (data.perSubdomain && data.perSubdomain.length > 1) {
    renderBreakdown('SUBDOMAIN BREAKDOWN', data.perSubdomain, 'subdomain', 'views', (s) => s + '.stencil.cloud');
  }

  // ── Recent views ──
  if (data.recentViews && data.recentViews.length > 0) {
    const recentSection = document.createElement('div');
    recentSection.style.cssText = 'border:1px solid var(--border);padding:12px;margin-bottom:8px;';

    const recentTitle = document.createElement('div');
    recentTitle.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;letter-spacing:1px;margin-bottom:8px;';
    recentTitle.textContent = 'RECENT VIEWS';
    recentSection.appendChild(recentTitle);

    for (const view of data.recentViews) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:8px;padding:3px 0;border-bottom:1px solid var(--border);font-size:9px;font-family:"DM Mono",monospace;';

      const time = document.createElement('span');
      time.style.cssText = 'color:var(--text-dim);white-space:nowrap;';
      const d = new Date(view.timestamp * 1000);
      time.textContent = `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

      const country = document.createElement('span');
      country.style.cssText = 'color:var(--accent);min-width:24px;';
      country.textContent = view.country || '--';

      const device = document.createElement('span');
      device.style.cssText = 'color:var(--text-dim);';
      device.textContent = (view.device_type || 'unknown').toUpperCase();

      const ref = document.createElement('span');
      ref.style.cssText = 'color:#fff;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
      try {
        ref.textContent = view.referrer ? new URL(view.referrer).hostname : 'Direct';
      } catch (_) {
        ref.textContent = view.referrer || 'Direct';
      }

      row.appendChild(time);
      row.appendChild(country);
      row.appendChild(device);
      row.appendChild(ref);
      recentSection.appendChild(row);
    }

    container.appendChild(recentSection);
  }

  // Subdomains list
  if (data.subdomains && data.subdomains.length > 0) {
    const subHint = document.createElement('div');
    subHint.style.cssText = 'font-size:9px;color:var(--text-dim);font-family:"DM Mono",monospace;text-align:center;margin-top:8px;';
    subHint.textContent = 'Tracking: ' + data.subdomains.map(s => s + '.stencil.cloud').join(', ');
    container.appendChild(subHint);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ACCOUNT MODAL — user profile, billing, and account settings
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Open the Account modal.
 * Shows user profile info, tier/subscription status, and billing management.
 * Integrates the former "Manage Billing" link directly.
 */
function openAccountModal() {
  document.querySelector('.signin-modal-overlay')?.remove();

  const user = getUser();
  if (!user) return;

  const overlay = document.createElement('div');
  overlay.className = 'signin-modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'signin-modal';
  modal.style.minWidth = '420px';
  modal.style.maxWidth = '480px';

  const title = document.createElement('h2');
  title.textContent = 'ACCOUNT';
  modal.appendChild(title);

  // Profile section
  const profileSection = document.createElement('div');
  profileSection.style.cssText = 'margin-bottom:20px;';

  // Avatar + name row
  const profileRow = document.createElement('div');
  profileRow.style.cssText = 'display:flex;align-items:center;gap:12px;margin-bottom:14px;';

  if (user.avatarUrl) {
    const avatar = document.createElement('img');
    avatar.src = user.avatarUrl;
    avatar.style.cssText = 'width:40px;height:40px;border-radius:50%;border:1px solid var(--border);';
    profileRow.appendChild(avatar);
  } else {
    const avatarPlaceholder = document.createElement('div');
    avatarPlaceholder.style.cssText = `
      width:40px;height:40px;border-radius:50%;border:1px solid var(--border);
      display:flex;align-items:center;justify-content:center;
      font-family:"DM Mono",monospace;font-size:16px;color:var(--accent);
      background:var(--bg);
    `;
    avatarPlaceholder.textContent = (user.displayName || user.email || '?')[0].toUpperCase();
    profileRow.appendChild(avatarPlaceholder);
  }

  const profileInfo = document.createElement('div');
  const nameEl = document.createElement('div');
  nameEl.style.cssText = 'font-size:13px;color:#fff;font-family:"DM Mono",monospace;letter-spacing:0.5px;';
  nameEl.textContent = user.displayName || 'No display name';
  profileInfo.appendChild(nameEl);

  const emailEl = document.createElement('div');
  emailEl.style.cssText = 'font-size:10px;color:var(--text-dim);margin-top:2px;font-family:"DM Mono",monospace;';
  emailEl.textContent = user.email;
  profileInfo.appendChild(emailEl);

  profileRow.appendChild(profileInfo);
  profileSection.appendChild(profileRow);

  // Details grid
  const details = [
    ['Subdomain', `${user.subdomain}.stencil.cloud`],
    ['Tier', (user.realTier || user.tier || 'free').toUpperCase()],
    ['Auth Provider', (user.authProvider || 'email').toUpperCase()],
    ['Member Since', user.createdAt ? new Date(user.createdAt * 1000).toLocaleDateString() : 'N/A'],
  ];

  if (user.customSubdomains?.length > 0) {
    details.push(['Custom Subdomains', user.customSubdomains.join(', ')]);
  }

  if (user.timezone) {
    details.push(['Timezone', user.timezone]);
  }

  for (const [label, value] of details) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--border);';

    const lbl = document.createElement('span');
    lbl.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;letter-spacing:0.5px;';
    lbl.textContent = label;

    const val = document.createElement('span');
    val.style.cssText = 'font-size:10px;color:#fff;font-family:"DM Mono",monospace;text-align:right;';
    val.textContent = value;

    row.appendChild(lbl);
    row.appendChild(val);
    profileSection.appendChild(row);
  }

  modal.appendChild(profileSection);

  // Billing section
  const billingSection = document.createElement('div');
  billingSection.style.cssText = 'border-top:1px solid var(--border);padding-top:16px;';

  const billingTitle = document.createElement('div');
  billingTitle.style.cssText = 'font-size:11px;color:var(--accent);font-family:"DM Mono",monospace;letter-spacing:1px;margin-bottom:12px;';
  billingTitle.textContent = 'BILLING';
  billingSection.appendChild(billingTitle);

  const tier = user.realTier || user.tier || 'free';

  if (tier === 'free') {
    const upgradeHint = document.createElement('div');
    upgradeHint.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;margin-bottom:10px;';
    upgradeHint.textContent = 'You are on the Free tier. Upgrade for premium features.';
    billingSection.appendChild(upgradeHint);

    const upgradeBtn = document.createElement('button');
    upgradeBtn.className = 'btn-small';
    upgradeBtn.style.cssText = 'width:100%;border-color:var(--accent);color:var(--accent);';
    upgradeBtn.textContent = 'UPGRADE';
    upgradeBtn.onclick = () => { overlay.remove(); showUpgradeModal(); };
    billingSection.appendChild(upgradeBtn);
  } else if (['designer', 'ultra'].includes(tier)) {
    const manageHint = document.createElement('div');
    manageHint.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;margin-bottom:10px;';
    manageHint.textContent = `${tier.toUpperCase()} subscription active. Manage your billing, invoices, and payment method through Stripe.`;
    billingSection.appendChild(manageHint);

    const manageBtn = document.createElement('button');
    manageBtn.className = 'btn-small';
    manageBtn.style.cssText = 'width:100%;';
    manageBtn.textContent = 'MANAGE BILLING';
    manageBtn.onclick = async () => {
      manageBtn.textContent = 'LOADING...';
      manageBtn.disabled = true;
      try {
        const resp = await fetch('/api/subscription/portal', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await resp.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          alert(data.error || 'Unable to open billing portal');
          manageBtn.textContent = 'MANAGE BILLING';
          manageBtn.disabled = false;
        }
      } catch (e) {
        alert('Error: ' + e.message);
        manageBtn.textContent = 'MANAGE BILLING';
        manageBtn.disabled = false;
      }
    };
    billingSection.appendChild(manageBtn);
  } else {
    // QA or other special tiers
    const tierInfo = document.createElement('div');
    tierInfo.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;';
    tierInfo.textContent = `${tier.toUpperCase()} tier — special account.`;
    billingSection.appendChild(tierInfo);
  }

  modal.appendChild(billingSection);

  overlay.appendChild(modal);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
}

// ─────────────────────────────────────────────────────────────────────────────
// DESIGNER KIT MODAL — editor/kit preferences
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Open the Designer Kit modal.
 * Kit-level settings: export defaults, snap-to-grid, auto-save preferences,
 * and editor display preferences.
 */
function openDesignerKitModal() {
  document.querySelector('.signin-modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'signin-modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'signin-modal';
  modal.style.minWidth = '420px';
  modal.style.maxWidth = '480px';

  const title = document.createElement('h2');
  title.textContent = 'DESIGNER KIT';
  modal.appendChild(title);

  const hint = document.createElement('div');
  hint.style.cssText = 'text-align:center;font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;margin-bottom:16px;';
  hint.textContent = 'Editor preferences for your design workflow';
  modal.appendChild(hint);

  // ── Snap-to-Grid ──
  const snapSection = document.createElement('div');
  snapSection.style.cssText = 'margin-bottom:16px;';

  const snapTitle = document.createElement('div');
  snapTitle.style.cssText = 'font-size:11px;color:var(--accent);font-family:"DM Mono",monospace;letter-spacing:1px;margin-bottom:8px;';
  snapTitle.textContent = 'GRID & SNAPPING';
  snapSection.appendChild(snapTitle);

  const snapRow = document.createElement('div');
  snapRow.className = 'check-row';
  const snapCb = document.createElement('input');
  snapCb.type = 'checkbox';
  snapCb.checked = snapEnabled;
  snapCb.id = 'dk-snap';
  const snapLbl = document.createElement('label');
  snapLbl.htmlFor = 'dk-snap';
  snapLbl.className = 'checkbox-label';
  snapLbl.textContent = 'Enable snap-to-grid';
  snapRow.appendChild(snapCb);
  snapRow.appendChild(snapLbl);
  snapSection.appendChild(snapRow);

  const gridLabel = document.createElement('label');
  gridLabel.className = 'field-label';
  gridLabel.textContent = 'Grid Size (px)';
  snapSection.appendChild(gridLabel);

  const gridInput = document.createElement('input');
  gridInput.className = 'field-input';
  gridInput.type = 'number';
  gridInput.min = '5';
  gridInput.max = '100';
  gridInput.step = '5';
  gridInput.value = snapGridSize;
  gridInput.style.width = '100px';
  snapSection.appendChild(gridInput);

  modal.appendChild(snapSection);

  // ── Editor Mode ──
  const modeSection = document.createElement('div');
  modeSection.style.cssText = 'margin-bottom:16px;';

  const modeTitle = document.createElement('div');
  modeTitle.style.cssText = 'font-size:11px;color:var(--accent);font-family:"DM Mono",monospace;letter-spacing:1px;margin-bottom:8px;';
  modeTitle.textContent = 'EDITOR MODE';
  modeSection.appendChild(modeTitle);

  const modeInfo = document.createElement('div');
  modeInfo.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;margin-bottom:8px;line-height:1.5;';
  modeInfo.textContent = editorMode === 'template'
    ? 'TEMPLATE mode — layout engine positions elements automatically.'
    : 'ADVANCED mode — free placement, elements stay where you put them.';
  modeSection.appendChild(modeInfo);

  const modeBtn = document.createElement('button');
  modeBtn.className = 'btn-small';
  modeBtn.style.width = '100%';
  modeBtn.textContent = editorMode === 'template' ? 'SWITCH TO ADVANCED' : 'SWITCH TO TEMPLATE';
  modeBtn.onclick = () => {
    const prevMode = editorMode;
    // Save current positions into the outgoing mode's store
    const outStore = prevMode === 'advanced' ? advancedPositions : templatePositions;
    savePositions(currentSize, outStore);

    editorMode = editorMode === 'template' ? 'advanced' : 'template';
    modeInfo.textContent = editorMode === 'template'
      ? 'TEMPLATE mode — layout engine positions elements automatically.'
      : 'ADVANCED mode — free placement, elements stay where you put them.';
    modeBtn.textContent = editorMode === 'template' ? 'SWITCH TO ADVANCED' : 'SWITCH TO TEMPLATE';

    // Apply the mode change to the canvas
    if (editorMode === 'advanced') {
      if (advancedPositions[currentSize]) {
        restorePositions(currentSize, currentSize, advancedPositions);
      }
    } else {
      // Template mode: re-run layout engine
      const template = TEMPLATES[currentState.template];
      const scheme = getScheme(currentState.scheme.name || 'dark');
      hydrateTemplate(template, scheme, currentSize);
    }
    pushUndoState(fabricCanvas, currentState, currentSize);
    buildSidebar();
    logAction('Editor mode: ' + editorMode);

    // Close the modal after applying
    overlay.remove();
  };
  modeSection.appendChild(modeBtn);

  modal.appendChild(modeSection);

  // ── Export Defaults ──
  const exportSection = document.createElement('div');
  exportSection.style.cssText = 'margin-bottom:16px;';

  const exportTitle = document.createElement('div');
  exportTitle.style.cssText = 'font-size:11px;color:var(--accent);font-family:"DM Mono",monospace;letter-spacing:1px;margin-bottom:8px;';
  exportTitle.textContent = 'EXPORT';
  exportSection.appendChild(exportTitle);

  const exportHint = document.createElement('div');
  exportHint.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;line-height:1.5;';
  exportHint.textContent = 'PNG exports render at full template resolution regardless of display scale. Use batch export to generate all sizes at once.';
  exportSection.appendChild(exportHint);

  modal.appendChild(exportSection);

  // ── Actions ──
  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;gap:8px;margin-top:16px;justify-content:flex-end;';

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'btn-small';
  cancelBtn.textContent = 'CANCEL';
  cancelBtn.onclick = () => overlay.remove();

  const applyBtn = document.createElement('button');
  applyBtn.className = 'btn-small';
  applyBtn.style.cssText = 'border-color:var(--accent);color:var(--accent);';
  applyBtn.textContent = 'APPLY';
  applyBtn.onclick = () => {
    snapEnabled = snapCb.checked;
    snapGridSize = parseInt(gridInput.value, 10) || 20;
    logAction('Kit settings applied');
    overlay.remove();
  };

  actions.appendChild(cancelBtn);
  actions.appendChild(applyBtn);
  modal.appendChild(actions);

  overlay.appendChild(modal);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
}

// ─────────────────────────────────────────────────────────────────────────────
// QA TIER MODAL — switch simulated tier for QA testing
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Open the QA Tier switching modal.
 * QA users can simulate Free or Ultra tiers to test feature gating.
 * Switching reloads the page so all API calls pick up the new header.
 */
function openQATierModal() {
  document.querySelector('.signin-modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'signin-modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'signin-modal';
  modal.style.minWidth = '360px';
  modal.style.maxWidth = '400px';

  const title = document.createElement('h2');
  title.textContent = 'QA TESTING';
  modal.appendChild(title);

  const hint = document.createElement('div');
  hint.style.cssText = 'text-align:center;font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;margin-bottom:16px;line-height:1.5;';
  hint.textContent = 'Simulate a subscription tier to test feature gating. Switching will reload the page.';
  modal.appendChild(hint);

  const currentTier = getQATier() || 'free';

  const tiers = [
    { key: null, label: 'FREE', desc: 'Default free tier — limited features' },
    { key: 'ultra', label: 'ULTRA', desc: 'Full Ultra Kit — all premium features' },
  ];

  for (const tier of tiers) {
    const card = document.createElement('div');
    const isActive = (tier.key === null && !getQATier()) || (tier.key === currentTier);
    card.style.cssText = `
      border:1px solid ${isActive ? 'var(--accent)' : 'var(--border)'};
      padding:14px;margin-bottom:8px;cursor:pointer;transition:border-color 0.15s;
    `;
    card.onmouseenter = () => { if (!isActive) card.style.borderColor = 'var(--text-dim)'; };
    card.onmouseleave = () => { if (!isActive) card.style.borderColor = 'var(--border)'; };

    const tierLabel = document.createElement('div');
    tierLabel.style.cssText = `font-size:13px;font-family:"DM Mono",monospace;letter-spacing:2px;color:${isActive ? 'var(--accent)' : '#fff'};margin-bottom:4px;`;
    tierLabel.textContent = tier.label + (isActive ? ' \u2713' : '');
    card.appendChild(tierLabel);

    const tierDesc = document.createElement('div');
    tierDesc.style.cssText = 'font-size:10px;color:var(--text-dim);font-family:"DM Mono",monospace;';
    tierDesc.textContent = tier.desc;
    card.appendChild(tierDesc);

    card.onclick = () => {
      if (isActive) return;
      setQATier(tier.key); // This reloads the page
    };

    modal.appendChild(card);
  }

  // Close button
  const closeBtn = document.createElement('button');
  closeBtn.className = 'btn-small';
  closeBtn.style.cssText = 'width:100%;margin-top:8px;';
  closeBtn.textContent = 'CLOSE';
  closeBtn.onclick = () => overlay.remove();
  modal.appendChild(closeBtn);

  overlay.appendChild(modal);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE SETTINGS MODAL — configure public subdomain landing page
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Open the Page Settings modal.
 * Loads current settings from API, provides form to edit, saves on submit.
 *
 * Settings control the public-facing subdomain page:
 * - Headline, bio
 * - Links (up to 10)
 * - CTA button text/URL
 * - Venue text
 * - Accent and background colors
 * - Show/hide toggles for sections
 */
async function openPageSettingsModal() {
  const overlay = document.createElement('div');
  overlay.className = 'scheduler-overlay';

  const panel = document.createElement('div');
  panel.className = 'scheduler-panel';
  panel.style.maxWidth = '540px';

  const title = document.createElement('h3');
  title.textContent = 'PAGE SETTINGS';
  panel.appendChild(title);

  const hint = document.createElement('div');
  hint.className = 'hint';
  hint.style.marginBottom = '12px';
  hint.textContent = 'Configure your public subdomain landing page';
  panel.appendChild(hint);

  // Loading state
  const loading = document.createElement('div');
  loading.style.cssText = 'text-align:center;padding:20px;color:var(--text-dim);font-size:11px;font-family:"DM Mono",monospace;';
  loading.textContent = 'Loading settings...';
  panel.appendChild(loading);

  overlay.appendChild(panel);
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  document.body.appendChild(overlay);

  // Fetch current settings
  let settings;
  try {
    const res = await fetch('/api/page-settings', { credentials: 'include' });
    settings = await res.json();
  } catch (e) {
    loading.textContent = 'Failed to load settings.';
    return;
  }

  loading.remove();

  // Helper: make a labeled text input
  function makeField(label, key, placeholder, titleText, type = 'text') {
    const lbl = document.createElement('label');
    lbl.className = 'field-label';
    lbl.textContent = label;
    lbl.title = titleText;
    const inp = document.createElement('input');
    inp.type = type;
    inp.className = type === 'color' ? 'field-color' : 'field-input';
    inp.value = settings[key] || '';
    inp.placeholder = placeholder;
    inp.title = titleText;
    inp.dataset.key = key;
    panel.appendChild(lbl);
    panel.appendChild(inp);
    return inp;
  }

  // Helper: make a toggle
  function makeToggle(label, key, titleText) {
    const row = document.createElement('div');
    row.className = 'check-row';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = settings[key] !== false;
    cb.id = `ps-${key}`;
    cb.dataset.key = key;
    cb.title = titleText;
    const lbl = document.createElement('label');
    lbl.htmlFor = `ps-${key}`;
    lbl.className = 'checkbox-label';
    lbl.textContent = label;
    lbl.title = titleText;
    row.appendChild(cb);
    row.appendChild(lbl);
    panel.appendChild(row);
    return cb;
  }

  // Build form fields
  const headlineInput = makeField('Headline', 'headline', 'Your event or brand name', 'Bold text displayed above the campaign image on your public page');
  const bioInput = makeField('Bio', 'bio', 'Short description', 'Description shown below the headline');

  // CTA section
  const ctaToggle = makeToggle('Show CTA Button', 'show_cta', 'Display a call-to-action button on your public page');
  const ctaTextInput = makeField('CTA Text', 'cta_text', 'LEARN MORE', 'Text displayed on the CTA button');
  const ctaUrlInput = makeField('CTA URL', 'cta_url', 'https://...', 'URL the CTA button links to');

  // Venue
  const venueToggle = makeToggle('Show Venue', 'show_venue', 'Display a venue/location line on your public page');
  const venueInput = makeField('Venue', 'venue_text', 'The Grand Ballroom, NYC', 'Venue name and/or address');

  // Date
  const dateToggle = makeToggle('Show Event Date', 'show_date', 'Display the event date from your campaign');

  // Colors
  const colorRow = document.createElement('div');
  colorRow.style.cssText = 'display:flex;gap:12px;margin-top:8px;';
  const accentLbl = document.createElement('label');
  accentLbl.className = 'field-label';
  accentLbl.textContent = 'Accent Color';
  accentLbl.title = 'Accent color for buttons and links on your public page';
  const accentInput = document.createElement('input');
  accentInput.type = 'color';
  accentInput.className = 'field-color';
  accentInput.value = settings.accent_color || '#e040a0';
  accentInput.title = 'Accent color for buttons and links';

  const bgLbl = document.createElement('label');
  bgLbl.className = 'field-label';
  bgLbl.textContent = 'Background';
  bgLbl.title = 'Background color of your public page';
  const bgInput = document.createElement('input');
  bgInput.type = 'color';
  bgInput.className = 'field-color';
  bgInput.value = settings.bg_color || '#0d0d0d';
  bgInput.title = 'Background color of your public page';

  const accentWrap = document.createElement('div');
  accentWrap.appendChild(accentLbl);
  accentWrap.appendChild(accentInput);
  const bgWrap = document.createElement('div');
  bgWrap.appendChild(bgLbl);
  bgWrap.appendChild(bgInput);
  colorRow.appendChild(accentWrap);
  colorRow.appendChild(bgWrap);
  panel.appendChild(colorRow);

  // Branding
  const brandingToggle = makeToggle('Show "Made with stencil.cloud"', 'show_branding', 'Display the stencil.cloud attribution in the footer');

  // Links section
  const linksLabel = document.createElement('label');
  linksLabel.className = 'field-label';
  linksLabel.textContent = 'Links (up to 10)';
  linksLabel.title = 'Custom links displayed as buttons on your public page';
  linksLabel.style.marginTop = '10px';
  panel.appendChild(linksLabel);

  const linksContainer = document.createElement('div');
  linksContainer.id = 'ps-links-container';
  panel.appendChild(linksContainer);

  const currentLinks = Array.isArray(settings.links) ? [...settings.links] : [];

  function renderLinks() {
    linksContainer.innerHTML = '';
    currentLinks.forEach((link, i) => {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:4px;margin-bottom:4px;align-items:center;';
      const labelInp = document.createElement('input');
      labelInp.className = 'field-input';
      labelInp.style.flex = '1';
      labelInp.value = link.label;
      labelInp.placeholder = 'Label';
      labelInp.title = 'Display text for this link button';
      labelInp.oninput = () => { currentLinks[i].label = labelInp.value; };
      const urlInp = document.createElement('input');
      urlInp.className = 'field-input';
      urlInp.style.flex = '2';
      urlInp.value = link.url;
      urlInp.placeholder = 'https://...';
      urlInp.title = 'URL this link opens';
      urlInp.oninput = () => { currentLinks[i].url = urlInp.value; };
      const rmBtn = document.createElement('button');
      rmBtn.className = 'btn-small';
      rmBtn.textContent = '\u2715';
      rmBtn.title = 'Remove this link';
      rmBtn.style.padding = '4px 8px';
      rmBtn.onclick = () => { currentLinks.splice(i, 1); renderLinks(); };
      row.appendChild(labelInp);
      row.appendChild(urlInp);
      row.appendChild(rmBtn);
      linksContainer.appendChild(row);
    });
  }
  renderLinks();

  if (currentLinks.length < 10) {
    const addLinkBtn = document.createElement('button');
    addLinkBtn.className = 'btn-small';
    addLinkBtn.style.cssText = 'width:100%;margin-top:4px;';
    addLinkBtn.textContent = '+ Add Link';
    addLinkBtn.title = 'Add a new link to your public page (max 10)';
    addLinkBtn.onclick = () => {
      if (currentLinks.length >= 10) return;
      currentLinks.push({ label: '', url: '' });
      renderLinks();
    };
    panel.appendChild(addLinkBtn);
  }

  // Action buttons
  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;gap:8px;margin-top:16px;justify-content:flex-end;';

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'btn-small';
  cancelBtn.textContent = 'CANCEL';
  cancelBtn.onclick = () => overlay.remove();

  const saveBtn = document.createElement('button');
  saveBtn.className = 'btn-small';
  saveBtn.style.cssText = 'border-color:var(--accent);color:var(--accent);';
  saveBtn.textContent = 'SAVE';
  saveBtn.title = 'Save page settings — changes are reflected immediately on your public subdomain';
  saveBtn.onclick = async () => {
    saveBtn.textContent = 'SAVING...';
    saveBtn.disabled = true;

    const body = {
      headline: headlineInput.value,
      bio: bioInput.value,
      links: currentLinks.filter(l => l.label && l.url),
      show_cta: ctaToggle.checked,
      cta_text: ctaTextInput.value || 'LEARN MORE',
      cta_url: ctaUrlInput.value,
      show_venue: venueToggle.checked,
      venue_text: venueInput.value,
      show_date: dateToggle.checked,
      accent_color: accentInput.value,
      bg_color: bgInput.value,
      show_branding: brandingToggle.checked,
    };

    try {
      const res = await fetch('/api/page-settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Save failed');
      }
      logAction('Page settings saved');
      overlay.remove();
    } catch (e) {
      alert('Failed to save: ' + e.message);
      saveBtn.textContent = 'SAVE';
      saveBtn.disabled = false;
    }
  };

  actions.appendChild(cancelBtn);
  actions.appendChild(saveBtn);
  panel.appendChild(actions);
}

/**
 * Initialize the editor.
 */
export async function init() {
  // Initialize auth state (checks session cookie)
  await initAuth();

  // QA tier simulation — inject X-QA-Tier header into all /api/ requests
  if (isQA() && getQATier()) {
    const _origFetch = window.fetch;
    window.fetch = function(url, opts = {}) {
      if (typeof url === 'string' && url.startsWith('/api/')) {
        opts.headers = Object.assign({}, opts.headers || {}, { 'X-QA-Tier': getQATier() });
      }
      return _origFetch.call(this, url, opts);
    };
  }

  // ── Preload Google Fonts before any canvas rendering ──
  // document.fonts.ready resolves immediately if no fonts are in the loading
  // queue — which happens when the Google Fonts CSS hasn't been parsed yet.
  // We must explicitly trigger loads for every font family the templates use,
  // then wait for them all to finish. This ensures Fabric.js textboxes render
  // with the correct fonts from the start, not serif fallbacks.
  const REQUIRED_FONTS = [
    '400 16px "DM Mono"',
    '400 16px "Work Sans"',
    '700 16px "Work Sans"',
    '700 16px "Bebas Neue"',
    '400 16px "Inter"',
    '600 16px "Inter"',
    '400 16px "Space Grotesk"',
    '700 16px "Space Grotesk"',
    '400 16px "Oswald"',
    '700 16px "Oswald"',
    '700 16px "Outfit"',
    '400 16px "Montserrat"',
    '700 16px "Montserrat"',
    '400 16px "Archivo Black"',
    '400 16px "Righteous"',
    '400 16px "Barlow Condensed"',
    '700 16px "Barlow Condensed"',
    '400 16px "Anton"',
    '400 16px "Orbitron"',
    '700 16px "Orbitron"',
  ];
  try {
    await Promise.all(REQUIRED_FONTS.map(f => document.fonts.load(f)));
  } catch (e) {
    console.warn('Some fonts failed to preload:', e);
  }

  // Create design state
  currentState = createDesignState();
  currentSize = DEFAULT_SIZE;

  // Initialize Fabric canvas
  const canvasEl = document.getElementById('design-canvas');
  fabricCanvas = new fabric.Canvas(canvasEl, {
    backgroundColor: '#0d0d0d',
    preserveObjectStacking: true,
    selection: true,
  });

  // ── Check for shared design link in URL hash ──
  // Share links (#/d/v1.<payload>) take priority over localStorage state.
  // The recipient gets a copy of the shared design to customize.
  let sharedDesign = null;
  if (hasShareLink()) {
    try {
      const decoded = await decodeShareLink();
      if (decoded?.state) {
        sharedDesign = decoded;
        // Store referral attribution for future tracking
        if (decoded.ref) {
          try { localStorage.setItem('stencil-referred-by', decoded.ref); } catch(_) {}
        }
        // Clear the hash so it doesn't persist in the URL bar
        clearShareHash();
      }
    } catch (err) {
      console.error('Failed to load shared design:', err);
    }
  }

  // Restore persisted state if available (shared design overrides localStorage)
  const saved = sharedDesign ? null : loadEditorState();

  if (sharedDesign) {
    // ── Hydrate from shared design link ──
    const st = sharedDesign.state;
    currentState.template = st.template || 'generic-event';
    currentSize = st.size || DEFAULT_SIZE;
    const schemeName = st.scheme || 'dark';
    const scheme = getScheme(schemeName);
    currentState.scheme = { ...scheme, name: schemeName };
    currentState.overlays = st.overlays || {
      lineArt: { type: 'flow', density: 50, opacity: 25, seed: 42 },
      lineArt2: { type: 'none', density: 30, opacity: 20, seed: 137 },
      grain: { enabled: true, density: 40, opacity: 15 },
    };
    currentState.eventDateISO = st.eventDateISO || '';
    currentState.dateFormat = st.dateFormat || 'dayMonth';
    currentState.eventTime = st.eventTime || '';

    // Wordmark
    if (st.wordmark) {
      currentState._wordmarkText = st.wordmark.text;
      currentState._wordmarkFont = st.wordmark.font;
      currentState._wordmarkColor = st.wordmark.color;
      currentState._wordmarkBold = st.wordmark.bold;
      currentState._wordmarkItalic = st.wordmark.italic;
      currentState._wordmarkUppercase = st.wordmark.uppercase;
      currentState._wordmarkAccent = st.wordmark.accent;
    }

    // Mangle
    if (st.mangleSeed != null) setMangleSeed(st.mangleSeed);
    if (st.mangleParams) currentState._mangleParams = st.mangleParams;

    // Text style / alignment overrides
    if (st.textStyleOverrides) Object.assign(textStyleOverrides, st.textStyleOverrides);
    if (st.groupAlignOverrides) Object.assign(groupAlignOverrides, st.groupAlignOverrides);

    // User-created text objects
    if (st.userTextObjects) userTextObjects = st.userTextObjects;

    const template = TEMPLATES[currentState.template];
    buildSidebar();
    hydrateTemplate(template, scheme, currentSize);

    // Apply text edits
    if (st.textEdits) {
      fabricCanvas.getObjects().forEach(obj => {
        if (obj.data?.id && st.textEdits[obj.data.id]) {
          obj.set('text', st.textEdits[obj.data.id]);
        }
      });
      fabricCanvas.renderAll();
    }

    logAction('Loaded shared design');
  } else if (saved) {
    // Restore persisted session
    currentState.template = saved.template || 'generic-event';
    currentSize = saved.size || DEFAULT_SIZE;
    const schemeName = saved.scheme || 'dark';
    const scheme = getScheme(schemeName);
    currentState.scheme = { ...scheme, name: schemeName };
    currentState.overlays = saved.overlays || {
      lineArt: { type: 'flow', density: 50, opacity: 25, seed: 42 },
      lineArt2: { type: 'none', density: 30, opacity: 20, seed: 137 },
      grain: { enabled: true, density: 40, opacity: 15 },
    };
    currentState.eventDateISO = saved.eventDateISO || '';
    currentState._mangleParams = saved.mangleParams || { spread: 50, angle: 0, colorShift: 0, styleShift: 0 };
    currentState.blendLayers = saved.blendLayers || [];
    currentState.dateFormat = saved.dateFormat || 'dayMonth';
    currentState.eventTime = saved.eventTime || '';

    // Restore wordmark config
    if (saved.wordmark) {
      currentState._wordmarkText = saved.wordmark.text;
      currentState._wordmarkFont = saved.wordmark.font;
      currentState._wordmarkColor = saved.wordmark.color;
      currentState._wordmarkBold = saved.wordmark.bold;
      currentState._wordmarkItalic = saved.wordmark.italic;
      currentState._wordmarkUppercase = saved.wordmark.uppercase;
      currentState._wordmarkAccent = saved.wordmark.accent;
    }

    // Restore section collapse state
    if (saved.sectionCollapsed) {
      Object.assign(sectionCollapsed, saved.sectionCollapsed);
    }

    // Restore snap state
    snapEnabled = saved.snapEnabled || false;
    snapGridSize = saved.snapGridSize || 20;

    // Restore editor mode + position stores
    editorMode = saved.editorMode || 'template';
    if (saved.advancedPositions) {
      Object.assign(advancedPositions, saved.advancedPositions);
    }
    if (saved.templatePositions) {
      Object.assign(templatePositions, saved.templatePositions);
    }
    if (saved.userModifiedSizes) {
      saved.userModifiedSizes.forEach(s => userModifiedSizes.add(s));
    }
    if (saved.userTextObjects) {
      userTextObjects = saved.userTextObjects;
    }
    if (saved.textStyleOverrides) {
      Object.assign(textStyleOverrides, saved.textStyleOverrides);
    }
    if (saved.groupAlignOverrides) {
      Object.assign(groupAlignOverrides, saved.groupAlignOverrides);
    }

    // Restore mangle seed
    if (saved.mangleSeed != null) setMangleSeed(saved.mangleSeed);

    const template = TEMPLATES[currentState.template];
    buildSidebar();
    hydrateTemplate(template, scheme, currentSize);

    // Apply saved text edits after hydration
    if (saved.textEdits) {
      fabricCanvas.getObjects().forEach(obj => {
        if (obj.data?.id && saved.textEdits[obj.data.id]) {
          obj.set('text', saved.textEdits[obj.data.id]);
        }
      });
      fabricCanvas.renderAll();
    }

    // Restore advanced positions if in advanced mode
    if (editorMode === 'advanced' && advancedPositions[currentSize]) {
      restoreAdvancedPositions(currentSize, currentSize);
    }

    logAction('Restored previous session');
  } else {
    // Load default template BEFORE building sidebar so controls reflect template state
    const defaultTemplate = TEMPLATES['generic-event'];
    const defaultScheme = getScheme(defaultTemplate.defaultScheme || 'dark');
    currentState.template = 'generic-event';
    currentState.scheme = { ...defaultScheme, name: defaultTemplate.defaultScheme || 'dark' };
    currentState.overlays = {
      lineArt: { ...defaultTemplate.overlays.lineArt },
      lineArt2: { type: 'none', density: 30, opacity: 0.2, seed: 137 },
      grain: { ...defaultTemplate.overlays.grain },
    };

    buildSidebar();
    hydrateTemplate(defaultTemplate, defaultScheme, currentSize);
  }

  // Debug: expose canvas for inspection
  window.__stencilCanvas = fabricCanvas;

  // Setup interaction handlers
  setupSelectionHandlers();
  setupCanvasContextMenu();

  // Snap-to-grid on object movement
  fabricCanvas.on('object:moving', (e) => {
    if (!snapEnabled) return;
    const obj = e.target;
    obj.set({
      left: Math.round(obj.left / snapGridSize) * snapGridSize,
      top: Math.round(obj.top / snapGridSize) * snapGridSize,
    });
  });

  // Push undo on object drag/resize/rotate, sync text backgrounds
  fabricCanvas.on('object:modified', (e) => {
    const obj = e.target;
    // Sync text background position when its text object moves/resizes
    if (obj?.type === 'textbox' && obj.data?.textBg?.enabled) {
      applyTextBackground(obj);
    }
    // Sync blend layer state when dragged/resized
    if (obj?.data?.stencilType === 'blendLayer') {
      syncBlendLayerToState(obj);
    }
    pushUndoState(fabricCanvas, currentState, currentSize);
  });

  // Also sync text backgrounds during movement (real-time follow)
  fabricCanvas.on('object:moving', (e) => {
    const obj = e.target;
    if (obj?.type === 'textbox' && obj.data?.textBg?.enabled) {
      applyTextBackground(obj);
      fabricCanvas.renderAll();
    }
  });

  // Keyboard shortcuts for undo/redo
  document.addEventListener('keydown', (e) => {
    const mod = e.metaKey || e.ctrlKey;
    // Don't intercept when editing text on canvas
    const active = fabricCanvas.getActiveObject();
    if (active && active.isEditing) return;

    if (mod && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      undo(applySnapshotToEditor);
      logAction('Undo');
    }
    if (mod && e.key === 'z' && e.shiftKey) {
      e.preventDefault();
      redo(applySnapshotToEditor);
      logAction('Redo');
    }
    if (mod && e.key === 'y') {
      e.preventDefault();
      redo(applySnapshotToEditor);
      logAction('Redo');
    }

    // R key → open remix (only when no modifier keys, no text editing, no modal open)
    if (e.key === 'r' && !mod && !e.shiftKey && !e.altKey) {
      // Don't intercept if a text input/textarea is focused
      const focused = document.activeElement;
      if (focused && (focused.tagName === 'INPUT' || focused.tagName === 'TEXTAREA' || focused.tagName === 'SELECT')) return;
      // Don't intercept if a modal is open
      if (document.querySelector('.signin-modal-overlay') || document.querySelector('.remix-modal-overlay')) return;

      e.preventDefault();
      openRemixModal(true);
    }

    // Cmd/Ctrl+V → open Paste & Parse when no text input is focused
    if (mod && e.key === 'v') {
      const focused = document.activeElement;
      // Only intercept if nothing editable is focused (sidebar inputs, textareas, canvas text editing)
      if (focused && (focused.tagName === 'INPUT' || focused.tagName === 'TEXTAREA' || focused.tagName === 'SELECT' || focused.isContentEditable)) return;
      if (active && active.isEditing) return;
      // Don't open if a modal is already open
      if (document.querySelector('.paste-parse-overlay') || document.querySelector('.remix-modal-overlay') || document.querySelector('.share-modal-overlay')) return;

      e.preventDefault();
      openPasteParseModal();
    }
  });

  // ── Export dropdown ──
  setupExportDropdown();

  // AI Designer button — show for all users (gated inside the modal)
  const aiBtn = document.getElementById('ai-btn');
  if (aiBtn) {
    aiBtn.style.display = '';
    aiBtn.onclick = () => openAiDesigner();
  }

  // Add feedback button
  setupFeedbackButton();

  // Header Share button — opens share modal (design link, not social share)
  const headerShareBtn = document.getElementById('share-btn');
  if (headerShareBtn) {
    headerShareBtn.onclick = () => openShareModal();
  }

  // Action log overlay (replaces sidebar section)
  setupActionLogOverlay();

  // Auth UI (sign-in button, user menu, banner)
  setupAuthUI();

  // Initial object panel state
  clearObjectPanel();

  // Push initial state onto undo stack
  pushUndoState(fabricCanvas, currentState, currentSize);

  // State persistence — auto-save
  setupPersistence();

  // Resize canvas when window resizes — rebuild to recalculate display scale
  // Preserves user positions across resize events
  let _resizeTimer = null;
  window.addEventListener('resize', () => {
    if (_resizeTimer) clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(() => {
      if (!fabricCanvas || !currentSize) return;
      const store = editorMode === 'advanced' ? advancedPositions : templatePositions;
      savePositions(currentSize, store);
      const template = TEMPLATES[currentState.template];
      const scheme = getScheme(currentState.scheme.name || 'dark');
      if (template) {
        hydrateTemplate(template, scheme, currentSize);
        restorePositions(currentSize, currentSize, store);
      }
    }, 200);
  });

  // Mobile: tab bar + bottom sheet system
  if (window.innerWidth <= 800) {
    setupMobileUI();
  }
}

// ─────────────────────────────────────────────────
// MOBILE UI — Tab bar + Bottom sheet
// ─────────────────────────────────────────────────

const MOBILE_TABS = [
  { id: 'design',  icon: '🎨', label: 'DESIGN' },
  { id: 'layout',  icon: '📐', label: 'LAYOUT' },
  { id: 'text',    icon: '🔤', label: 'TEXT' },
  { id: 'fx',      icon: '✦',  label: 'FX' },
  { id: 'more',    icon: '•••', label: 'MORE' },
];

let _mobileSheet = null;
let _mobileTabBar = null;
let _activeTab = null;

function setupMobileUI() {
  // Remove old drawer toggle if present
  const oldToggle = document.getElementById('mobile-drawer-toggle');
  if (oldToggle) oldToggle.style.display = 'none';

  // Build tab bar
  _mobileTabBar = document.createElement('div');
  _mobileTabBar.className = 'mobile-tab-bar';

  for (const tab of MOBILE_TABS) {
    const btn = document.createElement('button');
    btn.className = 'tab-btn';
    btn.dataset.tab = tab.id;
    btn.innerHTML = `<span class="tab-icon">${tab.icon}</span>${tab.label}`;
    btn.onclick = () => toggleMobileTab(tab.id);
    _mobileTabBar.appendChild(btn);
  }
  document.body.appendChild(_mobileTabBar);

  // Build bottom sheet
  _mobileSheet = document.createElement('div');
  _mobileSheet.className = 'mobile-sheet';

  const handle = document.createElement('div');
  handle.className = 'mobile-sheet-handle';
  _mobileSheet.appendChild(handle);

  const header = document.createElement('div');
  header.className = 'mobile-sheet-header';
  const title = document.createElement('span');
  title.className = 'mobile-sheet-title';
  title.id = 'mobile-sheet-title';
  title.textContent = '';
  const closeBtn = document.createElement('button');
  closeBtn.className = 'mobile-sheet-close';
  closeBtn.innerHTML = '&times;';
  closeBtn.onclick = () => closeMobileSheet();
  header.appendChild(title);
  header.appendChild(closeBtn);
  _mobileSheet.appendChild(header);

  const content = document.createElement('div');
  content.className = 'mobile-sheet-content';
  content.id = 'mobile-sheet-content';
  _mobileSheet.appendChild(content);

  document.body.appendChild(_mobileSheet);

  // Setup drag-to-dismiss on handle
  setupSheetDrag(handle, _mobileSheet);

  // Setup long-press for context menu on touch
  setupTouchContextMenu();

  // Listen for object selection to auto-switch to Text tab
  fabricCanvas.on('selection:created', onMobileObjectSelected);
  fabricCanvas.on('selection:updated', onMobileObjectSelected);
  fabricCanvas.on('selection:cleared', onMobileObjectCleared);
}

function toggleMobileTab(tabId) {
  const wasOpen = _mobileSheet.classList.contains('open');
  const sameTab = _activeTab === tabId;

  if (wasOpen && sameTab) {
    closeMobileSheet();
    return;
  }

  _activeTab = tabId;

  // Update active state on tab buttons
  _mobileTabBar.querySelectorAll('.tab-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.tab === tabId);
  });

  // Render tab content
  renderMobileTabContent(tabId);

  // Update sheet title
  const tab = MOBILE_TABS.find(t => t.id === tabId);
  document.getElementById('mobile-sheet-title').textContent = tab?.label || '';

  // Open sheet
  _mobileSheet.classList.add('open');
}

function closeMobileSheet() {
  _mobileSheet.classList.remove('open');
  _activeTab = null;
  _mobileTabBar.querySelectorAll('.tab-btn').forEach(btn => btn.classList.remove('active'));
}

function renderMobileTabContent(tabId) {
  const container = document.getElementById('mobile-sheet-content');
  container.innerHTML = '';

  switch (tabId) {
    case 'design':
      container.appendChild(buildSection('Template', buildTemplateSelector()));
      container.appendChild(buildSection('Color Scheme', buildSchemeSelector()));
      container.appendChild(buildSection('Presets', buildPresetsControls()));
      break;

    case 'layout':
      container.appendChild(buildSection('Editor Mode', buildEditorModeToggle()));
      container.appendChild(buildSection('Output Size', buildSizeSelector()));
      // Snap to grid
      const snapRow = document.createElement('div');
      snapRow.className = 'check-row';
      snapRow.style.marginTop = '8px';
      const snapCheck = document.createElement('input');
      snapCheck.type = 'checkbox';
      snapCheck.id = 'mobile-snap-check';
      snapCheck.checked = snapEnabled;
      snapCheck.onchange = () => {
        snapEnabled = snapCheck.checked;
        logAction(snapEnabled ? 'Snap ON' : 'Snap OFF');
      };
      const snapLabel = document.createElement('label');
      snapLabel.htmlFor = 'mobile-snap-check';
      snapLabel.className = 'checkbox-label';
      snapLabel.style.cssText = 'font-size:11px;color:var(--text-dim);';
      snapLabel.textContent = `Snap to grid (${snapGridSize}px)`;
      snapRow.appendChild(snapCheck);
      snapRow.appendChild(snapLabel);
      container.appendChild(snapRow);
      break;

    case 'text': {
      // Remove any stale desktop #object-properties so getElementById finds the mobile one
      const stalePanel = document.getElementById('object-properties');
      if (stalePanel && !stalePanel.closest('.mobile-sheet')) stalePanel.removeAttribute('id');

      // Show selected object panel or instruction
      const obj = fabricCanvas?.getActiveObject();
      if (obj && (obj.type === 'textbox' || obj.data?.id)) {
        const panel = document.createElement('div');
        panel.id = 'object-properties';
        container.appendChild(panel);
        updateObjectPanel();
      } else {
        const hint = document.createElement('div');
        hint.style.cssText = 'color:var(--text-dim);font-size:11px;padding:20px 0;text-align:center;';
        hint.textContent = 'Tap a text object on the canvas to edit it';
        container.appendChild(hint);
      }
      break;
    }

    case 'fx':
      container.appendChild(buildGroupSection('Line Art', [
        { title: 'Layer 1', content: buildLineArtControls() },
        { title: 'Layer 2', content: buildLineArt2Controls() },
      ]));
      container.appendChild(buildSection('Film Grain', buildGrainControls()));
      container.appendChild(buildSection('Mangle', buildMangleControls()));
      container.appendChild(buildSection('Blend Layers', buildBlendLayerControls()));
      break;

    case 'more':
      container.appendChild(buildSection('Wordmark', buildWordmarkControls()));
      container.appendChild(buildSection('Event Date', buildDatePickerControls()));
      container.appendChild(buildSection('QR Code', buildQrControls()));
      if (hiddenObjects.size > 0) {
        container.appendChild(buildSection('Hidden Objects', buildHiddenObjectsList()));
      }
      break;
  }
}

/**
 * When an object is selected on mobile, auto-switch to the Text tab
 * so the user can immediately edit properties.
 */
function onMobileObjectSelected() {
  if (!_mobileTabBar) return;
  // If the sheet is closed or on a different tab, switch to text
  const obj = fabricCanvas.getActiveObject();
  if (!obj) return;

  // Auto-open the text/object tab
  if (_activeTab !== 'text') {
    toggleMobileTab('text');
  } else {
    // Already on text tab — just re-render content
    renderMobileTabContent('text');
  }
}

function onMobileObjectCleared() {
  if (!_mobileTabBar) return;
  // If text tab is open, refresh to show the "tap to select" hint
  if (_activeTab === 'text') {
    renderMobileTabContent('text');
  }
}

/**
 * Drag-to-dismiss on the sheet handle.
 * Swipe down to close the bottom sheet.
 */
function setupSheetDrag(handle, sheet) {
  let startY = 0;
  let currentY = 0;
  let dragging = false;

  handle.addEventListener('touchstart', (e) => {
    startY = e.touches[0].clientY;
    dragging = true;
    sheet.style.transition = 'none';
  }, { passive: true });

  handle.addEventListener('touchmove', (e) => {
    if (!dragging) return;
    currentY = e.touches[0].clientY;
    const dy = Math.max(0, currentY - startY);
    sheet.style.transform = `translateY(${dy}px)`;
  }, { passive: true });

  handle.addEventListener('touchend', () => {
    if (!dragging) return;
    dragging = false;
    sheet.style.transition = '';
    const dy = currentY - startY;
    if (dy > 80) {
      closeMobileSheet();
    } else {
      sheet.style.transform = '';
      if (sheet.classList.contains('open')) {
        sheet.style.transform = 'translateY(0)';
      }
    }
  }, { passive: true });
}

/**
 * Long-press on canvas triggers the context menu on touch devices.
 * The right-click handler already exists — this bridges touch to it.
 */
function setupTouchContextMenu() {
  const canvasEl = fabricCanvas.upperCanvasEl || fabricCanvas.wrapperEl;
  let longPressTimer = null;
  let touchStartPos = null;

  canvasEl.addEventListener('touchstart', (e) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    touchStartPos = { x: touch.clientX, y: touch.clientY };

    longPressTimer = setTimeout(() => {
      // Synthesize a contextmenu event at the touch location
      const evt = new MouseEvent('contextmenu', {
        bubbles: true,
        clientX: touch.clientX,
        clientY: touch.clientY,
      });
      canvasEl.dispatchEvent(evt);
    }, 500);
  }, { passive: true });

  canvasEl.addEventListener('touchmove', (e) => {
    if (!touchStartPos || !longPressTimer) return;
    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - touchStartPos.x);
    const dy = Math.abs(touch.clientY - touchStartPos.y);
    if (dx > 10 || dy > 10) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  }, { passive: true });

  canvasEl.addEventListener('touchend', () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  }, { passive: true });
}
